import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import DOMPurify from 'dompurify';
// F-9: Force rel=noopener on all links; block javascript:/data: hrefs
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
  if (node.tagName === 'A' && node.getAttribute('href')?.startsWith('javascript:')) {
    node.removeAttribute('href');
  }
});
import {
  Application, DocumentMetadata, DocumentRequest, Comment, AuditLog,
  SpecialRequest, PersonalDetails, PassportImmigration, EducationEntry,
  WorkEntry, EmergencyContact, ProgramPreferences, ApplicationStatus, ServiceProvider,
} from '../../types';
import { getSafeApplicantName } from '../../lib/nameUtils';
import { safePiiText } from '../../lib/piiDisplay';
import { buildApplicantPdfFileName } from '../../lib/pdfFileName';
import { isAcceptableDocumentFileName, unsupportedTypeMessage } from '../../lib/documentFileTypes';
import { hasModuleAccess, canEditModule, hasApplicantAction, isApplicantInScope } from '../../lib/permissions';
import FormBuilder from '../shared/FormBuilder';
import { renderFormattedResponseValue } from '../../lib/responseFormatter';
import FormTemplatePickerBar from './FormTemplatePickerBar';
import {
  ShieldCheck, Upload, Download, FileText, Trash2, X, Check, CheckCircle, XCircle, AlertCircle,
  Edit3, Paperclip, Send, Clock, User, Plus, ChevronDown, ChevronUp,
  Camera, MapPin, Briefcase, Plane, FileSpreadsheet, Star, Eye, EyeOff, Mail,
  CalendarDays, MoreHorizontal, Copy, CheckCheck, Ban, ExternalLink, Phone,
  MessageSquare, Activity, FileSignature, RefreshCw, ListFilter,
  Layers, Hash, AlertTriangle, DollarSign, Receipt, CreditCard, Loader, Search,
  Filter, PanelLeftClose, PanelLeftOpen, Sparkles,
  TicketCheck, Archive, Shield, Video, Bell, UserCheck, Users, Link2, History, Share2,
  Workflow,
} from 'lucide-react';
import { FinanceProduct, InvoiceRecord, QuoteRecord } from '../../types';
import { getActiveWorkspaceId, isSameWorkspaceSync, workspaceHeader } from '../../lib/workspaces';
import { mapBulkReviewJobItem } from '../../lib/bulkReviewJobSummary';
import { useWorkspaces } from '../shared/WorkspaceProvider';

// Lazy-loaded heavy tabs and modals for faster initial ApplicantDetail shell load
const SignTab = React.lazy(() => import('./SignTab'));
const EmailTab = React.lazy(() => import('./EmailTab'));
const InvoiceBuilderModal = React.lazy(() => import('./InvoiceBuilderModal'));
const QuoteBuilderModal = React.lazy(() => import('./QuoteBuilderModal'));
const InvoicePreviewModal = React.lazy(() => import('./InvoicePreviewModal'));
const QuotePreviewModal = React.lazy(() => import('./QuotePreviewModal'));
const TicketDetailModal = React.lazy(() => import('./TicketDetailModal'));
const ApplicantDrawer = React.lazy(() => import('./workflow/ApplicantDrawer').then(m => ({ default: m.ApplicantDrawer })));
const WorkspaceTeamAssignModal = React.lazy(() => import('./WorkspaceTeamAssignModal'));
const MatchesTab = React.lazy(() => import('./MatchesTab'));
const ScreeningTab = React.lazy(() => import('./ScreeningTab'));
import MasterPipeline, { MASTER_STAGES } from '../MasterPipeline';
import { getPipelineStages, stageLabel } from '../../lib/programPipeline';
import { db, storage, resolveFirebaseDocumentUrl, getUploadedStoragePath, uploadBytesWithTimeout, createAuditLog } from '../../lib/firebase';
import { toDateInputValue } from '../../lib/applicationSchema';
import { normalizePlacements, getPlacementSummary, hasActivePrimaryPlacement, isPlacementActive, sortPlacementsForDisplay } from '../../lib/placementUtils';
import { EMAIL_REGEX } from '../../lib/static-data';
import {
  collection, query, orderBy, getDocs, setDoc, doc, deleteDoc, updateDoc, addDoc, where
} from 'firebase/firestore';
import {
  ref, deleteObject
} from 'firebase/storage';
import { auth } from '../../lib/firebase';
import Avatar from '../shared/Avatar';
import { safeFetch, downloadPdf, triggerBlobDownload } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog, promptDialog } from '../../lib/confirmStore';
import { parseApplicationDocumentExtras } from '../../lib/requiredDocuments';
import { resolveLinkedReferrer, assignReferrerSelectModel } from '../../lib/referrerLinkage';
import { confirmNotification, sendSystemNotification, buildEmailTemplate } from '../../lib/notifications';
import CountrySelect from '../shared/CountrySelect';
import SearchableSelect from '../shared/SearchableSelect';
import DocPreviewModal from '../shared/DocPreviewModal';
import DocumentValidationPopup from '../shared/DocumentValidationPopup';
import Modal from '../shared/Modal';
import HousingArrangementEditor from '../shared/HousingArrangementEditor';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import { PHONE_REGEX, US_STATES, COMMON_TIMEZONES, localToUTC, utcToLocalISO } from '../../lib/static-data';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};


const normalizePhone = (value: string) => value.replace(/[\s\-()]/g, '');
const isValidPhone = (value: string) => PHONE_REGEX.test(normalizePhone(value));

/* ── Job Board Access Toggle ── */
function JobBoardAccessToggle({ userId }: { userId: string }) {
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/admin/applicants/${userId}/job-board-access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBlocked(data.jobBoardBlocked === true);
        }
      } catch {} finally { setLoading(false); }
    })();
  }, [userId]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`/api/admin/applicants/${userId}/job-board-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blocked: !blocked }),
      });
      setBlocked(!blocked);
    } catch {} finally { setToggling(false); }
  };

  if (loading) return <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />;

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[11px] text-slate-500 font-medium">Job Board Access</p>
        <p className={`text-[10px] font-semibold mt-0.5 ${blocked ? 'text-red-500' : 'text-emerald-600'}`}>
          {blocked ? 'Blocked' : 'Granted'}
        </p>
      </div>
      <button
        onClick={handleToggle}
        disabled={toggling}
        className={`relative inline-flex h-6 w-10 items-center rounded-full transition-all cursor-pointer disabled:opacity-50 ${
          blocked ? 'bg-red-200' : 'bg-emerald-200'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-all ${
          blocked ? 'translate-x-1' : 'translate-x-5'
        }`} />
      </button>
    </div>
  );
}

/* ── ID Verification Badge ── */
function DiditBadge({ userId }: { userId: string }) {
  const [status, setStatus] = useState<string | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        // Fetch from admin endpoint — gets the full user doc including didit fields
        const res = await fetch(`/api/admin/users/${userId}/didit-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setStatus(data.status || null);
          setVerifiedAt(data.verifiedAt || null);
        }
      } catch {} finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div className="h-7 rounded-lg bg-slate-100 animate-pulse" />;

  if (!status) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-50 text-slate-500 border border-slate-200">
        <Clock className="h-3 w-3" />
        Not Started
      </span>
    );
  }

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <ShieldCheck className="h-3 w-3" />
          Verified
        </span>
        {verifiedAt && (
          <span className="text-[11px] text-slate-500">{new Date(verifiedAt).toLocaleDateString()}</span>
        )}
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  }

  if (status === 'reverification_required') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200">
        <RefreshCw className="h-3 w-3" />
        Re-Verification Required
      </span>
    );
  }

  if (status === 'in_review') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
        <Clock className="h-3 w-3" />
        In Review
      </span>
    );
  }

  if (status === 'not_started') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-50 text-slate-500 border border-slate-200">
        <Shield className="h-3 w-3" />
        Not Started
      </span>
    );
  }

  // Declined or expired
  if (status === 'declined') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-700 border border-red-200">
        <XCircle className="h-3 w-3" />
        Declined
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-50 text-slate-600 border-slate-200">
      <XCircle className="h-3 w-3" />
      Expired
    </span>
  );
}

/* ── Admin Actions for ID Verification ── */
function DiditAdminActions({ userId, app }: { userId: string; app: Application }) {
  const [showReverificationModal, setShowReverificationModal] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [diditStatus, setDiditStatus] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Fetch didit status to conditionally show/hide actions
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/admin/users/${userId}/didit-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDiditStatus(data.status || null);
        }
      } catch {
        // Silently fail — buttons will be shown by default
      } finally {
        setStatusLoading(false);
      }
    })();
  }, [userId]);

  const isVerified = diditStatus === 'approved';

  const handleSendReminder = async () => {
    setReminderLoading(true);
    setReminderMessage(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/didit/send-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setReminderMessage({ type: 'error', text: data.error || 'Failed to send reminder' });
      } else {
        setReminderMessage({ type: 'success', text: 'Reminder sent successfully!' });
      }
    } catch {
      setReminderMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setReminderLoading(false);
    }
  };

  // Verified users get a single "Request New Verification" button
  if (!statusLoading && isVerified) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setShowReverificationModal(true)}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-amber-600 hover:bg-amber-50 border border-amber-200 transition-all cursor-pointer"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Request New Verification
        </button>
        {showReverificationModal && (
          <ReverificationModal
            userId={userId}
            app={app}
            onClose={() => setShowReverificationModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      {/* Send Verification Reminder */}
      <button
        onClick={handleSendReminder}
        disabled={reminderLoading || statusLoading}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 transition-all cursor-pointer disabled:opacity-50"
      >
        <Mail className="h-3.5 w-3.5" />
        {reminderLoading ? 'Sending...' : 'Send Verification Reminder'}
      </button>
      {reminderMessage && (
        <p className={`text-[10px] ${reminderMessage.type === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
          {reminderMessage.text}
        </p>
      )}

      {/* Request Re-Verification */}
      <button
        onClick={() => setShowReverificationModal(true)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-amber-600 hover:bg-amber-50 border border-amber-200 transition-all cursor-pointer"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Request Re-Verification
      </button>

      {showReverificationModal && (
        <ReverificationModal
          userId={userId}
          app={app}
          onClose={() => setShowReverificationModal(false)}
        />
      )}
    </div>
  );
}

/* ── Re-Verification Request Modal ── */
function ReverificationModal({ userId, app, onClose }: { userId: string; app: Application; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    if (reason.trim().length < 10) {
      setError('Please provide a reason (at least 10 characters).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/didit/request-reverification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, reason: reason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to request re-verification');
      } else {
        setSuccess(data.message || 'Re-verification requested. The applicant has been emailed their verification link.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <button aria-label="Close" title="Close" onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer">
          <X className="h-4 w-4" />
        </button>
        <h3 className="text-sm font-bold text-slate-900 mb-1">Request Re-Verification</h3>
        <p className="text-xs text-slate-500 mb-4">
          This will email the applicant their own direct verification link to complete re-verification. If they already have an active session, that session is reused — no new session is created.
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-bold px-4 py-3 rounded-xl mb-4" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-4 py-3 rounded-xl mb-4" role="status">
            {success}
          </div>
        )}
        <div className="space-y-1 mb-4">
          <label className="text-xs font-semibold text-slate-600">Reason for re-verification <span className="text-brand">*</span></label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain why re-verification is needed..."
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-blue-400 resize-none min-h-[80px]"
            rows={3}
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-xl text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            {loading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {loading ? 'Submitting...' : 'Request Re-Verification'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

async function downloadFile(storagePath: string | undefined, downloadUrl: string | undefined, fileName: string) {
  try {
    const url = await resolveFirebaseDocumentUrl(storagePath, downloadUrl);
    if (!url) return;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (err) {
    console.error('Download failed:', err);
  }
}

const formatFullDateTime = (value?: string) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(value));
};
const isImagePreview = (url = '', name = '') => /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url) || /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
const isPdfPreview = (url = '', name = '') => /\.pdf(\?|$)/i.test(url) || /\.pdf$/i.test(name);

interface ApplicantDetailProps {
  selectedApp: Application;
  appCtx: any;
  events: any[];
  eventsCtx: any;
  adminEmail: string;
  allAdmins?: { uid: string; name: string; email: string }[];
  batchesCtx?: any;
  isEnhancedReferrer?: boolean;
  /** The signed-in workspace member's profile (used for fine-grained
   *  `applicant.*` permission checks). Enhanced referrers keep their legacy
   *  read-only tab set and are not workspace members. */
  currentUserProfile?: any;
}

/**
 * Two-step confirm button (no native confirm() dialogs): the first
 * click arms it ("Click again to confirm"), the second fires. Used
 * for the Talent Pool remove action — reversible, but worth one
 * deliberate second click.
 */
function ConfirmButton({ label, confirmLabel, onConfirm, className }: { label: React.ReactNode; confirmLabel: string; onConfirm: () => void; className?: string }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      onClick={() => {
        if (!armed) { setArmed(true); return; }
        setArmed(false);
        onConfirm();
      }}
      className={className}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}

export default function ApplicantDetail({ selectedApp, appCtx, events, eventsCtx, adminEmail, allAdmins, batchesCtx, isEnhancedReferrer, currentUserProfile }: ApplicantDetailProps) {
  const app = selectedApp;
  const tab = appCtx.adminDetailTab || 'profile';
  // Enhanced referrers are NOT workspace members — they keep the legacy tab
  // set. Workspace team members resolve `applicant.*` actions from their
  // effective-identity permissions (server-computed).
  const inScope = useMemo(
    () => isApplicantInScope(selectedApp, currentUserProfile),
    [selectedApp, currentUserProfile]
  );

  const canApplicant = useCallback((action: string, level: 'view' | 'edit' = 'edit') => {
    if (!inScope) return false;
    if (isEnhancedReferrer) {
      if (level === 'view') return true;
      return hasApplicantAction(currentUserProfile, action, 'edit');
    }
    return hasApplicantAction(currentUserProfile, action, level);
  }, [currentUserProfile, isEnhancedReferrer, inScope]);
  // PII visibility: workspace members see contact details only when the role
  // holds `applicant.showContact`; otherwise the fields are masked.
  const canViewContact = useMemo(
    () => isEnhancedReferrer || hasApplicantAction(currentUserProfile, 'applicant.showContact', 'view'),
    [currentUserProfile, isEnhancedReferrer],
  );
  const isWsAdmin = useMemo(() => {
    const role = String(currentUserProfile?.workspaceRole || currentUserProfile?.role || '').toLowerCase();
    return ['admin', 'owner', 'super', 'master', 'platform_admin'].includes(role) || currentUserProfile?.isSuperAdmin === true || currentUserProfile?.platformAdmin === true;
  }, [currentUserProfile]);
  const isAdmin = isWsAdmin;

  const canEditProfile = useMemo(() => {
    if (!inScope) return false;
    if (isEnhancedReferrer) return true;
    if (isWsAdmin) return true;
    return canApplicant('applicant.profile', 'edit');
  }, [inScope, isEnhancedReferrer, isWsAdmin, canApplicant]);

  const canEditProfilePhoto = canEditProfile;
  const maskValue = useCallback((value?: string, kind: 'email' | 'phone' | 'text' | 'passport' = 'text') => {
    // Defensive: a still-encrypted PII block ({iv, ciphertext, authTag})
    // can reach here when server-side decryption was unavailable. Never
    // return it (rendering it crashes React, error #31) and never call
    // string methods on it (TypeError). Coerce to a safe string first.
    const text = safePiiText(value, '');
    if (!text || canViewContact) return text;
    if (kind === 'email') {
      const at = text.indexOf('@');
      if (at <= 0) return 'a***@***.com';
      const firstChar = text.charAt(0);
      const tld = text.slice(text.lastIndexOf('.') + 1) || 'com';
      return `${firstChar}***@***.${tld}`;
    }
    if (kind === 'phone') {
      const digits = text.replace(/[^0-9]/g, '');
      if (digits.length >= 4) {
        return `***-***-${digits.slice(-4)}`;
      }
      return '***-***-1234';
    }
    if (kind === 'passport') {
      const raw = text.trim();
      if (raw.length >= 4) {
        return `***${raw.slice(-4)}`;
      }
      return '***1234';
    }
    return '••••••••';
  }, [canViewContact]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [adminEmailNew, setAdminEmailNew] = useState('');
  const [adminEmailUpdating, setAdminEmailUpdating] = useState(false);
  const [adminEmailError, setAdminEmailError] = useState('');
  const [adminEmailApps, setAdminEmailApps] = useState<any[]>([]);
  const [adminEmailSelectedApps, setAdminEmailSelectedApps] = useState<string[]>([]);
  const [adminEmailAppsLoading, setAdminEmailAppsLoading] = useState(false);
  const [showChangeUser, setShowChangeUser] = useState(false);
  const [showWorkflowDrawer, setShowWorkflowDrawer] = useState(false);
  const [changeUserEmail, setChangeUserEmail] = useState('');
  const [changeUserFound, setChangeUserFound] = useState<any>(null);
  const [changeUserLoading, setChangeUserLoading] = useState(false);
  const [changeUserUpdateEmail, setChangeUserUpdateEmail] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);

  // Referrer/Agency list state for admin assignment
  const [referrersList, setReferrersList] = useState<any[]>([]);
  // Tombstones of permanently deleted referrers — preserved applications
  // resolve their referrer name + removed marker from these.
  const [deletedReferrersList, setDeletedReferrersList] = useState<any[]>([]);
  const [loadingReferrers, setLoadingReferrers] = useState(false);
  // This applicant's commission record (ADMIN-ONLY surface — never shown
  // to the applicant; the section rendering it is admin-gated and this
  // fetch never runs for enhanced referrers).
  const [appCommission, setAppCommission] = useState<any>(null);
  // Team Member assignment modal
  const [showTeamAssignModal, setShowTeamAssignModal] = useState(false);
  const [unassigningMember, setUnassigningMember] = useState<string | null>(null);

  const [fetchedScreeningRecords, setFetchedScreeningRecords] = useState<any[]>([]);

  useEffect(() => {
    if (tab !== 'screening') return;
    let isMounted = true;
    const fetchScreenings = async () => {
      const appId = selectedApp?.applicationId || app?.applicationId;
      if (!appId) return;
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/admin/applications/${appId}/screenings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          if (isMounted && Array.isArray(data.records)) {
            setFetchedScreeningRecords(data.records);
          }
        }
      } catch { /* ignore error */ }
    };
    fetchScreenings();
    return () => { isMounted = false; };
  }, [tab, selectedApp?.applicationId, app?.applicationId]);

  useEffect(() => {
    if (isEnhancedReferrer || tab !== 'profile') return;
    const fetchReferrers = async () => {
      setLoadingReferrers(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/admin/referrers', {
          // #1125 Item 3: the workspace header is REQUIRED here (house
          // rule for raw fetch) — without it the server resolved the
          // default workspace and this list missed the org stamped in
          // the admin's active workspace, so the assign control could
          // not reflect the stamped referrer the section displayed.
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workspaceHeader() }
        });
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.referrers)) {
            setReferrersList(data.referrers);
          }
          if (data && Array.isArray(data.deletedReferrers)) {
            setDeletedReferrersList(data.deletedReferrers);
          }
        }
      } catch (err) {
        console.error('Failed to load referrers:', err);
      } finally {
        setLoadingReferrers(false);
      }
    };
    fetchReferrers();
  }, [tab, isEnhancedReferrer]);

  // Load this applicant's commission record (stamp, status, full event
  // history) from the admin Finance commissions list for display in the
  // Referrer / Agency section. Admin context only.
  useEffect(() => {
    if (isEnhancedReferrer || tab !== 'profile') return;
    const appId = (app as any)?.applicationId;
    if (!appId) { setAppCommission(null); return; }
    let isMounted = true;
    const fetchCommission = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/admin/finance/commissions', {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workspaceHeader() }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!isMounted) return;
        const match = (data.commissions || []).find((c: any) => c.applicationId === appId) || null;
        setAppCommission(match);
      } catch { /* finance permission may not be held — section degrades to the stamp */ }
    };
    fetchCommission();
    return () => { isMounted = false; };
  }, [tab, isEnhancedReferrer, (app as any)?.applicationId]);

  // Canonical multi-field resolution (follow-up Item 10): referrerId →
  // org doc id, then referredBy → org userId, then the creation stamps
  // themselves. The old single-shape join (referredBy ↔ userId only)
  // showed "No referrer assigned" for referrer-added candidates whose
  // linkage was stamped at creation.
  const currentReferrer = React.useMemo(
    () => resolveLinkedReferrer(app, referrersList, deletedReferrersList),
    [app.referredBy, (app as any).referrerId, (app as any).referrerName, referrersList, deletedReferrersList],
  );

  // #1125 Item 3: the assign control binds the SAME resolution as the
  // section above it. When the stamped referrer has no option in the
  // fetched list, the model injects a synthetic option for it — a
  // <select> whose value matches no option displays "-- No Referrer --"
  // even when bound to the stamped uid (the live divergence).
  const assignReferrerModel = React.useMemo(
    () => assignReferrerSelectModel(app, referrersList, deletedReferrersList),
    [app.referredBy, (app as any).referrerId, (app as any).referrerName, referrersList, deletedReferrersList],
  );

  const handleUnassignTeamMember = async (memberId: string) => {
    const member = (app.assignedTeamMembers || []).find((m: any) => m.uid === memberId);
    const memberLabel = member?.name || member?.email || 'this team member';
    if (!(await confirmDialog(`Remove ${memberLabel} from the applicant? They will no longer be assigned to this applicant.`))) return;
    setUnassigningMember(memberId);
    try {
      await safeFetch(`/api/admin/applications/${app.applicationId}/unassign-team-member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId }),
      });
      appCtx.setSelectedApp?.({
        ...app,
        assignedTeamMemberIds: (app.assignedTeamMemberIds || []).filter((id: string) => id !== memberId),
        assignedTeamMembers: (app.assignedTeamMembers || []).filter((m: any) => m.uid !== memberId),
        updatedAt: new Date().toISOString(),
      });
      notifyToast('success', 'Team member unassigned.');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to unassign team member.');
    } finally {
      setUnassigningMember(null);
    }
  };

  // Lock body scroll when any inline modal is open
  // Using appCtx.previewDoc which is always in scope at this point
  useBodyScrollLock(!!showEmailEdit || !!showChangeUser || !!showTeamAssignModal || !!showStatusConfirmModal || !!appCtx.previewDoc || !!showWorkflowDrawer);



  const lookupChangeUser = async () => {
    const email = changeUserEmail.trim().toLowerCase();
    if (!email) return;
    setChangeUserLoading(true);
    setChangeUserFound(null);
    try {
      const data = await safeFetch(`/api/admin/users/lookup?email=${encodeURIComponent(email)}`);
      setChangeUserFound(data.user);
    } catch (err: any) {
      notifyToast('error', err.message || 'User not found');
    } finally {
      setChangeUserLoading(false);
    }
  };

  const confirmChangeUser = async () => {
    if (!changeUserFound) return;
    const prompt = changeUserUpdateEmail
      ? `Confirm changing this application to ${changeUserFound.email} and updating the applicant email shown on the application?`
      : `Confirm changing this application user to ${changeUserFound.email} without changing the applicant email?`;
    if (!(await confirmDialog(prompt))) return;
    setChangeUserLoading(true);
    try {
      const data = await safeFetch(`/api/admin/applications/${app.applicationId}/change-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: changeUserFound.uid, email: changeUserFound.email, updateApplicantEmail: changeUserUpdateEmail }),
      });
      const updated = { ...app, ...data.updates } as any;
      appCtx.setSelectedApp?.(updated);
      setShowChangeUser(false);
      setChangeUserFound(null);
      setChangeUserEmail('');
      setChangeUserUpdateEmail(false);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to change user');
    } finally {
      setChangeUserLoading(false);
    }
  };

  // Compute completion %
  const docsTotal = (appCtx.docsList || []).length;
  const docsApproved = (appCtx.docsList || []).filter((d: DocumentMetadata) => d.status === 'Approved').length;
  const reqsTotal = (appCtx.documentRequests || []).length;
  const reqsDone = (appCtx.documentRequests || []).filter((r: DocumentRequest) => r.status === 'Completed' || r.status === 'Uploaded').length;
  const totalItems = docsTotal + reqsTotal || 1;
  const doneItems = docsApproved + reqsDone;
  const pct = Math.min(100, Math.round((doneItems / totalItems) * 100));

  // Days since submission
  const daysSince = app.createdAt
    ? Math.floor((Date.now() - new Date(app.createdAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Close status dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getSubStatus = (a: any) => a ? (a as any).submissionStatus || a.status || 'Draft' : 'Draft';
  const isClosed = (a: any) => getSubStatus(a) === 'Closed';
  const submissionStatuses = ['Draft', 'Submitted', 'Returned', 'Cancelled', 'Applicant Cancelled', 'Closed', 'Archived'];
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Returned':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Cancelled':
      case 'Applicant Cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'Closed':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Archived':
        return 'bg-zinc-100 text-zinc-700 border-zinc-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };
  const matchedProgram = (appCtx?.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
  const activePipelineStages = getPipelineStages(matchedProgram);
  const currentPhaseLabel = stageLabel(activePipelineStages, app.masterStage, '—');
  const isApplicationActive = (app as any)?.activeStatus !== false;
  const formatDateTime = (value?: string) => value ? new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // NOTE (PR #1336 review): the Quick Action "Submit" button was the
  // only caller of the Didit-gated handleAdminSubmit helper, so the
  // helper, its loading state, and its now-unreachable "Applicant Not
  // Verified" modal were removed with the Quick Actions section.
  // Submit-on-behalf remains available from the applicants list actions
  // and the workflow stage focus, which call handleUpdateStatus
  // directly.

  const loadUserApps = async () => {
    setAdminEmailAppsLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/applications-by-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetUid: app.userId, userEmail: app.userEmail }),
      });
      const data = await res.json();
      const apps = data.applications || [];
      setAdminEmailApps(apps);
      setAdminEmailSelectedApps(apps.map((a: any) => a.applicationId));
    } catch {
      setAdminEmailApps([]);
    } finally {
      setAdminEmailAppsLoading(false);
    }
  };

  const handleAdminEmailUpdate = async () => {
    if (!adminEmailNew || !EMAIL_REGEX.test(adminEmailNew)) {
      setAdminEmailError('Please enter a valid email address.');
      return;
    }
    if (adminEmailSelectedApps.length === 0) {
      setAdminEmailError('Select at least one application to update.');
      return;
    }
    setAdminEmailUpdating(true);
    setAdminEmailError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/update-applicant-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          targetUid: app.userId,
          newEmail: adminEmailNew.trim().toLowerCase(),
          applicationIds: adminEmailSelectedApps,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAdminEmailError(data.error || 'Failed to update email.');
        return;
      }
      setAdminEmailUpdating(false);
      setShowEmailEdit(false);
      setAdminEmailApps([]);
      appCtx.handleRefreshOne?.(app.applicationId);
    } catch {
      setAdminEmailError('Network error. Please try again.');
    } finally {
      setAdminEmailUpdating(false);
    }
  };

  return (
    <div className="flex h-full animate-fade-in">
      <div className={`border-r border-slate-200 bg-white overflow-y-auto flex flex-col transition-all duration-200 ${sidebarCollapsed ? 'w-16 min-w-[64px]' : 'w-72 min-w-[288px]'}`}>
        <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
          {!sidebarCollapsed && <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Applicant Snapshot</span>}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="h-9 w-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 flex items-center justify-center cursor-pointer transition-all"
            title={sidebarCollapsed ? 'Expand applicant sidebar' : 'Collapse applicant sidebar'}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        {!sidebarCollapsed && <>
        <div className="px-5 pt-6 pb-5 border-b border-slate-200">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="relative group">
              <button
                type="button"
                onClick={() => {
                  if (app.profilePhotoUrl) {
                    window.open(app.profilePhotoUrl, '_blank');
                  }
                }}
                className={`rounded-full transition-all ${app.profilePhotoUrl ? 'cursor-zoom-in hover:opacity-90' : 'cursor-default'}`}
                title={app.profilePhotoUrl ? 'Click to view full image' : undefined}
                disabled={!app.profilePhotoUrl}
              >
                <Avatar
                  name={`${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim() || app.userEmail}
                  photoUrl={app.profilePhotoUrl}
                  size="xl"
                  app={app}
                />
              </button>
              {canEditProfilePhoto && (
                <button
                  type="button"
                  onClick={() => profilePhotoInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-brand-primary hover:bg-brand-primary-dark text-white flex items-center justify-center shadow-sm cursor-pointer border-2 border-white transition-all"
                  title="Upload new profile image"
                >
                  <Camera className="h-3 w-3" />
                </button>
              )}
            </div>
            {canEditProfilePhoto && (
              <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={appCtx.handlePhotoUploadReal}
              />
            )}
            <div>
              <h2 className="text-base font-semibold text-slate-900 leading-tight tracking-tight">
                {app.personalDetails?.firstName || '—'} {app.personalDetails?.lastName || ''}
              </h2>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <p className="text-xs text-slate-500">{maskValue(app.userEmail, 'email')}</p>
                {!isEnhancedReferrer && canViewContact && (
                <button
                  onClick={() => { setShowEmailEdit(true); setAdminEmailNew(app.userEmail || ''); loadUserApps(); }}
                  className="text-slate-400 hover:text-brand transition-colors cursor-pointer"
                  title="Change email address"
                >
                  <Edit3 className="h-3 w-3" />
                </button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 font-mono mt-0.5">{app.applicationId}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold border ${getStatusStyle(getSubStatus(app))}`}>
              {getSubStatus(app)}
            </span>
            {!isEnhancedReferrer && (
              <button
                type="button"
                onClick={() => { setShowChangeUser(true); setChangeUserEmail(''); setChangeUserFound(null); setChangeUserUpdateEmail(false); }}
                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                title="Change the Firebase user who owns this applicant application"
              >
                <User className="h-3 w-3" /> Change User
              </button>
            )}

            {!isEnhancedReferrer && (
            <div className="relative w-full" ref={statusRef}>
              <button onClick={() => setStatusOpen(!statusOpen)} className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-slate-200 bg-white hover:bg-slate-50 transition-all cursor-pointer">
                <span>{getSubStatus(app)}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
              </button>
              {statusOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                  {submissionStatuses.filter(s => !isEnhancedReferrer || s !== 'Closed').map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setStatusOpen(false);
                        if (s !== getSubStatus(app)) {
                          setPendingStatus(s);
                          setShowStatusConfirmModal(true);
                        }
                      }}
                      className={`w-full text-left px-3 py-1.5 text-[11px] font-semibold hover:bg-slate-50 transition-colors cursor-pointer ${getSubStatus(app) === s ? 'text-blue-700 bg-blue-50' : 'text-slate-700'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}
            {!isEnhancedReferrer && app.userId && (
              <button
                type="button"
                onClick={() => setShowWorkflowDrawer(true)}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-[11px] font-semibold text-white hover:bg-brand-active transition-all cursor-pointer"
                title="Open the workflow drawer for this applicant"
              >
                <Workflow className="h-3.5 w-3.5" /> View Workflow
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-4 space-y-4 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">About</p>
          {[
            { label: 'Application ID', value: app.applicationId },
            { label: 'Program / Service', value: (() => { const p = (appCtx.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId); return p?.name || app.programType || app.programPreferences?.programType || app.programId || '—'; })() },
            { label: 'Service Provider', value: app.serviceProvider?.companyName || app.serviceProvider?.name },
            { label: 'Submission Status', value: getSubStatus(app) },
            { label: 'Master Phase', value: currentPhaseLabel },
            { label: 'Created', value: formatDateTime(app.createdAt) },
            { label: 'Last Modified', value: formatDateTime(app.updatedAt) },
            { label: 'Citizenship', value: app.personalDetails?.countryOfCitizenship },
            { label: 'Gender', value: app.personalDetails?.gender },
            { label: 'Phone', value: maskValue(app.personalDetails?.phone, 'phone') },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-start justify-between gap-2">
              <span className="text-[11px] text-slate-500 font-medium shrink-0">{label}</span>
              <span className="text-[11px] text-slate-800 font-semibold text-right">{value || '—'}</span>
            </div>
          ))}
          {/* ID Verification Status */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">ID Verification</p>
            <DiditBadge userId={app.userId} />
            {!isEnhancedReferrer && <DiditAdminActions userId={app.userId} app={app} />}
          </div>
          {/* Job Board Access Control — hidden for enhanced referrers */}
          {!isEnhancedReferrer && (
          <div className="pt-3 border-t border-slate-100">
            <JobBoardAccessToggle userId={app.userId} />
          </div>
          )}

          {/* Referrer/Agency Assignment Section (only with applicant.referrer) */}
          {!isEnhancedReferrer && canApplicant('applicant.referrer', 'edit') && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Referrer / Agency</p>
              {currentReferrer ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5 text-xs animate-fade-in">
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Name:</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1.5">
                      {currentReferrer.name || '—'}
                      {(currentReferrer as any).removed && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[11px] font-bold uppercase tracking-wide">Organisation removed</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Email:</span>
                    <span className="font-semibold text-slate-600 font-mono text-[10px] truncate max-w-[130px]">{currentReferrer.email || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Referrer ID:</span>
                    <span className="font-bold text-brand font-mono uppercase text-[10px]">{(currentReferrer as any).inviteCode || '—'}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic bg-slate-50 border border-slate-200 p-3 rounded-xl">No referrer assigned to applicant.</p>
              )}

              {/* Referrer commission — ADMIN-ONLY. Applicants never see
                  amounts, rates, stamps, or this history anywhere in
                  their own portal or API responses. */}
              {currentReferrer && (
                <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Referrer Commission · Admin only</p>
                  {appCommission?.removedAt ? (
                    <p className="text-xs font-semibold text-red-700">
                      Commission removed {new Date(appCommission.removedAt).toLocaleDateString()} by {appCommission.removedBy?.name || appCommission.removedBy?.email || 'admin'} — the referrer earns nothing for this applicant.
                    </p>
                  ) : appCommission ? (
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-400 font-medium">Amount:</span>
                        <span className="font-bold text-slate-800">${Number(appCommission.amount ?? appCommission.rate ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400 font-medium">Status:</span>
                        <span className="font-semibold text-slate-600">{appCommission.payoutProcessedAt ? 'Payout processed' : `${String(appCommission.commissionStatus || 'not_committed').replace(/_/g, ' ')} · ${String(appCommission.payoutStatus || 'not_queued').replace(/_/g, ' ')}`}</span>
                      </div>
                    </div>
                  ) : (app as any).commissionRate != null ? (
                    <p className="text-xs text-slate-600">
                      Stamped at referral: <strong>${Number((app as any).commissionRate).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      {(app as any).commissionRateStampedAt ? <> on {new Date((app as any).commissionRateStampedAt).toLocaleDateString()}</> : null}.
                      The commission record appears when Finance reviews commissions.
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No commission amount stamped for this applicant.</p>
                  )}
                  {Array.isArray(appCommission?.history) && appCommission.history.length > 0 && (
                    <div className="pt-1.5 border-t border-slate-100 space-y-1">
                      {appCommission.history.slice().reverse().slice(0, 6).map((h: any, hi: number) => (
                        <p key={hi} className="text-[11px] text-slate-500">
                          <span className="font-semibold text-slate-700">{h.action}</span>
                          {h.note ? <> — {h.note}</> : null}
                          {' '}<span className="text-slate-400">({h.by?.name || h.by?.email || 'System'}{h.at ? ` · ${new Date(h.at).toLocaleDateString()}` : ''})</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500">Edit or remove this commission in Finance → Commissions. Never visible to the applicant.</p>
                </div>
              )}

              {/* Reassign selector */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Assign Referrer</label>
                <SearchableSelect
                  value={assignReferrerModel.value}
                  onChange={async (newUid) => {
                    // Re-selecting the current assignment (including the
                    // synthetic stamped option) is a no-op, not a clear.
                    if (newUid === String(app.referredBy || '')) return;
                    const rObj = referrersList.find((r: any) => r.userId === newUid);
                    const newName = rObj ? rObj.name : '';

                    if (await confirmDialog(`Are you sure you want to reassign this applicant to ${newName || 'no referrer'}?`)) {
                      try {
                        // B-2: routed through the server re-link endpoint,
                        // which applies the commission stamp rules (#1127):
                        // a fresh link stamps the new referrer's current
                        // configured rate, an existing stamp is preserved,
                        // and unlinking clears the stamp. The old generic
                        // profile update changed the linkage while
                        // bypassing stamping entirely.
                        const data = await safeFetch(`/api/admin/applications/${app.applicationId}/referrer-link`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ referrerUserId: newUid || null }),
                        });

                        // Update local states in context
                        const localUpdate: any = {
                          ...app,
                          referredBy: data?.referrer?.referredBy ?? null,
                          referrerName: data?.referrer?.referrerName ?? null,
                          referrerId: data?.referrer?.referrerId ?? null,
                          updatedAt: new Date().toISOString()
                        };
                        if (data?.action === 'unlink') {
                          localUpdate.commissionRate = null;
                          localUpdate.commissionRateStampedAt = null;
                        } else if (data?.stampWritten) {
                          localUpdate.commissionRate = data.commissionRate;
                          localUpdate.commissionRateStampedAt = new Date().toISOString();
                        }
                        appCtx.setSelectedApp?.(localUpdate);
                        notifyToast('success', 'Referrer reassigned successfully!');
                      } catch (err: any) {
                        notifyToast('error', 'Failed to reassign referrer: ' + err.message);
                      }
                    }
                  }}
                  options={assignReferrerModel.options}
                  placeholder="Search referrers…"
                  clearLabel="-- No Referrer --"
                  emptyLabel="No referrers found"
                />
              </div>
            </div>
          )}

          {/* Team Member Assignment Section */}
          {!isEnhancedReferrer && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assigned Team Members</p>
                {canApplicant('applicant.teamMember', 'edit') && (
                <button onClick={() => setShowTeamAssignModal(true)} className="text-[10px] font-bold text-brand hover:text-brand-dark flex items-center gap-1 cursor-pointer" title="Assign to team member(s)">
                  <UserCheck className="h-3 w-3" /> Assign
                </button>
                )}
              </div>
              {app.assignedTeamMembers && app.assignedTeamMembers.length > 0 ? (
                <div className="space-y-1.5">
                  {app.assignedTeamMembers.map((m: any) => (
                    <div key={m.uid} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-2 animate-fade-in">
                      <div className="h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 shrink-0 overflow-hidden">
                        {m.profilePhotoUrl
                          ? <img src={m.profilePhotoUrl} className="h-full w-full object-cover" alt="" />
                          : (m.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold text-slate-800 truncate">{m.name || '—'}</p>
                        <p className="text-[11px] text-slate-500 font-mono truncate">{m.email || m.uid}</p>
                      </div>
                      {canApplicant('applicant.teamMember', 'edit') && (
                      <button
                        onClick={() => handleUnassignTeamMember(m.uid)}
                        disabled={unassigningMember === m.uid}
                        className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition cursor-pointer shrink-0"
                        title="Remove assignment"
                      >
                        {unassigningMember === m.uid ? <Loader className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic bg-slate-50 border border-slate-200 p-3 rounded-xl">No team members assigned to this applicant.</p>
              )}
            </div>
          )}
        </div>
        </>}
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6">
          <nav className="flex space-x-0 overflow-x-auto">
            {(['profile', 'screening', 'placement', 'matches', 'documents', 'activity', 'events', 'finance', 'sign', 'email', 'tickets'] as const).filter(t => {
              // Enhanced referrers: hide Sign, Matches, and Email tabs (admin-only features)
              if (isEnhancedReferrer) {
                if (t === 'sign' || t === 'matches' || t === 'email') return false;
                return true;
              }
              // Workspace members: hide any module/action they have no access
              // to entirely (server enforces the same permissions).
              switch (t) {
                case 'screening': return canApplicant('applicant.screening', 'view');
                case 'placement': return canApplicant('applicant.placement', 'view');
                case 'matches': return canApplicant('applicant.matches', 'view');
                case 'documents': return canApplicant('applicant.documents', 'view');
                case 'activity': return canApplicant('applicant.activity', 'view') || canApplicant('applicant.comments', 'view') || canApplicant('applicant.notes', 'view');
                case 'events': return canApplicant('applicant.events', 'view');
                case 'finance': return canApplicant('applicant.invoice', 'view') || canApplicant('applicant.payments', 'view') || hasModuleAccess('finance', currentUserProfile);
                case 'sign': return canApplicant('applicant.signRequest', 'view');
                case 'email': return canApplicant('applicant.sendEmail', 'view') || hasModuleAccess('communications', currentUserProfile);
                case 'tickets': return canApplicant('applicant.tickets', 'view');
                case 'profile': return canApplicant('applicant.profile', 'view');
                default: return true;
              }
            }).map(t => {
              const showCheckmark = (() => {
                if (!isAdmin && !isEnhancedReferrer) return false;
                if (t === 'profile') {
                  return !!(app.personalDetails?.firstName && app.personalDetails?.lastName && app.personalDetails?.phone);
                }
                if (t === 'screening') {
                  const records = fetchedScreeningRecords.length > 0
                    ? fetchedScreeningRecords
                    : (app.screeningRecords || app.screening || (app as any).screenings || []);
                  const topStatus = String(app.screeningStatus || app.screeningResult || '').toLowerCase().trim();
                  if (['passed', 'approved', 'pass', 'sponsor ready', 'sponsor_ready', 'completed'].includes(topStatus)) {
                    return true;
                  }
                  return Array.isArray(records) && records.some((r: any) => {
                    const status = String(r.status || r.screeningStatus || '').toLowerCase().trim();
                    return ['passed', 'approved', 'pass', 'sponsor ready', 'sponsor_ready', 'completed'].includes(status) || r.isSponsorShareable === true || r.sponsorReady === true;
                  });
                }
                if (t === 'placement') {
                  const placements = app.placements || [];
                  const hasPrimaryPlaced = Array.isArray(placements) && placements.some((p: any) => (p.isPrimary || p.type === 'primary') && (p.status === 'Placed' || p.status === 'Confirmed'));
                  return hasPrimaryPlaced || app.placementStatus === 'Placed' || app.placementStatus === 'Confirmed';
                }
                if (t === 'documents') {
                  const docs = app.documents || [];
                  return Array.isArray(docs) && docs.length > 0 && !docs.some((d: any) => d.status === 'Pending' || d.status === 'PendingAdminReview');
                }
                if (t === 'finance') {
                  const invoices = app.invoices || [];
                  if (!Array.isArray(invoices) || invoices.length === 0) return true;
                  const totalDue = invoices.reduce((sum: number, inv: any) => sum + Number(inv.amountDue ?? inv.balance ?? 0), 0);
                  return totalDue <= 0;
                }
                return false;
              })();

              return (
                <button
                  key={t}
                  onClick={() => { appCtx.setAdminDetailTab(t); appCtx.handleLazyLoadTab?.(t); }}
                  className={`px-5 py-3 text-xs font-semibold border-b-2 transition-all cursor-pointer capitalize whitespace-nowrap inline-flex items-center gap-1.5 ${
                    tab === t
                      ? 'border-brand text-brand'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span>
                    {t === 'profile' ? 'Profile'
                      : t === 'activity' ? 'Activity'
                      : t === 'sign' ? 'Sign'
                      : t.charAt(0).toUpperCase() + t.slice(1)}
                  </span>
                  {showCheckmark && <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6 space-y-5">
          <React.Suspense fallback={
            <div className="p-8 text-center flex flex-col items-center justify-center space-y-3 bg-white rounded-2xl border border-slate-200/60 shadow-xs">
              <Loader className="h-6 w-6 animate-spin text-brand" />
              <p className="text-xs text-slate-500 font-medium">Loading section...</p>
            </div>
          }>
            {tab === 'profile' && canApplicant('applicant.profile', 'view') && <ProfileTab app={app} appCtx={appCtx} maskValue={maskValue} canViewContact={canViewContact} canEditProfile={canEditProfile} />}
            {tab === 'screening' && (
              <ScreeningTab
                applicationId={selectedApp.applicationId || selectedApp.id || selectedApp.applicantId || app.applicationId || app.id || app.applicantId || ''}
                isEnhancedReferrer={isEnhancedReferrer}
                currentUserProfile={currentUserProfile}
                onRecordsChange={(recs) => setFetchedScreeningRecords(recs)}
              />
            )}
            {tab === 'placement' && <PlacementTab app={app} appCtx={appCtx} batchesCtx={batchesCtx} isEnhancedReferrer={isEnhancedReferrer} currentUserProfile={currentUserProfile} />}
            {tab === 'matches' && (
              <MatchesTab
                applicantId={app.userId || app.applicantId || app.applicationId || app.id || selectedApp.userId || selectedApp.applicationId || ''}
                applicantName={`${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim() || app.userEmail || 'Applicant'}
                applicantProgram={(() => {
                  const p = (appCtx.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId);
                  return p?.name || app.programType || app.programPreferences?.programType || undefined;
                })()}
              />
            )}
            {tab === 'documents' && <DocumentsTab app={app} appCtx={appCtx} isEnhancedReferrer={isEnhancedReferrer} currentUserProfile={currentUserProfile} />}
            {tab === 'activity' && <ActivityTab app={app} appCtx={appCtx} isEnhancedReferrer={isEnhancedReferrer} canViewNotes={canApplicant('applicant.notes', 'view')} canEditNotes={canApplicant('applicant.notes', 'edit')} canViewComments={canApplicant('applicant.comments', 'view')} canEditComments={canApplicant('applicant.comments', 'edit')} />}
            {tab === 'events' && <EventsTab app={app} events={events} eventsCtx={eventsCtx} isEnhancedReferrer={isEnhancedReferrer} />}
            {tab === 'finance' && <ReceiptsTab app={app} isEnhancedReferrer={isEnhancedReferrer} currentUserProfile={currentUserProfile} />}
            {tab === 'sign' && !isEnhancedReferrer && <SignTab app={app} />}
            {tab === 'email' && (
              <EmailTab
                applicationId={selectedApp.applicationId}
                applicantEmail={selectedApp.personalDetails?.email || selectedApp.userEmail}
                applicantName={`${selectedApp.personalDetails?.firstName || ''} ${selectedApp.personalDetails?.lastName || ''}`.trim() || 'Applicant'}
                adminEmail={adminEmail}
                applicantData={selectedApp}
                canSendEmail={canApplicant('applicant.sendEmail', 'edit')}
                currentUserProfile={currentUserProfile}
                isEnhancedReferrer={isEnhancedReferrer}
              />
            )}
          </React.Suspense>
          {tab === 'tickets' && (
            <TicketsTabField
              app={selectedApp}
              applicationId={selectedApp.applicationId}
              applicantName={`${selectedApp.personalDetails?.firstName || ''} ${selectedApp.personalDetails?.lastName || ''}`.trim() || selectedApp.userEmail || 'Applicant'}
              applicantEmail={selectedApp.personalDetails?.email || selectedApp.userEmail}
              programName={selectedApp.programType}
              adminEmail={adminEmail}
              allAdmins={allAdmins}
              isEnhancedReferrer={isEnhancedReferrer}
              canCreate={canApplicant('applicant.tickets', 'edit')}
            />
          )}
        </div>



        {/* Status Change Confirmation Modal */}
        {showStatusConfirmModal && pendingStatus && (
          <Modal onClose={() => { if (!statusUpdating) { setShowStatusConfirmModal(false); setPendingStatus(null); } }} ariaLabel="Confirm Application Status Change">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden p-6 space-y-5">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Confirm Status Change</h3>
                  <p className="text-xs text-slate-500">Applicant: {getSafeApplicantName(app)} ({app.applicationId})</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-3">
                <p className="text-xs font-medium text-slate-700">
                  You are changing the main application status:
                </p>
                <div className="flex items-center justify-center gap-3 py-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(getSubStatus(app))}`}>
                    {getSubStatus(app)}
                  </span>
                  <span className="text-slate-400 font-bold">➔</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusStyle(pendingStatus)}`}>
                    {pendingStatus}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  This will set the main status for this application. The backend will automatically dispatch appropriate notification emails to the applicant.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={statusUpdating}
                  onClick={() => { setShowStatusConfirmModal(false); setPendingStatus(null); }}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={statusUpdating}
                  onClick={async () => {
                    try {
                      setStatusUpdating(true);
                      // handleUpdateStatus surfaces its own failures
                      // (toast/alert) and resolves false when the change
                      // did not commit — only close + report success on
                      // an explicit success signal.
                      const ok = await appCtx.handleUpdateStatus?.(app.applicationId, pendingStatus as any);
                      if (!ok) {
                        // Keep the modal open with the pending status
                        // intact so the admin can retry or cancel.
                        return;
                      }
                      notifyToast('success', `Application status changed to ${pendingStatus}`);
                      setShowStatusConfirmModal(false);
                      setPendingStatus(null);
                    } catch (err: any) {
                      notifyToast('error', err?.message || 'Failed to update application status.');
                    } finally {
                      setStatusUpdating(false);
                    }
                  }}
                  className="px-4 py-2 rounded-xl btn-primary text-xs font-semibold text-white flex items-center gap-2 cursor-pointer shadow-md transition-all disabled:opacity-50"
                >
                  {statusUpdating && <Loader className="h-3.5 w-3.5 animate-spin" />}
                  <span>Submit Change</span>
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showChangeUser && (
          <Modal onClose={() => setShowChangeUser(false)} ariaLabel="Change Application User">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Change Application User</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Move this application to another existing Firebase user.</p>
                </div>
                <button aria-label="Close" title="Close" onClick={() => setShowChangeUser(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Current owner UID: <span className="font-mono font-bold">{app.userId || '—'}</span>
                </div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500">New user email</label>
                <div className="flex gap-2">
                  <input value={changeUserEmail} onChange={(e) => setChangeUserEmail(e.target.value)} placeholder="user@example.com" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-primary/20" />
                  <button onClick={lookupChangeUser} disabled={changeUserLoading || !changeUserEmail.trim()} className="rounded-xl bg-brand hover:bg-brand-active px-4 py-2 text-xs font-black text-white disabled:opacity-50">{changeUserLoading ? 'Checking…' : 'Find'}</button>
                </div>
                {changeUserFound && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                    <p className="text-xs font-black text-emerald-900">User found</p>
                    <div className="text-xs text-emerald-800 space-y-1">
                      <p><span className="font-bold">Email:</span> {changeUserFound.email}</p>
                      <p><span className="font-bold">UID:</span> <span className="font-mono">{changeUserFound.uid}</span></p>
                      <p><span className="font-bold">Name:</span> {changeUserFound.displayName || '—'}</p>
                    </div>
                    <label className="flex items-start gap-2 text-xs text-emerald-900">
                      <input type="checkbox" checked={changeUserUpdateEmail} onChange={(e) => setChangeUserUpdateEmail(e.target.checked)} className="mt-0.5" />
                      <span>Also update the existing applicant email on this application to this user's email.</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button onClick={() => setShowChangeUser(false)} className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600">Cancel</button>
                <button onClick={confirmChangeUser} disabled={!changeUserFound || changeUserLoading} className="px-4 py-2 rounded-xl btn-primary text-white text-xs font-black disabled:opacity-50">Confirm Change</button>
              </div>
            </div>
          </Modal>
        )}

        {/* Email Edit Confirmation Modal */}
        {showEmailEdit && (
          <Modal onClose={() => setShowEmailEdit(false)} ariaLabel="Change Email Address">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Change Email Address</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Update the applicant's email and linked applications</p>
                </div>
                <button aria-label="Close" title="Close" onClick={() => setShowEmailEdit(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Body */}
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <p className="text-xs text-slate-500">
                  You are about to change the email for <strong>{app.personalDetails?.firstName || 'this applicant'}</strong>{' '}
                  from <code className="bg-slate-100 px-1 rounded text-[11px]">{app.userEmail}</code> to{' '}
                  <code className="bg-slate-100 px-1 rounded text-[11px]">{adminEmailNew}</code>.
                </p>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-600">New email</label>
                  <input
                    type="email"
                    value={adminEmailNew}
                    onChange={e => { setAdminEmailNew(e.target.value); setAdminEmailError(''); }}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl outline-none focus:border-brand focus:ring-1 focus:ring-brand-primary"
                  />
                </div>

                {/* Associated applications */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-slate-600">
                      Associated Applications {adminEmailAppsLoading && '(loading...)'}
                    </label>
                    {adminEmailApps.length > 0 && !adminEmailAppsLoading && (
                      <button
                        onClick={() => {
                          if (adminEmailSelectedApps.length === adminEmailApps.length) {
                            setAdminEmailSelectedApps([]);
                          } else {
                            setAdminEmailSelectedApps(adminEmailApps.map((a: any) => a.applicationId));
                          }
                        }}
                        className="text-[10px] text-brand hover:text-blue-800 font-semibold cursor-pointer"
                      >
                        {adminEmailSelectedApps.length === adminEmailApps.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                    {adminEmailAppsLoading ? (
                      <div className="p-4 text-center text-xs text-slate-500">Loading applications...</div>
                    ) : adminEmailApps.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500">No applications found for this user.</div>
                    ) : (
                      adminEmailApps.map((a: any) => {
                        const name = [a.personalDetails?.firstName, a.personalDetails?.lastName].filter(Boolean).join(' ') || a.userEmail || '—';
                        const isSelected = adminEmailSelectedApps.includes(a.applicationId);
                        return (
                          <label
                            key={a.applicationId}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setAdminEmailSelectedApps(prev =>
                                  isSelected ? prev.filter(id => id !== a.applicationId) : [...prev, a.applicationId]
                                );
                              }}
                              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium text-slate-900 truncate">{name}</p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {(() => { const p = (appCtx.programsList || []).find((x: any) => x.id === a.programId || x.programId === a.programId); return p?.name || a.programType; })()} · {a.status}
                              </p>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {adminEmailError && (
                  <p className="text-xs font-semibold text-red-600">{adminEmailError}</p>
                )}
              </div>
              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200">
                <span className="text-[11px] text-slate-500">
                  {adminEmailSelectedApps.length} of {adminEmailApps.length} application(s) selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowEmailEdit(false)}
                    className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdminEmailUpdate}
                    disabled={adminEmailUpdating || !adminEmailNew || !EMAIL_REGEX.test(adminEmailNew) || adminEmailSelectedApps.length === 0}
                    className="px-4 py-2 text-xs font-bold text-white btn-primary  rounded-xl disabled:opacity-40 cursor-pointer transition-all"
                  >
                    {adminEmailUpdating ? 'Updating...' : `Update ${adminEmailSelectedApps.length} App(s)`}
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* Assign to Team Member(s) Modal */}
        <React.Suspense fallback={null}>
          <WorkspaceTeamAssignModal
            open={showTeamAssignModal}
            applicationIds={[app.applicationId]}
            onClose={() => setShowTeamAssignModal(false)}
            onAssigned={(assignedMembers: any[]) => {
              const memberSnapshots = (assignedMembers || []).map(m => ({ uid: m.uid, name: m.name, email: m.email, role: m.role }));
              const existingIds = app.assignedTeamMemberIds || [];
              const existingSnaps = app.assignedTeamMembers || [];
              const mergedIds = [...new Set([...existingIds, ...memberSnapshots.map(m => m.uid)])];
              appCtx.setSelectedApp?.({
                ...app,
                assignedTeamMemberIds: mergedIds,
                assignedTeamMembers: [
                  ...existingSnaps.filter(m => mergedIds.includes(m.uid)),
                  ...memberSnapshots.filter(m => !existingIds.includes(m.uid)),
                ],
                updatedAt: new Date().toISOString(),
              });
              appCtx.fetchApplications?.({ force: true });
            }}
          />

          {/* Workflow drawer */}
          {showWorkflowDrawer && app.userId && (
            <ApplicantDrawer
              uid={app.userId}
              onClose={() => setShowWorkflowDrawer(false)}
              onChanged={() => { appCtx.fetchApplications?.(); }}
            />
          )}
        </React.Suspense>
      </div>
    </div>
  );
}

/* ───────────────────── EVENTS TAB (per-applicant) ───────────────────── */

const EventsTab = React.memo(function EventsTab({ app, events, eventsCtx, isEnhancedReferrer }: { app: Application; events: any[]; eventsCtx: any; isEnhancedReferrer?: boolean }) {
  // Match events by applicantId (application doc) OR applicantUserId (user UID).
  // applicantId is the reliable key for per-applicant listing — includes
  // "Awaiting Response" invitation placeholders and legacy events.
  const appEvents = (events || []).filter((ev: any) =>
    ev.applicantId === app.applicationId || ev.applicantUserId === app.userId
  );

  // Group events workspace-scoped states
  const [workspaceGroupEvents, setWorkspaceGroupEvents] = useState<any[]>([]);
  const [loadingGroupEvents, setLoadingGroupEvents] = useState(false);
  const [isManagingGroupInvites, setIsManagingGroupInvites] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const fetchWorkspaceGroupEvents = async () => {
    setLoadingGroupEvents(true);
    try {
      const activeWorkspaceId = getActiveWorkspaceId();
      const data = await safeFetch('/api/admin/events');
      const list: any[] = (data.events || []).filter((ev: any) =>
        ev.isGroupEvent && (!activeWorkspaceId || !ev.workspaceId || isSameWorkspaceSync(ev.workspaceId, activeWorkspaceId))
      );
      setWorkspaceGroupEvents(list);
    } catch (err) {
      console.error('Failed to fetch workspace group events:', err);
    } finally {
      setLoadingGroupEvents(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceGroupEvents();
  }, []);

  const handleGroupInvite = async (eventId: string) => {
    setIsActionInProgress(true);
    try {
      const res = await safeFetch(`/api/admin/group-events/${eventId}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: [app.applicationId] })
      });
      fetchWorkspaceGroupEvents();
      eventsCtx.fetchEvents();

      if (res?.invited && res.invited.length > 0) {
        notifyToast('success', 'Candidate invited to group event!');
      } else if (res?.errors && res.errors.length > 0) {
        notifyToast('error', `Cannot invite candidate: ${res.errors[0]?.error || 'Eligibility requirements not met'}`);
      } else {
        notifyToast('info', 'Invitation processed.');
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to send invitation');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleGroupRemove = async (eventId: string) => {
    if (!(await confirmDialog('Are you sure you want to remove this applicant? This will also cancel any booked slot they have.'))) return;
    setIsActionInProgress(true);
    try {
      await safeFetch(`/api/admin/group-events/${eventId}/invitations/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId: app.applicationId })
      });
      fetchWorkspaceGroupEvents();
      eventsCtx.fetchEvents();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to remove invitation');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState<string>('Embassy');
  const [formDateTime, setFormDateTime] = useState('');
  const [formTimezone, setFormTimezone] = useState('America/New_York');
  const [formLocation, setFormLocation] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState<string>('Scheduled');
  const [formEmployerId, setFormEmployerId] = useState<string>('');
  const [formPartnerId, setFormPartnerId] = useState<string>('');
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isVirtual, setIsVirtual] = useState(false);
  const [formLocationType, setFormLocationType] = useState<'physical' | 'url' | 'phone' | 'jitsi'>('physical');
  const [formDuration, setFormDuration] = useState<number>(30);
  const [formAssignedTo, setFormAssignedTo] = useState('');
  const [formAssignedToName, setFormAssignedToName] = useState('');
  const [agentsList, setAgentsList] = useState<{ uid: string; name: string; email: string; role?: string }[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentsError, setAgentsError] = useState(false);
  const [employersList, setEmployersList] = useState<any[]>([]);
  const [sending, setSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ success: boolean; url?: string } | null>(null);
  const [viewEvent, setViewEvent] = useState<any | null>(null);
  const currentUid = auth.currentUser?.uid || '';

  const fetchEmployers = async () => {
    try {
      const data = await safeFetch('/api/admin/employers');
      setEmployersList(data.employers || []);
    } catch {}
  };

  const fetchAgents = async () => {
    setAgentsError(false);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) { setAgentsLoaded(true); return; }
      // 12s timeout so a slow/hung response can never leave the UI stuck on
      // "Loading agents..." forever — it falls through to the error/retry state.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const res = await fetch('/api/admin/agents', { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
        if (res.ok) { const data = await res.json(); setAgentsList(Array.isArray(data.agents) ? data.agents : []); }
        else { console.warn('fetchAgents returned', res.status); setAgentsError(true); }
      } finally {
        clearTimeout(timer);
      }
    } catch (err) {
      console.warn('fetchAgents failed:', err);
      setAgentsError(true);
    } finally {
      setAgentsLoaded(true);
    }
  };

  // Pre-load agents on mount so they're ready when the form opens
  React.useEffect(() => { fetchAgents(); }, []);

  const openCreate = () => {
    setIsEditing(null);
    setFormTitle('');
    setFormType('Embassy');
    setFormDateTime('');
    setFormTimezone('America/New_York');
    setFormLocation('');
    setFormDescription('');
    setFormStatus('Scheduled');
    setFormEmployerId('');
    setFormPartnerId('');
    setIsVirtual(false);
    setFormLocationType('physical');
    setFormDuration(30);
    setFormAssignedTo('');
    setFormAssignedToName('');
    fetchEmployers();
    fetchAgents();
    setShowForm(true);
  };

  const openEdit = (ev: any) => {
    setIsEditing(ev.eventId);
    setFormTitle(ev.title);
    setFormType(ev.eventType);
    const targetTz = ev.timezone || 'America/New_York';
    setFormDateTime(ev.dateTime ? utcToLocalISO(ev.dateTime, targetTz) : '');
    setFormTimezone(targetTz);
    setFormLocation(ev.location || '');
    setFormDescription(ev.description || '');
    setFormStatus(ev.status || 'Scheduled');
    setFormEmployerId(ev.employerId || '');
    setFormPartnerId(ev.partnerId || '');
    // Derive the destination type from stored metadata (legacy fallbacks:
    // isVirtual/meetingUrl imply Jitsi; otherwise physical).
    const derivedType = (ev.locationType as 'physical' | 'url' | 'phone' | 'jitsi' | undefined)
      || (ev.isVirtual || ev.meetingUrl ? 'jitsi' : 'physical');
    setIsVirtual(derivedType === 'jitsi');
    setFormLocationType(derivedType === 'physical' || derivedType === 'url' || derivedType === 'phone' ? derivedType : 'physical');
    setFormDuration(ev.durationMinutes || 30);
    setFormAssignedTo(ev.assignedTo || '');
    setFormAssignedToName(ev.assignedToName || '');
    fetchEmployers();
    fetchAgents();
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formDateTime) {
      notifyToast('warning', 'Please fill in title and date/time.');
      return;
    }
    const isJitsi = formLocationType === 'jitsi';
    if (!isJitsi && !formLocation.trim()) {
      notifyToast('warning', 'Please provide a location, meeting link, or phone number.');
      return;
    }
    try {
      const payload: any = {
        applicantId: app.applicationId,
        title: formTitle.trim(),
        eventType: formType,
        dateTime: localToUTC(formDateTime, formTimezone),
        timezone: formTimezone,
        location: isJitsi ? '' : formLocation.trim(),
        description: formDescription.trim(),
        status: formStatus,
        isVirtual: isJitsi,
        locationType: formLocationType,
        durationMinutes: formDuration,
      };
      if (isJitsi) {
        payload.meetingProvider = 'jitsi';
      }
      if (formAssignedTo) {
        payload.assignedTo = formAssignedTo;
        payload.assignedToName = formAssignedToName;
      }
      if (formEmployerId) payload.employerId = formEmployerId;
      if (formPartnerId) payload.partnerId = formPartnerId;
      if (isEditing) {
        payload.eventId = isEditing;
        // On edit of a Jitsi event, don't send a meetingUrl so the server
        // PRESERVES the existing permanent room link.
      }
      await safeFetch('/api/admin/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await eventsCtx.fetchEvents();
      setShowForm(false);
    } catch (err) {
      console.error('Failed to save event:', err);
      notifyToast('error', 'Failed to save event. Please try again.');
    }
  };

  const handleDelete = async (ev: any) => {
    if (!(await confirmDialog(`Delete event "${ev.title}"?`))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/events/${ev.eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'Delete failed');
      }
      await eventsCtx.fetchEvents();
      notifyToast('success', `Event "${ev.title}" deleted successfully.`);
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      notifyToast('error', err.message || 'Failed to delete event. Please try again.');
    }
  };

  const typeColors: Record<string, string> = {
    Embassy: 'bg-amber-50 text-amber-700 border-amber-100',
    Interview: 'bg-brand-active border-blue-100',
    Orientation: 'bg-purple-50 text-purple-700 border-purple-100',
    Training: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    Flight: 'bg-sky-50 text-sky-700 border-sky-100',
    Medical: 'bg-rose-50 text-rose-700 border-rose-100',
    Other: 'bg-slate-50 text-slate-700 border-slate-100',
  };

  const statusColors: Record<string, string> = {
    Scheduled: 'bg-amber-50 text-amber-700 border-amber-100',
    Completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          Events ({appEvents.length})
        </h3>
        <button
          onClick={openCreate}
          className="px-3 py-1.5 btn-primary  text-white text-caption font-semibold rounded-lg transition-all flex items-center space-x-1 cursor-pointer"
        >
          <Plus className="h-3.5 w-3.5" /><span>Add Event</span>
        </button>
      </div>

      {appEvents.length === 0 && !showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm">
          <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-body font-semibold text-slate-500">No events yet</p>
          <p className="text-caption text-slate-400 mt-1">Add an event to track this applicant's journey.</p>
        </div>
      )}

      {appEvents.map((ev: any) => (
        <div key={ev.eventId} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-start justify-between hover:shadow-md transition-all">
          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="flex items-center space-x-2 flex-wrap">
              <span className={`text-caption font-bold px-2 py-0.5 rounded-lg border ${typeColors[ev.eventType] || 'bg-slate-50 text-slate-700 border-slate-100'}`}>
                {ev.eventType}
              </span>
              <span className={`text-caption font-semibold px-2 py-0.5 rounded-lg border ${statusColors[ev.status] || 'bg-slate-50 text-slate-700 border-slate-100'}`}>
                {ev.status}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900">{ev.title}</p>
            {ev.description && <p className="text-caption text-slate-500">{ev.description}</p>}
            <div className="flex items-center space-x-3 text-caption font-medium text-slate-400">
              {ev.dateTime && (
                <span className="flex items-center space-x-1">
                  <CalendarDays className="h-3 w-3" />
                  <span>{new Date(ev.dateTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', ...(ev.timezone ? { timeZone: ev.timezone, timeZoneName: 'short' } : {}) })}</span>
                </span>
              )}
              {ev.timezone && (
                <span className="flex items-center space-x-1 text-slate-400">
                  <Clock className="h-3 w-3" />
                  <span>{ev.timezone.replace('_', ' ').split('/').pop()}</span>
                </span>
              )}
              {ev.location && (
                <span className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>{ev.location}</span>
                </span>
              )}
            </div>
            {ev.status === 'Cancelled' && (
              <p className="text-[10px] text-rose-600 mt-0.5">
                <strong>Cancelled</strong> by {ev.cancelledBy === 'applicant' ? 'Applicant'
                  : ev.cancelledBy === 'employer' ? 'Employer'
                  : ev.cancelledBy || 'Admin'}
                {ev.cancelReason ? ` — Reason: ${ev.cancelReason}` : ''}
              </p>
            )}
            {ev.rescheduledBy && (
              <p className="text-[10px] text-purple-600 mt-0.5">
                <strong>Rescheduled</strong> by {ev.rescheduledBy === 'applicant' ? 'Applicant'
                  : ev.rescheduledBy === 'employer' ? 'Employer'
                  : ev.rescheduledBy || 'Admin'}
                {ev.rescheduleReason ? ` — Reason: ${ev.rescheduleReason}` : ''}
              </p>
            )}
            {ev.employerId && (
              <p className="text-[10px] text-brand mt-0.5">Assigned Employer: {ev.employerName || ev.employerId}</p>
            )}
          </div>
          <div className="flex items-center space-x-1 shrink-0 ml-3">
            <button onClick={() => setViewEvent(ev)} className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer" title="View Details">
              <Eye className="h-3.5 w-3.5" />
            </button>
            {(!isEnhancedReferrer || ev.createdBy === currentUid) && (
              <button onClick={() => openEdit(ev)} className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer" title="Edit">
                <Edit3 className="h-3.5 w-3.5" />
              </button>
            )}
            {(!isEnhancedReferrer || ev.createdBy === currentUid) && (
              <button onClick={() => handleDelete(ev)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* ── GROUP EVENTS & INVITATION WORKFLOWS ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-extrabold text-slate-900">Group Events History & Invitations</h4>
            <p className="text-[11px] text-slate-500 font-semibold mt-0.5">Manage and track invitations to workspace-wide Group Events.</p>
          </div>
          <button
            onClick={() => setIsManagingGroupInvites(!isManagingGroupInvites)}
            className="px-3.5 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-caption font-bold rounded-xl transition flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <Layers className="h-4 w-4 text-brand" />
            <span>{isManagingGroupInvites ? 'Hide Invitation Manager' : 'Manage Group Invitations'}</span>
          </button>
        </div>

        {isManagingGroupInvites && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <h5 className="text-xs font-black text-slate-800 uppercase tracking-wider">Workspace Group Events Catalogue</h5>
            {loadingGroupEvents ? (
              <p className="text-slate-400 text-center py-2 italic">Loading events...</p>
            ) : workspaceGroupEvents.length === 0 ? (
              <p className="text-slate-400 text-center py-2 italic">No Group Events registered in this workspace yet.</p>
            ) : (
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto border border-slate-100 rounded-lg">
                {workspaceGroupEvents.map(ev => {
                  const invitations = ev.invitations || {};
                  const isInvited = !!invitations[app.applicationId] || (ev.invitationList || []).includes(app.applicationId);

                  // Eligibility checks
                  const cfg = ev.groupEventConfig || {};
                  let isEligible = true;
                  let ineligibleReason = '';

                  if (cfg.eligibleProgramId && cfg.eligibleProgramId !== 'all' && cfg.eligibleProgramId !== '') {
                    if (app.programId !== cfg.eligibleProgramId) {
                      isEligible = false;
                      ineligibleReason = 'Program mismatch';
                    }
                  }
                  if (isEligible && cfg.eligibleTerm && cfg.eligibleTerm !== 'all' && cfg.eligibleTerm !== '') {
                    if (app.programTerm !== cfg.eligibleTerm) {
                      isEligible = false;
                      ineligibleReason = 'Term mismatch';
                    }
                  }
                  if (isEligible && cfg.eligibleStage !== undefined && cfg.eligibleStage !== null && cfg.eligibleStage !== 'all' && cfg.eligibleStage !== '') {
                    if (String(app.masterStage) !== String(cfg.eligibleStage)) {
                      isEligible = false;
                      ineligibleReason = 'Stage mismatch';
                    }
                  }
                  if (isEligible && cfg.eligibleStatus && cfg.eligibleStatus !== 'all' && cfg.eligibleStatus !== '') {
                    if (app.submissionStatus !== cfg.eligibleStatus) {
                      isEligible = false;
                      ineligibleReason = 'Status mismatch';
                    }
                  }

                  return (
                    <div key={ev.eventId} className="p-3 hover:bg-slate-50/50 flex flex-wrap items-center justify-between gap-3 text-[11px] font-medium">
                      <div className="space-y-0.5">
                        <p className="font-bold text-slate-800">{ev.title}</p>
                        <p className="text-[11px] text-slate-500">
                          {new Date(ev.dateTime).toLocaleDateString()} at {new Date(ev.dateTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ({ev.timezone})
                        </p>
                        <div className="text-[11px] text-slate-500 mt-1 flex gap-2">
                          <span>Req: {cfg.eligibleProgramId || 'All Prog'} · {cfg.eligibleTerm || 'All Terms'}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {isInvited ? (
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-100 text-[10px] uppercase">
                              {invitations[app.applicationId]?.status || 'Invited'}
                            </span>
                            <button
                              onClick={() => handleGroupRemove(ev.eventId)}
                              disabled={isActionInProgress}
                              className="px-2.5 py-1 text-[10px] font-bold bg-white border border-rose-200 text-rose-600 rounded-lg hover:bg-rose-50 transition cursor-pointer"
                            >
                              Remove Invitation
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {isEligible ? (
                              <span className="text-emerald-600 font-bold flex items-center gap-1 text-[10px] mr-1">
                                <CheckCircle className="h-3.5 w-3.5" /> Eligible
                              </span>
                            ) : (
                              <span className="text-rose-600 font-bold flex items-center gap-1 text-[10px] mr-1" title={ineligibilityReason}>
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {ineligibilityReason}
                              </span>
                            )}
                            <button
                              onClick={() => handleGroupInvite(ev.eventId)}
                              disabled={!isEligible || isActionInProgress}
                              className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30 font-bold text-[10px] transition cursor-pointer flex items-center gap-0.5"
                            >
                              <Plus className="h-3 w-3" /> Invite
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Dynamic history tracking log */}
        <div className="space-y-2">
          <h5 className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Applicant's Group Event History</h5>
          {(() => {
            const invitedEvents = workspaceGroupEvents.filter(ev => {
              const invitations = ev.invitations || {};
              const invitationList = ev.invitationList || [];
              return !!invitations[app.applicationId] || invitationList.includes(app.applicationId);
            });

            if (invitedEvents.length === 0) {
              return <p className="text-slate-500 italic text-[11px] py-1">No group event history or active invitations for this candidate.</p>;
            }

            return (
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 text-[10px] uppercase select-none">
                      <th className="p-2.5">Event Name</th>
                      <th className="p-2.5">Event Date</th>
                      <th className="p-2.5">Restrictions</th>
                      <th className="p-2.5">Invite Status</th>
                      <th className="p-2.5">Registration & Attendance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {invitedEvents.map(ev => {
                      const invite = ev.invitations?.[app.applicationId] || {};
                      const bookedSlot = (ev.slots || []).find((s: any) => s.bookedBy?.applicationId === app.applicationId);

                      return (
                        <tr key={ev.eventId} className="hover:bg-slate-50/50">
                          <td className="p-2.5">
                            <span className="font-bold text-slate-800 block">{ev.title}</span>
                            <span className="text-[11px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 mt-1 inline-block">{ev.eventType}</span>
                          </td>
                          <td className="p-2.5 text-slate-600 text-[10px]">
                            {new Date(ev.dateTime).toLocaleDateString()}
                            <div className="text-slate-400">{ev.timezone}</div>
                          </td>
                          <td className="p-2.5 text-slate-500 text-[10px]">
                            <div>Prog: {ev.groupEventConfig?.eligibleProgramId || 'All'}</div>
                            <div>Term: {ev.groupEventConfig?.eligibleTerm || 'All'}</div>
                          </td>
                          <td className="p-2.5">
                            <span className="px-2 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-100 text-[10px] uppercase">
                              {invite.status || 'Invited'}
                            </span>
                            <div className="text-[11px] text-slate-500 mt-1">
                              Invited: {invite.invitedAt ? new Date(invite.invitedAt).toLocaleDateString() : '—'}
                            </div>
                          </td>
                          <td className="p-2.5 text-[10px]">
                            {bookedSlot ? (
                              <div className="space-y-1">
                                <p className="text-slate-700 font-bold">Registered (Slot: {bookedSlot.startTime} - {bookedSlot.endTime})</p>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-bold text-slate-400">Attendance:</span>
                                  <span className={`px-2 py-0.5 rounded-lg border text-[11px] font-black uppercase ${
                                    bookedSlot.checkInStatus === 'checked_in' ? 'bg-emerald-50 text-emerald-700 border-emerald-150' :
                                    bookedSlot.checkInStatus === 'no_show' ? 'bg-rose-50 text-rose-700 border-rose-150' :
                                    'bg-slate-50 text-slate-500 border-slate-200'
                                  }`}>
                                    {bookedSlot.checkInStatus || 'pending'}
                                  </span>
                                </div>
                                {invite.registrationDate && (
                                  <p className="text-[11px] text-slate-500">Booked: {new Date(invite.registrationDate).toLocaleDateString()}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500 italic">Not Registered</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Event Details modal — read-only, similar to the main Events view */}
      {viewEvent && (
        <Modal onClose={() => setViewEvent(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div>
                <h3 className="text-sm font-bold text-white">{viewEvent.title}</h3>
                <p className="text-[11px] text-blue-100">{viewEvent.eventType} · {viewEvent.status}</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setViewEvent(null)} className="p-1.5 text-white/70 hover:text-white rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {viewEvent.dateTime && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold">{new Date(viewEvent.dateTime).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
              {viewEvent.timezone && <p className="text-xs text-slate-500">Time zone: {viewEvent.timezone}</p>}
              {viewEvent.location && (
                <div className="flex items-center gap-2 text-sm">
                  {viewEvent.isVirtual ? <Video className="h-4 w-4 text-purple-500" /> : <MapPin className="h-4 w-4 text-slate-400" />}
                  <span className="text-slate-600 break-all">{viewEvent.location}</span>
                </div>
              )}
              {viewEvent.isVirtual && viewEvent.meetingUrl && viewEvent.status !== 'Cancelled' && viewEvent.status !== 'Declined' && (
                <a href={viewEvent.meetingUrl} target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center px-4 py-3 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-sm font-bold transition-all">
                  <Video className="h-4 w-4 inline mr-2" /> Join Meeting Room
                </a>
              )}
              {viewEvent.assignedToName && (
                <p className="text-xs text-slate-500">Organizer: <strong>{viewEvent.assignedToName}</strong></p>
              )}
              {viewEvent.description && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Description / Notes</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{viewEvent.description}</p>
                </div>
              )}
              {viewEvent.isSchedulingRequest && viewEvent.schedulingToken && viewEvent.status === 'Awaiting Response' && (
                <a href={`/schedule/${viewEvent.schedulingToken}`} target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center px-4 py-3 btn-primary text-white rounded-xl text-sm font-bold transition-all">
                  Open Invitation
                </a>
              )}
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className={`px-2 py-0.5 rounded-full font-bold capitalize ${
                  viewEvent.status === 'Cancelled' || viewEvent.status === 'Declined' ? 'bg-rose-50 text-rose-700' :
                  viewEvent.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-700' :
                  viewEvent.status === 'Awaiting Response' ? 'bg-amber-50 text-amber-700' :
                  viewEvent.status === 'Rescheduled' ? 'bg-indigo-50 text-indigo-700' :
                  'bg-blue-50 text-blue-700'
                }`}>{viewEvent.status}</span>
                {viewEvent.rejectionReason && <span className="text-rose-600">Rejection: {viewEvent.rejectionReason}</span>}
                {viewEvent.rejectedAt && <span>· Rejected {new Date(viewEvent.rejectedAt).toLocaleString()}</span>}
                {viewEvent.durationMinutes && <span>· {viewEvent.durationMinutes} min</span>}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Event Form Modal — uses Modal portal so it renders above all elements */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 mx-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-800">{isEditing ? 'Edit Event' : 'Add Event'}</h3>
              <button aria-label="Close" title="Close" onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Title *</p>
                <input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Event title" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Type</p>
                  <select value={formType} onChange={e => setFormType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                    {['Embassy', 'Interview', 'Orientation', 'Training', 'Flight', 'Medical', 'Other'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Status</p>
                  <select value={formStatus} onChange={e => setFormStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                    {['Scheduled', 'Pending', 'Awaiting Response', 'Confirmed', 'Rescheduled', 'Completed', 'Cancelled', 'Declined'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Date & Time *</p>
                  <input type="datetime-local" value={formDateTime} onChange={e => setFormDateTime(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Duration (min)</p>
                  <input type="number" min={5} step={5} value={formDuration} onChange={e => setFormDuration(parseInt(e.target.value) || 30)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Time Zone *</p>
                  <select value={formTimezone} onChange={e => setFormTimezone(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                    {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>

              {/* Meeting destination type */}
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Meeting Location *</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {([
                    { key: 'physical', label: 'Physical Location', icon: MapPin },
                    { key: 'url', label: 'Custom URL', icon: ExternalLink },
                    { key: 'phone', label: 'Phone Meeting', icon: Phone },
                    { key: 'jitsi', label: 'Jitsi Video', icon: Video },
                  ] as const).map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setFormLocationType(opt.key)}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
                        formLocationType === opt.key
                          ? 'bg-brand text-white border-brand shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <opt.icon className="h-3.5 w-3.5" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                {formLocationType === 'jitsi' ? (
                  <>
                    <p className="text-caption font-semibold text-slate-500 mb-1">Meeting (auto-generated)</p>
                    <div className="flex items-center justify-between px-3 py-2 text-sm border border-emerald-200 bg-emerald-50 rounded-xl">
                      <span className="flex items-center gap-1.5 text-emerald-700 font-medium">
                        <Video className="h-3.5 w-3.5" />
                        {isEditing ? 'Permanent Jitsi room will be preserved on save' : 'A unique Jitsi room will be created on save'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-caption font-semibold text-slate-500 mb-1">
                      {formLocationType === 'phone' ? 'Phone Number *' : formLocationType === 'url' ? 'Meeting Link *' : 'Location *'}
                    </p>
                    <input
                      value={formLocation}
                      onChange={e => setFormLocation(e.target.value)}
                      placeholder={formLocationType === 'phone' ? '+1-555-0100 (conference PIN)' : formLocationType === 'url' ? 'https://meet.google.com/...' : 'Street address, building, room'}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    />
                  </>
                )}
              </div>

              {/* Assign to Agent */}
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Assign To (Agent / Staff){agentsError && (
                  <button type="button" onClick={fetchAgents} className="ml-2 text-[10px] font-bold text-rose-500 hover:text-rose-700 underline cursor-pointer">Retry</button>
                )}</p>
                {agentsError ? (
                  <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">Failed to load agents. <button type="button" onClick={fetchAgents} className="font-bold underline cursor-pointer">Retry</button></div>
                ) : !agentsLoaded ? (
                  <div className="text-xs text-slate-500 px-3 py-2">Loading agents…</div>
                ) : (
                  <SearchableSelect
                    value={formAssignedTo}
                    onChange={(v) => {
                      if (!v) { setFormAssignedTo(''); setFormAssignedToName(''); return; }
                      const ag = agentsList.find(a => a.uid === v);
                      setFormAssignedTo(v);
                      setFormAssignedToName(ag ? ag.name : v);
                    }}
                    placeholder="Search admin, referrer, staff…"
                    emptyLabel="No agents found"
                    clearLabel="— Not assigned —"
                    options={agentsList.map(ag => ({
                      value: ag.uid,
                      label: ag.name,
                      hint: ag.email,
                      tag: ag.role ? `{${ag.role}}` : undefined,
                    }))}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Assign to Employer</p>
                  <SearchableSelect
                    value={formEmployerId}
                    onChange={setFormEmployerId}
                    placeholder="Search employer…"
                    emptyLabel="No employers found"
                    clearLabel="None"
                    options={employersList.map((em: any) => ({
                      value: em.id,
                      label: em.companyName || em.name || em.id,
                      tag: '{Employer}',
                    }))}
                  />
                </div>
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Partner / Tag</p>
                  <input value={formPartnerId} onChange={e => setFormPartnerId(e.target.value)} placeholder="Partner name or tag" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                </div>
              </div>
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Description</p>
                <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} placeholder="Optional notes..." rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 resize-none" />
              </div>
            </div>
            <div className="flex justify-between items-center pt-1">
              <div className="flex gap-2">
                {!isEditing && (
                  <button onClick={async () => {
                    if (!formTitle.trim() || !formAssignedTo) { notifyToast('warning', 'Please fill in title and select an agent first.'); return; }
                    setSending(true); setInviteResult(null);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await fetch('/api/scheduling/create', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          applicantId: app.applicationId,
                          applicantEmail: app.userEmail || '',
                          applicantName: `${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim(),
                          agentId: formAssignedTo, agentName: formAssignedToName,
                          title: formTitle.trim(), description: formDescription.trim(),
                          eventType: formType, durationMinutes: 30,
                        }),
                      });
                      const data = await res.json();
                      if (data.success && data.schedulingUrl) {
                        setInviteResult({ success: true, url: data.schedulingUrl });
                        try {
                          const { emailSchedulingInvite } = await import('../../lib/emailTriggers');
                          await emailSchedulingInvite(app, formTitle.trim(), data.schedulingUrl, formAssignedToName, formDescription.trim());
                        } catch {}
                      } else { setInviteResult({ success: false }); }
                    } catch { setInviteResult({ success: false }); }
                    setSending(false);
                  }} className="px-3 py-2 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-xl transition cursor-pointer">
                    {sending ? 'Sending...' : 'Invite for Event'}
                  </button>
                )}
                {inviteResult && (
                  <span className={`text-[10px] font-semibold ${inviteResult.success ? 'text-emerald-600' : 'text-red-600'}`}>
                    {inviteResult.success ? '✅ Invite sent!' : '❌ Failed'}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 text-caption font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button onClick={handleSave} className="px-4 py-2 btn-primary text-white text-caption font-bold rounded-xl transition-all cursor-pointer">
                  {isEditing ? 'Update' : 'Create Event'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
});

/* ───────────────────── RECEIPTS TAB (per-applicant) ───────────────────── */

interface ReceiptRecord {
  receiptId: string;
  applicantId: string;
  amount: number;
  currency: string;
  description: string;
  category: string;
  fileName: string;
  storagePath: string;
  downloadUrl?: string;
  uploadedBy: string;
  uploadedAt: string;
  status: 'Approved' | 'Pending' | 'Rejected';
  rejectionNote?: string;
}

const ReceiptsTab = React.memo(function ReceiptsTab({ app, isEnhancedReferrer, currentUserProfile }: { app: Application; isEnhancedReferrer?: boolean; currentUserProfile?: any }) {
  const { activeWorkspace } = useWorkspaces();
  const canFin = (action: string, level: 'view' | 'edit' = 'edit') =>
    isEnhancedReferrer ? true : hasApplicantAction(currentUserProfile, action, level);
  const canCreateInvoice = canFin('applicant.invoice', 'edit');
  const canRecordPayment = canFin('applicant.payments', 'edit');
  const canApplyAllocation = canFin('applicant.paymentAllocation', 'edit');
  const branding = activeWorkspace?.branding;
  const [receipts, setReceipts] = useState<ReceiptRecord[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [allocations, setAllocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Sub-tabs & Modals
  const [financeSubTab, setFinanceSubTab] = useState<'invoices' | 'quotes' | 'payments' | 'allocations'>('invoices');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [selectedQuoteForPreview, setSelectedQuoteForPreview] = useState<QuoteRecord | null>(null);
  const [isQuotePreviewOpen, setIsQuotePreviewOpen] = useState(false);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('ALL');
  const [paymentSearchQuery, setPaymentSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadAmount, setUploadAmount] = useState('');
  const [uploadCurrency, setUploadCurrency] = useState('USD');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Program Fee');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptRecord | null>(null);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Standalone Payment Record Modal
  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
  const [newPayAmount, setNewPayAmount] = useState('');
  const [newPayMethod, setNewPayMethod] = useState('Credit Card / Stripe');
  const [newPayRef, setNewPayRef] = useState('');
  const [newPayNotes, setNewPayNotes] = useState('');
  const [newPayFile, setNewPayFile] = useState<File | null>(null);
  const [emailReceiptToApplicant, setEmailReceiptToApplicant] = useState(true);
  const [newPaySelectedInvoiceId, setNewPaySelectedInvoiceId] = useState('');
  const [newPayAllocAmount, setNewPayAllocAmount] = useState('');
  const [isSavingPay, setIsSavingPay] = useState(false);

  // Payment Detail Modal
  const [selectedPaymentForDetail, setSelectedPaymentForDetail] = useState<any | null>(null);

  // Payment Allocation Modal
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [targetInvoiceForAlloc, setTargetInvoiceForAlloc] = useState<any | null>(null);
  const [targetPaymentForAlloc, setTargetPaymentForAlloc] = useState<any | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [allocAmount, setAllocAmount] = useState('');
  const [isSubmittingAlloc, setIsSubmittingAlloc] = useState(false);

  // ─── Invoices (per-applicant) ──────────────────────────────
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceCurrency, setInvoiceCurrency] = useState('USD');
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState<number | null>(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [pendingInvoiceUpload, setPendingInvoiceUpload] = useState<{ file: File; localUrl: string } | null>(null);
  const invoiceFileRef = useRef<HTMLInputElement>(null);
  const [invoicePreview, setInvoicePreview] = useState<{ open: boolean; storagePath: string; fileName: string }>({ open: false, storagePath: '', fileName: '' });
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState<InvoiceRecord | null>(null);

  // Full Invoice Builder State
  const [isInvoiceBuilderOpen, setIsInvoiceBuilderOpen] = useState(false);
  const [isQuoteBuilderOpen, setIsQuoteBuilderOpen] = useState(false);
  const [products, setProducts] = useState<FinanceProduct[]>([]);

  const safeNum = (v: any) => { const n = Number(v); return isNaN(n) ? 0 : n; };
  const fmtMoney = (v: any) => safeNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(collection(db, 'financeProducts'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        const list: FinanceProduct[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as FinanceProduct));
        setProducts(list);
      } catch (err) {
        console.warn('Could not fetch finance products', err);
      }
    };
    fetchProducts();
  }, []);

  const handleSaveInvoiceRecord = async (invoiceData: Partial<InvoiceRecord>) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(invoiceData),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to save invoice.');
      }

      await fetchInvoices();
    } catch (err: any) {
      console.error(err);
      throw err;
    }
  };

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/invoices?applicantId=${app.applicationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
        return;
      }
    } catch (e) {
      console.warn("Server fetch invoices error, falling back to Firestore SDK:", e);
    }
    try {
      const q = query(collection(db, 'invoices'), where('applicantId', '==', app.applicationId), orderBy('uploadedAt', 'desc'));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ invoiceId: d.id, ...d.data() }));
      setInvoices(list);
    } catch (err) {
      console.warn("Could not fetch invoices:", err);
    } finally {
      setInvoicesLoading(false);
    }
  }, [app.applicationId]);

  const fetchPayments = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments?applicantId=${app.applicationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
      }
    } catch (e) {
      console.warn("Could not fetch payments:", e);
    }
  }, [app.applicationId]);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      if (isEnhancedReferrer) {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/referrer/applications/${app.applicationId}/receipts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setReceipts(data.receipts || []);
        } else {
          setReceipts([]);
        }
      } else {
        const q = query(collection(db, 'receipts'), where('applicantId', '==', app.applicationId), orderBy('uploadedAt', 'desc'));
        const snap = await getDocs(q);
        const list: ReceiptRecord[] = snap.docs.map(d => ({ receiptId: d.id, ...d.data() } as ReceiptRecord));
        setReceipts(list);
      }
    } catch (err) {
      console.warn("Could not fetch receipts:", err);
    } finally {
      setLoading(false);
    }
  }, [app.applicationId, isEnhancedReferrer]);

  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/quotes?applicantId=${app.applicationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data.quotes || []);
      }
    } catch (e) {
      console.warn("Could not fetch quotes:", e);
    } finally {
      setQuotesLoading(false);
    }
  }, [app.applicationId]);

  const fetchAllocations = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/allocations?applicantId=${app.applicationId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setAllocations(data.allocations || []);
      }
    } catch (e) {
      console.warn("Could not fetch allocations:", e);
    }
  }, [app.applicationId]);

  const loadAllFinance = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchInvoices(), fetchQuotes(), fetchReceipts(), fetchPayments(), fetchAllocations()]);
    setLoading(false);
  }, [fetchInvoices, fetchQuotes, fetchReceipts, fetchPayments, fetchAllocations]);

  useEffect(() => { loadAllFinance(); }, [loadAllFinance]);

  const handleRecordStandalonePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPayAmount || parseFloat(newPayAmount) <= 0) return;
    setIsSavingPay(true);
    try {
      let receiptFilePayload = null;
      if (newPayFile) {
        const reader = new FileReader();
        const fileBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(newPayFile);
        });
        receiptFilePayload = {
          fileName: newPayFile.name,
          fileBase64,
          mimeType: newPayFile.type,
        };
      }

      const allocationsPayload: any[] = [];
      if (newPaySelectedInvoiceId) {
        const allocVal = parseFloat(newPayAllocAmount);
        if (!isNaN(allocVal) && allocVal > 0) {
          allocationsPayload.push({
            invoiceId: newPaySelectedInvoiceId,
            amount: allocVal,
          });
        }
      }

      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          applicantId: app.applicationId,
          applicantUserId: app.userId || '',
          applicantName: getSafeApplicantName(app),
          applicantEmail: app.userEmail,
          programName: app.programType || '',
          amount: parseFloat(newPayAmount),
          currency: 'USD',
          paymentMethod: newPayMethod,
          paymentReference: newPayRef,
          notes: newPayNotes,
          receiptFile: receiptFilePayload,
          allocations: allocationsPayload,
          emailReceipt: emailReceiptToApplicant,
        }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to record payment');
      }

      setIsRecordPaymentOpen(false);
      setNewPayAmount('');
      setNewPayRef('');
      setNewPayNotes('');
      setNewPayFile(null);
      setNewPaySelectedInvoiceId('');
      setNewPayAllocAmount('');
      await loadAllFinance();
    } catch (err: any) {
      notifyToast('error', err.message || 'Error recording payment');
    } finally {
      setIsSavingPay(false);
    }
  };

  const handleApplyAllocation = async (e: React.FormEvent) => {
    e.preventDefault();
    const pId = selectedPaymentId || targetPaymentForAlloc?.paymentId || targetPaymentForAlloc?.id;
    const iId = selectedInvoiceId || targetInvoiceForAlloc?.invoiceId || targetInvoiceForAlloc?.id;
    const amountVal = parseFloat(allocAmount);

    if (!pId || !iId || isNaN(amountVal) || amountVal <= 0) return;
    setIsSubmittingAlloc(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/finance/allocations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          paymentId: pId,
          invoiceId: iId,
          amount: amountVal,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to allocate payment');

      setIsApplyModalOpen(false);
      setTargetInvoiceForAlloc(null);
      setTargetPaymentForAlloc(null);
      setAllocAmount('');
      await loadAllFinance();
    } catch (err: any) {
      notifyToast('error', err.message || 'Allocation failed');
    } finally {
      setIsSubmittingAlloc(false);
    }
  };

  const handleVoidInvoice = async (inv: any) => {
    if (!(await confirmDialog(`Are you sure you want to void Invoice #${inv.invoiceNumber || inv.id}? All payment allocations must be disassociated first.`))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/invoices/${inv.id || inv.invoiceId}/void`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to void invoice');
      }
      await loadAllFinance();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to void invoice');
    }
  };

  const handleDeleteInvoice = async (inv: any) => {
    if (!(await confirmDialog(`Permanently delete Invoice #${inv.invoiceNumber || inv.id}? All payment allocations must be disassociated first.`))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/invoices/${inv.id || inv.invoiceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete invoice');
      }
      await loadAllFinance();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to delete invoice');
    }
  };

  const handleReverseAllocation = async (allocId: string) => {
    if (!(await confirmDialog('Remove Payment Allocation?\n\nThis will remove the payment allocation from this invoice. The payment record itself will not be deleted.'))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/allocations/${allocId}/disassociate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Disassociation failed');
      }
      notifyToast('success', 'Payment allocation disassociated. Invoice balance recalculated.');
      await loadAllFinance();
      if (selectedInvoiceForPreview) {
        const updatedInv = invoices.find(i => (i.id || i.invoiceId) === selectedInvoiceForPreview.id);
        if (updatedInv) setSelectedInvoiceForPreview(updatedInv);
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to disassociate allocation');
    }
  };

  const handleApprovePayment = async (payId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments/${payId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to approve payment');
      notifyToast('success', 'Payment approved successfully! Balance has been updated.');
      await loadAllFinance();
      setSelectedPaymentForDetail(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to approve payment');
    }
  };

  const handleRejectPayment = async (payId: string) => {
    const reason = await promptDialog('Please enter a clear reason for rejecting this payment submission:');
    if (!reason || !reason.trim()) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/payments/${payId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rejectionReason: reason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to reject payment');
      notifyToast('success', 'Payment submission rejected.');
      await loadAllFinance();
      setSelectedPaymentForDetail(null);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to reject payment');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceFile(null);
    setInvoiceNumber('');
    setInvoiceDate('');
    setInvoiceAmount('');
    setInvoiceCurrency('USD');
    setInvoiceDescription('');
    setInvoiceProgress(null);
    setShowAiPreview(false);
    setPendingInvoiceUpload(null);
    setShowInvoiceModal(false);
    if (invoiceFileRef.current) invoiceFileRef.current.value = '';
  };

  const handleAiAnalyzeInvoice = async () => {
    if (!pendingInvoiceUpload) return;
    setAiAnalyzing(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pendingInvoiceUpload.file);
      });
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/documents/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          prompt: 'Extract the following fields from this invoice document and return them as JSON (no markdown, no code fences): {"invoiceNumber": string, "invoiceDate": string (YYYY-MM-DD), "amount": number, "currency": string, "description": string}. If a field cannot be determined, use empty string or 0.',
          documents: [{
            name: pendingInvoiceUpload.file.name,
            text: base64,
            isBase64: true,
            mimeType: pendingInvoiceUpload.file.type,
          }],
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const extracted = data?.answer ? JSON.parse(data.answer.replace(/```json|```/g, '').trim()) : {};
        if (extracted.invoiceNumber) setInvoiceNumber(extracted.invoiceNumber);
        if (extracted.invoiceDate) setInvoiceDate(extracted.invoiceDate);
        if (extracted.amount) setInvoiceAmount(String(extracted.amount));
        if (extracted.currency) setInvoiceCurrency(extracted.currency);
        if (extracted.description) setInvoiceDescription(extracted.description);
      }
      setShowAiPreview(true);
    } catch (err) {
      console.error('AI invoice analysis failed:', err);
      setShowAiPreview(true);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleUploadInvoice = async () => {
    if (!pendingInvoiceUpload || !invoiceNumber.trim() || !invoiceAmount) return;
    if (!auth.currentUser) return;
    setInvoiceUploading(true);
    setInvoiceProgress(0);
    const fileName = pendingInvoiceUpload.file.name;
    try {
      // Server-side upload (Admin SDK storage + invoices collection) — works
      // regardless of the token claim or an applicant userId.
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Not authenticated');
      const reader = new FileReader();
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pendingInvoiceUpload.file);
      });
      const res = await fetch(`/api/admin/applications/${app.applicationId}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileName,
          fileBase64,
          invoiceNumber: invoiceNumber.trim(),
          invoiceDate: invoiceDate || new Date().toISOString(),
          amount: invoiceAmount,
          currency: invoiceCurrency,
          description: invoiceDescription.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      const invoice = data.invoice;
      setInvoices(prev => [invoice, ...prev]);
      resetInvoiceForm();

      // F-1: Auto-notify applicant about new invoice (upgraded from prompt)
      const { emailNewInvoice } = await import('../../lib/emailTriggers');
      const dueDateStr = invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
      await emailNewInvoice(app, invoice.invoiceNumber, Number(invoice.amount).toLocaleString(), invoice.currency, dueDateStr);
    } catch (err) {
      console.error("Invoice upload failed:", err);
    } finally {
      setInvoiceUploading(false);
      setInvoiceProgress(null);
    }
  };


  // Resolve the receipt preview URL whenever the preview receipt changes
  React.useEffect(() => {
    if (previewReceipt) {
      resolveFirebaseDocumentUrl(previewReceipt.storagePath, previewReceipt.downloadUrl)
        .then(url => setPreviewReceiptUrl(url))
        .catch(() => setPreviewReceiptUrl(previewReceipt.downloadUrl || previewReceipt.storagePath || ''));
    } else {
      setPreviewReceiptUrl('');
    }
  }, [previewReceipt]);

  const categories = ['Program Fee', 'Visa Fee', 'Flight', 'Accommodation', 'Insurance', 'Admin Fee', 'Other'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'JMD', 'TTD', 'BBD', 'GYD', 'XCD'];

  React.useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const filteredReceipts = receipts
    .filter(r => statusFilter === 'ALL' || r.status === statusFilter)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setUploadFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadAmount) { notifyToast('warning', 'Please select a file and enter an amount.'); return; }
    if (parseFloat(uploadAmount) <= 0) { notifyToast('warning', 'Receipt amount must be greater than 0.'); return; }

    if (isEnhancedReferrer) {
      // Enhanced referrers upload via server API (bypasses Storage/Firestore rules)
      setUploading(true);
      setUploadProgress(0);
      try {
        const token = await auth.currentUser?.getIdToken();
        const reader = new FileReader();
        const fileBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(uploadFile);
        });
        const res = await fetch(`/api/referrer/applications/${app.applicationId}/receipts/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            fileName: uploadFile.name,
            fileBase64,
            amount: uploadAmount,
            currency: uploadCurrency,
            description: uploadDescription.trim(),
            category: uploadCategory,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        await fetchReceipts();
        setShowUpload(false);
        setUploadFile(null);
        setUploadAmount('');
        setUploadDescription('');
        setUploadProgress(null);
      } catch (err) {
        console.error('Receipt upload failed:', err);
        notifyToast('error', 'Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      // Server-side upload (Admin SDK storage + receipts collection) — works even
      // when the application has no userId and regardless of the token claim.
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Not authenticated');
      const reader = new FileReader();
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });
      const res = await fetch(`/api/admin/applications/${app.applicationId}/receipts/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileName: uploadFile.name,
          fileBase64,
          amount: uploadAmount,
          currency: uploadCurrency,
          description: uploadDescription.trim(),
          category: uploadCategory,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      await fetchReceipts();
      setShowUpload(false);
      setUploadFile(null);
      setUploadAmount('');
      setUploadDescription('');
      setUploadProgress(null);
    } catch (err) {
      console.error('Receipt upload failed:', err);
      notifyToast('error', 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleStatusChange = async (receipt: ReceiptRecord, newStatus: 'Approved' | 'Rejected') => {
    try {
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await safeFetch(`/api/admin/receipts/${receipt.receiptId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newStatus }),
      });
      setReceipts(prev => prev.map(r => r.receiptId === receipt.receiptId ? { ...r, status: newStatus } : r));

      // F-3 / F-4: Auto-notify applicant of receipt decision
      if (newStatus === 'Approved') {
        const { emailReceiptApproved } = await import('../../lib/emailTriggers');
        await emailReceiptApproved(app);
      } else {
        const { emailReceiptRejected } = await import('../../lib/emailTriggers');
        await emailReceiptRejected(app);
      }
    } catch (err) {
      console.error('Failed to update receipt status:', err);
    }
  };

  const handleDelete = async (receipt: ReceiptRecord) => {
    if (!(await confirmDialog(`Delete receipt "${receipt.fileName}"?`))) return;
    try {
      await deleteDoc(doc(db, 'receipts', receipt.receiptId));
      await fetchReceipts();
    } catch (err) {
      console.error('Failed to delete receipt:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="animate-spin h-6 w-6 text-slate-400" />
      </div>
    );
  }

  const totalInvoicedVal = invoices.reduce((sum, inv) => sum + (inv.status !== 'Cancelled' ? safeNum(inv.total ?? inv.amount) : 0), 0);
  const totalPaidVal = payments.reduce((sum, p) => sum + safeNum(p.amount), 0);
  const outstandingBalVal = invoices.reduce((sum, inv) => sum + (inv.status !== 'Cancelled' ? safeNum(inv.amountDue ?? (inv.total ?? inv.amount)) : 0), 0);
  const unappliedCreditVal = payments.reduce((sum, p) => sum + safeNum(p.unappliedBalance ?? (p.amount - (p.totalAllocated || 0))), 0);

  return (<>
    <div className="space-y-6 animate-fade-in text-xs">
      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-1">
          <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">${fmtMoney(totalInvoicedVal)}</div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Total Invoiced</p>
          <p className="text-[11px] text-slate-500 font-medium">{invoices.length} total invoice(s)</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-1">
          <div className="text-2xl font-black text-emerald-700 font-mono tracking-tight">${fmtMoney(totalPaidVal)}</div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Total Payments</p>
          <p className="text-[10px] text-emerald-700 font-semibold">{payments.length} payment(s) received</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-1">
          <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">${fmtMoney(outstandingBalVal)}</div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Outstanding Balance</p>
          <p className="text-[11px] text-slate-500 font-medium">Remaining balance owed</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-1">
          <div className="text-2xl font-black text-purple-700 font-mono tracking-tight">${fmtMoney(unappliedCreditVal)}</div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Unapplied Credit</p>
          <p className="text-[10px] text-purple-700 font-medium">Available for allocation</p>
        </div>
      </div>

      {/* Header Actions & Sub-tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-4 rounded-2xl border border-slate-200">
        <div className="flex border-b sm:border-b-0 border-slate-200 gap-4 text-xs font-bold text-slate-500 flex-wrap">
          <button
            onClick={() => setFinanceSubTab('invoices')}
            className={`pb-1.5 transition-colors cursor-pointer ${financeSubTab === 'invoices' ? 'border-b-2 border-brand text-slate-900 font-extrabold' : 'hover:text-slate-800'}`}
          >
            Invoices ({invoices.length})
          </button>
          <button
            onClick={() => setFinanceSubTab('quotes')}
            className={`pb-1.5 transition-colors cursor-pointer ${financeSubTab === 'quotes' ? 'border-b-2 border-brand text-slate-900 font-extrabold' : 'hover:text-slate-800'}`}
          >
            Quotes ({quotes.length})
          </button>
          <button
            onClick={() => setFinanceSubTab('payments')}
            className={`pb-1.5 transition-colors cursor-pointer ${financeSubTab === 'payments' ? 'border-b-2 border-brand text-slate-900 font-extrabold' : 'hover:text-slate-800'}`}
          >
            Payments Ledger ({payments.length})
          </button>
          <button
            onClick={() => setFinanceSubTab('allocations')}
            className={`pb-1.5 transition-colors cursor-pointer ${financeSubTab === 'allocations' ? 'border-b-2 border-brand text-slate-900 font-extrabold' : 'hover:text-slate-800'}`}
          >
            Allocations ({allocations.length})
          </button>
        </div>

        {!isEnhancedReferrer && (
          <div className="flex items-center gap-2 flex-wrap">
            {canCreateInvoice && (
            <>
                <button
                  onClick={() => { setSelectedInvoiceForPreview(null); setIsInvoiceBuilderOpen(true); }}
                  className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  <Plus className="h-3.5 w-3.5" /><span>Create Invoice</span>
                </button>
                <button
                  onClick={() => setIsQuoteBuilderOpen(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  <Plus className="h-3.5 w-3.5" /><span>Create Quote</span>
                </button>
              </>
            )}
            {canRecordPayment && (
            <button
              onClick={() => setIsRecordPaymentOpen(true)}
              className="px-3 py-1.5 bg-brand hover:bg-brand-active text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <CreditCard className="h-3.5 w-3.5" /><span>Record Payment / Upload Receipt</span>
            </button>
            )}
            <button
              onClick={async () => {
                try {
                  const token = await auth.currentUser?.getIdToken();
                  const res = await wsFetch(`/api/finance/applications/${app.applicationId}/statement-pdf`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                  });
                  if (!res.ok) throw new Error('Failed to generate statement PDF');
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = buildApplicantPdfFileName('AccountStatement', app.applicationId, getSafeApplicantName(app));
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err: any) {
                  notifyToast('error', err.message || 'Error exporting account statement');
                }
              }}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Download className="h-3.5 w-3.5" /><span>Export Statement PDF</span>
            </button>
          </div>
        )}
      </div>

      {/* SUB-TAB 1: INVOICES TABLE */}
      {financeSubTab === 'invoices' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
          <div className="p-3 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search invoices by number, status, total, or description..."
                value={invoiceSearchQuery}
                onChange={e => setInvoiceSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs outline-none border border-slate-200 rounded-lg focus:border-brand"
              />
            </div>
            <select
              value={invoiceStatusFilter}
              onChange={e => setInvoiceStatusFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-xs font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Issued / Sent</option>
              <option value="Partially Paid">Partially Paid</option>
              <option value="Paid">Paid</option>
              <option value="Overdue">Overdue</option>
              <option value="Cancelled">Cancelled / Voided</option>
            </select>
          </div>
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                <th className="p-3">Invoice #</th>
                <th className="p-3">Dates</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Paid</th>
                <th className="p-3 text-right">Due</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {invoices.filter((inv: any) => {
                if (invoiceStatusFilter !== 'ALL' && inv.status !== invoiceStatusFilter) return false;
                if (!invoiceSearchQuery.trim()) return true;
                const q = invoiceSearchQuery.toLowerCase();
                return (
                  inv.invoiceNumber?.toLowerCase().includes(q) ||
                  inv.status?.toLowerCase().includes(q) ||
                  inv.description?.toLowerCase().includes(q) ||
                  String(inv.total ?? inv.amount ?? '').includes(q)
                );
              }).map((inv: any) => (
                <tr key={inv.id || inv.invoiceId} className="hover:bg-slate-50/80 transition-colors">
                  <td className="p-3 font-mono font-bold text-slate-900">
                    #{inv.invoiceNumber}
                  </td>
                  <td className="p-3 text-slate-500 text-[11px]">
                    <div>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '-'}</div>
                    <div className="font-semibold text-slate-700">Due: {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}</div>
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-slate-900">${fmtMoney(inv.total ?? inv.amount)}</td>
                  <td className="p-3 text-right font-mono text-emerald-600">${fmtMoney(inv.amountPaid)}</td>
                  <td className="p-3 text-right font-mono font-bold text-blue-600">${fmtMoney(inv.amountDue ?? (inv.total ?? inv.amount))}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border ${
                      inv.status === 'Paid' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                      inv.status === 'Partially Paid' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                      inv.status === 'Overdue' ? 'bg-rose-50 text-rose-800 border-rose-200' :
                      inv.status === 'Sent' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                      inv.status === 'Cancelled' ? 'bg-slate-100 text-slate-500 border-slate-200' :
                      'bg-slate-50 text-slate-700 border-slate-200'
                    }`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="p-3 text-right space-x-1 flex items-center justify-end gap-1">
                    <button
                      onClick={() => setSelectedInvoiceForPreview(inv)}
                      className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg cursor-pointer"
                      title="Preview Branded Invoice"
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    <button
                      onClick={async () => {
                        try {
                          const invId = inv.id || inv.invoiceId;
                          const blob = await downloadPdf(`/api/finance/invoices/${invId}/pdf`);
                          triggerBlobDownload(blob, buildApplicantPdfFileName(`Invoice-${inv.invoiceNumber || invId}`, inv.applicationId || inv.applicantId || app.applicationId, inv.applicantName || getSafeApplicantName(app)));
                        } catch (e: any) {
                          notifyToast('error', 'Error downloading PDF: ' + e.message);
                        }
                      }}
                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg cursor-pointer"
                      title="Download Vector PDF"
                    >
                      <Download className="h-4 w-4" />
                    </button>

                    {safeNum(inv.amountDue ?? (inv.total ?? inv.amount)) > 0 && unappliedCreditVal > 0 && !isEnhancedReferrer && canApplyAllocation && (
                      <button
                        onClick={() => {
                          setTargetInvoiceForAlloc(inv);
                          setTargetPaymentForAlloc(null);
                          setSelectedInvoiceId(inv.id || inv.invoiceId);
                          setSelectedPaymentId('');
                          const defaultAmount = Math.min(unappliedCreditVal, safeNum(inv.amountDue ?? (inv.total ?? inv.amount)));
                          setAllocAmount(defaultAmount.toString());
                          setIsApplyModalOpen(true);
                        }}
                        className="px-2 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 font-bold rounded-lg text-[10px] cursor-pointer inline-flex items-center gap-1"
                        title="Apply available payment credit to this invoice"
                      >
                        <Sparkles className="h-3 w-3" /> Apply Payment
                      </button>
                    )}

                    {!isEnhancedReferrer && canCreateInvoice && (
                      <>
                        {inv.status !== 'Cancelled' && (
                          <button
                            onClick={() => handleVoidInvoice(inv)}
                            className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-lg text-[10px] transition-all cursor-pointer"
                            title="Void Invoice (payments must be disassociated first)"
                          >
                            Void
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteInvoice(inv)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                          title="Permanently Delete Invoice"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    No invoices created for this applicant yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* SUB-TAB 2: QUOTES TABLE */}
      {financeSubTab === 'quotes' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                <th className="p-3">Quote #</th>
                <th className="p-3">Issue Date</th>
                <th className="p-3">Expires</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Total Amount</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {quotes.map((q: any) => (
                <tr key={q.id || q.quoteId} className="hover:bg-slate-50/50">
                  <td className="p-3 font-bold text-slate-900">
                    #{q.quoteNumber}
                  </td>
                  <td className="p-3 text-slate-600">{q.issueDate || 'N/A'}</td>
                  <td className="p-3 text-amber-600 font-medium">{q.expirationDate || 'N/A'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      q.status === 'Accepted' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      q.status === 'Rejected' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                      q.status === 'Cancelled' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
                      'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {q.status || 'Draft'}
                    </span>
                  </td>
                  <td className="p-3 text-right font-bold font-mono text-slate-900">
                    ${Number(q.total || 0).toFixed(2)} {q.currency || 'USD'}
                  </td>
                  <td className="p-3 text-center space-x-1">
                    <button
                      onClick={() => {
                        setSelectedQuoteForPreview(q);
                        setIsQuotePreviewOpen(true);
                      }}
                      className="p-1 text-slate-600 hover:text-blue-600 rounded-md transition-colors cursor-pointer"
                      title="View Quote Details & Preview"
                    >
                      <Eye className="h-4 w-4 inline" />
                    </button>
                    {q.status !== 'Cancelled' && (
                      <button
                        onClick={async () => {
                          if (!(await confirmDialog(`Cancel Quote #${q.quoteNumber}?`))) return;
                          try {
                            const token = await auth.currentUser?.getIdToken();
                            const res = await wsFetch(`/api/admin/finance/quotes/${q.id || q.quoteId}/cancel`, {
                              method: 'POST',
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                            });
                            if (res.ok) {
                              notifyToast.success(`Quote #${q.quoteNumber} cancelled.`);
                              fetchQuotes();
                            } else {
                              const err = await res.json();
                              notifyToast.error(err.error || 'Failed to cancel quote.');
                            }
                          } catch (e: any) {
                            notifyToast.error(e.message || 'Error cancelling quote.');
                          }
                        }}
                        className="p-1 text-slate-400 hover:text-amber-600 rounded-md transition-colors cursor-pointer"
                        title="Cancel Quote"
                      >
                        <Ban className="h-4 w-4 inline" />
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!(await confirmDialog(`Delete Quote #${q.quoteNumber}?`))) return;
                        try {
                          const token = await auth.currentUser?.getIdToken();
                          const res = await wsFetch(`/api/admin/finance/quotes/${q.id || q.quoteId}`, {
                            method: 'DELETE',
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                          });
                          if (res.ok) {
                            notifyToast.success(`Quote #${q.quoteNumber} deleted.`);
                            fetchQuotes();
                          } else {
                            const err = await res.json();
                            notifyToast.error(err.error || 'Failed to delete quote.');
                          }
                        } catch (e: any) {
                          notifyToast.error(e.message || 'Error deleting quote.');
                        }
                      }}
                      className="p-1 text-slate-400 hover:text-rose-600 rounded-md transition-colors cursor-pointer"
                      title="Delete Quote"
                    >
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
              {quotes.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400">
                    No quotes or estimates created for this applicant yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* SUB-TAB 3: PAYMENTS LEDGER TABLE */}
      {financeSubTab === 'payments' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
          <div className="p-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400 shrink-0 ml-1" />
            <input
              type="text"
              placeholder="Search payments by ID, reference, method, or notes..."
              value={paymentSearchQuery}
              onChange={e => setPaymentSearchQuery(e.target.value)}
              className="w-full text-xs outline-none bg-transparent"
            />
          </div>
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                <th className="p-3">Payment ID</th>
                <th className="p-3">Date</th>
                <th className="p-3">Method & Ref</th>
                <th className="p-3 text-right">Original Amount</th>
                <th className="p-3 text-right">Allocated</th>
                <th className="p-3 text-right">Unapplied Credit</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {payments.filter((p: any) => {
                if (!paymentSearchQuery.trim()) return true;
                const q = paymentSearchQuery.toLowerCase();
                return (
                  p.paymentId?.toLowerCase().includes(q) ||
                  p.id?.toLowerCase().includes(q) ||
                  p.paymentReference?.toLowerCase().includes(q) ||
                  p.paymentMethod?.toLowerCase().includes(q) ||
                  p.notes?.toLowerCase().includes(q) ||
                  String(p.amount ?? '').includes(q)
                );
              }).map((p: any) => {
                const unapplied = safeNum(p.unappliedBalance ?? (p.amount - (p.totalAllocated || 0)));
                return (
                  <tr key={p.paymentId || p.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-mono font-bold text-slate-900">
                      {String(p.paymentId || p.id || '').toUpperCase()}
                    </td>
                    <td className="p-3 text-slate-600">
                      {p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{p.paymentMethod || 'Credit Card'}</div>
                      <div className="text-[10px] font-mono text-slate-400">{p.paymentReference || '-'}</div>
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${fmtMoney(p.amount)} {p.currency || 'USD'}</td>
                    <td className="p-3 text-right font-mono text-emerald-600">${fmtMoney(p.totalAllocated)}</td>
                    <td className="p-3 text-right font-mono font-bold text-purple-600">${fmtMoney(unapplied)}</td>
                    <td className="p-3 text-right flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => setSelectedPaymentForDetail(p)}
                        className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[10px] transition-all cursor-pointer flex items-center gap-1"
                        title="View Payment & Receipt"
                      >
                        <Eye className="h-3 w-3" /><span>View</span>
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const recId = p.paymentId || p.id;
                            const blob = await downloadPdf(`/api/finance/receipts/${recId}/pdf`);
                            triggerBlobDownload(blob, buildApplicantPdfFileName(`Receipt-${recId}`, p.applicationId || p.applicantId || app.applicationId, p.applicantName || getSafeApplicantName(app)));
                          } catch (err: any) {
                            notifyToast('error', err.message || 'Error exporting receipt PDF');
                          }
                        }}
                        className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-bold rounded-lg text-[10px] transition-all cursor-pointer flex items-center gap-1"
                        title="Download Receipt PDF"
                      >
                        <Download className="h-3 w-3" /><span>Receipt PDF</span>
                      </button>
                      {unapplied > 0 && invoices.some(i => safeNum(i.amountDue ?? (i.total ?? i.amount)) > 0) && !isEnhancedReferrer && canApplyAllocation && (
                        <button
                          onClick={() => {
                            setTargetPaymentForAlloc(p);
                            setTargetInvoiceForAlloc(null);
                            setSelectedPaymentId(p.paymentId || p.id);
                            setSelectedInvoiceId('');
                            setAllocAmount('');
                            setIsApplyModalOpen(true);
                          }}
                          className="px-2 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 font-bold rounded-lg text-[10px] cursor-pointer inline-flex items-center gap-1"
                        >
                          <Sparkles className="h-3 w-3" /> Allocate Funds
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    No payment records logged for this candidate yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* SUB-TAB 3: ALLOCATIONS LEDGER TABLE */}
      {financeSubTab === 'allocations' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                <th className="p-3">Alloc Ref</th>
                <th className="p-3">Payment ID</th>
                <th className="p-3">Applied To Invoice</th>
                <th className="p-3 text-right">Amount Allocated</th>
                <th className="p-3">Allocated By / Date</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {allocations.map((alloc: any) => (
                <tr key={alloc.id || alloc.allocationId} className="hover:bg-slate-50/80 transition-colors">
                  <td className="p-3 font-mono font-bold text-slate-900">
                    {String(alloc.allocationId || alloc.id || '').toUpperCase()}
                  </td>
                  <td className="p-3 font-mono font-bold text-slate-700">
                    <button
                      onClick={() => {
                        const matchedPay = payments.find(p => (p.paymentId || p.id) === alloc.paymentId);
                        if (matchedPay) {
                          setSelectedPaymentForDetail(matchedPay);
                        } else {
                          setSelectedPaymentForDetail({ paymentId: alloc.paymentId, amount: alloc.amount, currency: 'USD' });
                        }
                      }}
                      className="hover:underline text-purple-700 cursor-pointer"
                      title="Click to view payment detail"
                    >
                      {String(alloc.paymentId || '').toUpperCase()}
                    </button>
                  </td>
                  <td className="p-3 font-bold text-blue-600 font-mono">
                    <button
                      onClick={() => {
                        const matchedInv = invoices.find(i => (i.id || i.invoiceId) === alloc.invoiceId);
                        if (matchedInv) {
                          setSelectedInvoiceForPreview(matchedInv);
                        } else {
                          setSelectedInvoiceForPreview({ invoiceId: alloc.invoiceId, invoiceNumber: alloc.invoiceNumber || alloc.invoiceId } as any);
                        }
                      }}
                      className="hover:underline cursor-pointer"
                      title="Click to view invoice preview"
                    >
                      #{alloc.invoiceNumber || alloc.invoiceId}
                    </button>
                  </td>
                  <td className="p-3 text-right font-mono font-extrabold text-emerald-600">
                    ${fmtMoney(alloc.amount)}
                  </td>
                  <td className="p-3 text-slate-500 text-[11px]">
                    <div>{alloc.allocatedBy || 'Admin'}</div>
                    <div className="text-[11px] text-slate-500">{alloc.allocatedAt ? new Date(alloc.allocatedAt).toLocaleDateString() : '-'}</div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${alloc.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                      {alloc.status || 'Active'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {alloc.status === 'Active' && !isEnhancedReferrer && (
                      <button
                        onClick={() => handleReverseAllocation(alloc.id || alloc.allocationId)}
                        className="px-2 py-1 bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold rounded-lg text-[10px] cursor-pointer"
                        title="Reverse allocation and restore funds to payment"
                      >
                        Reverse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {allocations.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    No payment allocations created yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* PAYMENT DETAIL MODAL */}
    {selectedPaymentForDetail && (
      <Modal onClose={() => setSelectedPaymentForDetail(null)}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 text-xs animate-fade-in">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <div>
              <h3 className="font-extrabold text-slate-900 text-sm font-display">Payment Details & Receipt</h3>
              <p className="text-[10px] font-mono text-slate-400 mt-0.5">#{String(selectedPaymentForDetail.paymentId || selectedPaymentForDetail.id || '').toUpperCase()}</p>
            </div>
            <button aria-label="Close" title="Close" type="button" onClick={() => setSelectedPaymentForDetail(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase block">Amount Paid</span>
                <span className="text-base font-black font-mono text-slate-900">${fmtMoney(selectedPaymentForDetail.amount)} {selectedPaymentForDetail.currency || 'USD'}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase block">Unapplied Credit</span>
                <span className="text-base font-black font-mono text-purple-700">${fmtMoney(selectedPaymentForDetail.unappliedBalance ?? (selectedPaymentForDetail.amount - (selectedPaymentForDetail.totalAllocated || 0)))}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div><span className="font-bold text-slate-400">Payment Date:</span> {selectedPaymentForDetail.paymentDate ? new Date(selectedPaymentForDetail.paymentDate).toLocaleDateString() : '-'}</div>
              <div><span className="font-bold text-slate-400">Payment Method:</span> {selectedPaymentForDetail.paymentMethod || 'Credit Card'}</div>
              <div><span className="font-bold text-slate-400">Reference:</span> {selectedPaymentForDetail.paymentReference || '-'}</div>
              <div><span className="font-bold text-slate-400">Recorded By:</span> {selectedPaymentForDetail.recordedBy || 'admin'}</div>
            </div>

            {selectedPaymentForDetail.notes && (
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-slate-700">
                <span className="font-bold text-slate-400 block text-[10px] uppercase">Notes:</span>
                {selectedPaymentForDetail.notes}
              </div>
            )}

            {selectedPaymentForDetail.rejectionReason && (
              <div className="bg-rose-50 p-2.5 rounded-lg border border-rose-200 text-rose-800">
                <span className="font-bold block text-[10px] uppercase">Rejection Reason:</span>
                {selectedPaymentForDetail.rejectionReason}
              </div>
            )}

            {/* Approval / Rejection Action Banner for Pending Bank Deposit Payments */}
            {(selectedPaymentForDetail.status === 'Pending Approval' || selectedPaymentForDetail.status === 'Pending') && !isEnhancedReferrer && (
              <div className="bg-amber-50 p-3.5 rounded-xl border border-amber-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-900 tracking-wider">Pending Payment Review</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-200 text-amber-900">
                    Action Required
                  </span>
                </div>
                <p className="text-[11px] text-amber-800">
                  This payment proof has been submitted by the candidate. Approving will confirm funds and reduce invoice balance due.
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handleApprovePayment(selectedPaymentForDetail.paymentId || selectedPaymentForDetail.id)}
                    className="flex-1 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-extrabold rounded-lg text-xs transition-all cursor-pointer shadow-2xs"
                  >
                    Approve Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRejectPayment(selectedPaymentForDetail.paymentId || selectedPaymentForDetail.id)}
                    className="flex-1 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-extrabold rounded-lg text-xs transition-all cursor-pointer shadow-2xs"
                  >
                    Reject Payment
                  </button>
                </div>
              </div>
            )}

            {/* Proof Attachment */}
            {(selectedPaymentForDetail.downloadUrl || selectedPaymentForDetail.storagePath) && (
              <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase text-blue-800 block">Proof of Payment Attached</span>
                  <span className="text-xs text-slate-600 truncate block max-w-[200px]">{selectedPaymentForDetail.fileName || 'Attachment'}</span>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const p = selectedPaymentForDetail.storagePath
                        ? `/api/serve-inline?path=${encodeURIComponent(selectedPaymentForDetail.storagePath)}`
                        : selectedPaymentForDetail.downloadUrl;
                      const res = await fetch(p, {
                        headers: token && selectedPaymentForDetail.storagePath ? { Authorization: `Bearer ${token}` } : {}
                      });
                      if (!res.ok) throw new Error('Failed to fetch file');
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, '_blank');
                    } catch (e: any) {
                      notifyToast('error', e.message || 'Error opening file');
                    }
                  }}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition-all flex items-center gap-1 cursor-pointer"
                >
                  <Eye className="h-3.5 w-3.5" /><span>View File</span>
                </button>
              </div>
            )}
          </div>

          <div className="pt-3 flex justify-between items-center border-t border-slate-100">
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    const recId = selectedPaymentForDetail.paymentId || selectedPaymentForDetail.id;
                    const blob = await downloadPdf(`/api/finance/receipts/${recId}/pdf`);
                    triggerBlobDownload(blob, buildApplicantPdfFileName(`Receipt-${recId}`, selectedPaymentForDetail.applicationId || selectedPaymentForDetail.applicantId || app.applicationId, selectedPaymentForDetail.applicantName || getSafeApplicantName(app)));
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Error exporting receipt PDF');
                  }
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs text-xs"
              >
                <Download className="h-3.5 w-3.5" /><span>Export PDF</span>
              </button>

              <button
                onClick={async () => {
                  try {
                    const token = await auth.currentUser?.getIdToken();
                    const res = await wsFetch(`/api/admin/finance/receipts/${selectedPaymentForDetail.paymentId || selectedPaymentForDetail.id}/email`, {
                      method: 'POST',
                      headers: token ? { Authorization: `Bearer ${token}` } : {}
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.error || 'Failed to send receipt email');
                    notifyToast('success', 'Payment receipt email with attached PDF sent successfully!');
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Error sending receipt email');
                  }
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs text-xs"
              >
                <Mail className="h-3.5 w-3.5" /><span>Email Receipt</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setSelectedPaymentForDetail(null)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all cursor-pointer text-xs"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    )}

    {/* Invoice Builder Modal */}
    <InvoiceBuilderModal
      isOpen={isInvoiceBuilderOpen}
      onClose={() => setIsInvoiceBuilderOpen(false)}
      applicant={app}
      products={products}
      branding={branding}
      onSave={handleSaveInvoiceRecord}
    />

    {/* Invoice Preview Modal */}
    <InvoicePreviewModal
      isOpen={!!selectedInvoiceForPreview}
      onClose={() => setSelectedInvoiceForPreview(null)}
      invoice={selectedInvoiceForPreview}
      branding={branding}
      isAdminView={!isEnhancedReferrer}
      onRecordPayment={(inv) => {
        setSelectedInvoiceForPreview(null);
        setTargetInvoiceForAlloc(inv);
        setSelectedInvoiceId(inv.id || inv.invoiceId);
        setNewPaySelectedInvoiceId(inv.id || inv.invoiceId);
        const due = safeNum(inv.amountDue ?? (inv.total ?? inv.amount));
        setNewPayAllocAmount(String(due));
        setIsRecordPaymentOpen(true);
      }}
    />

    {/* RECORD STANDALONE PAYMENT MODAL */}
    {isRecordPaymentOpen && (
      <Modal onClose={() => setIsRecordPaymentOpen(false)}>
        <form onSubmit={handleRecordStandalonePayment} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 text-xs animate-fade-in">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h3 className="font-extrabold text-slate-900 text-sm font-display">Record Payment / Upload Receipt</h3>
            <button aria-label="Close" title="Close" type="button" onClick={() => setIsRecordPaymentOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment Amount ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newPayAmount}
                  onChange={e => {
                    const val = e.target.value;
                    setNewPayAmount(val);
                    if (newPaySelectedInvoiceId && !newPayAllocAmount) {
                      const inv = invoices.find(i => (i.id || i.invoiceId) === newPaySelectedInvoiceId);
                      const due = safeNum(inv?.amountDue ?? (inv?.total ?? inv?.amount));
                      const payVal = parseFloat(val) || 0;
                      setNewPayAllocAmount(String(Math.min(payVal, due)));
                    }
                  }}
                  placeholder="100.00"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none focus:bg-white"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Payment Method</label>
                <select
                  value={newPayMethod}
                  onChange={e => setNewPayMethod(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none"
                >
                  <option value="Credit Card / Stripe">Credit Card / Stripe</option>
                  <option value="Bank Wire / ACH">Bank Wire / ACH</option>
                  <option value="Cash / Manual">Cash / Manual</option>
                  <option value="Scholarship / Grant">Scholarship / Grant</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Transaction Ref / Cheque #</label>
              <input
                type="text"
                placeholder="e.g. TXN-998231"
                value={newPayRef}
                onChange={e => setNewPayRef(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800 outline-none"
              />
            </div>

            {/* Proof of Payment File Upload */}
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 block">Proof of Payment / Receipt File (Optional)</label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={e => setNewPayFile(e.target.files?.[0] || null)}
                className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300 cursor-pointer"
              />
            </div>

            {/* Inline Invoice Allocation */}
            <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 space-y-2">
              <label className="text-[10px] font-black uppercase text-blue-800 block">Apply Directly to Invoice (Optional)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-slate-500 font-semibold block mb-0.5">Select Outstanding Invoice</label>
                  <select
                    value={newPaySelectedInvoiceId}
                    onChange={e => {
                      const invId = e.target.value;
                      setNewPaySelectedInvoiceId(invId);
                      if (invId) {
                        const inv = invoices.find(i => (i.id || i.invoiceId) === invId);
                        const due = safeNum(inv?.amountDue ?? (inv?.total ?? inv?.amount));
                        const payVal = parseFloat(newPayAmount) || 0;
                        setNewPayAllocAmount(String(Math.min(payVal, due)));
                      } else {
                        setNewPayAllocAmount('');
                      }
                    }}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-semibold text-slate-800 outline-none"
                  >
                    <option value="">-- Leave as Unapplied Credit --</option>
                    {invoices.filter(i => safeNum(i.amountDue ?? (i.total ?? i.amount)) > 0).map(i => (
                      <option key={i.id || i.invoiceId} value={i.id || i.invoiceId}>
                        #{i.invoiceNumber} (Due: ${safeNum(i.amountDue ?? (i.total ?? i.amount)).toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>

                {newPaySelectedInvoiceId && (
                  <div>
                    <label className="text-[11px] text-slate-500 font-semibold block mb-0.5">Allocation Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newPayAllocAmount}
                      onChange={e => setNewPayAllocAmount(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono font-bold text-slate-900 outline-none"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={emailReceiptToApplicant}
                  onChange={e => setEmailReceiptToApplicant(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                />
                <span>Email receipt to applicant ({app.userEmail || 'applicant'})</span>
              </label>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Notes</label>
              <textarea
                rows={2}
                placeholder="Optional payment notes..."
                value={newPayNotes}
                onChange={e => setNewPayNotes(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-medium text-slate-800 outline-none resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsRecordPaymentOpen(false)}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 font-bold rounded-lg cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSavingPay || !newPayAmount}
              className="px-4 py-1.5 bg-brand hover:bg-brand-active text-white font-bold rounded-lg cursor-pointer disabled:opacity-50"
            >
              {isSavingPay ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </Modal>
    )}

    {/* PAYMENT ALLOCATION MODAL */}
    {isApplyModalOpen && (
      <Modal onClose={() => setIsApplyModalOpen(false)}>
        <form onSubmit={handleApplyAllocation} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 text-xs animate-fade-in">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h3 className="font-extrabold text-slate-900 text-sm font-display">Apply Payment Allocation</h3>
            </div>
            <button aria-label="Close" title="Close" type="button" onClick={() => setIsApplyModalOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            {/* Target Invoice */}
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Target Invoice *</label>
              {targetInvoiceForAlloc ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-0.5">
                  <div className="font-bold text-slate-900">Invoice #{targetInvoiceForAlloc.invoiceNumber}</div>
                  <div className="text-[11px] text-slate-500">
                    Total: ${safeNum(targetInvoiceForAlloc.total ?? targetInvoiceForAlloc.amount).toFixed(2)} &bull; Balance Due: <span className="font-bold text-blue-600">${safeNum(targetInvoiceForAlloc.amountDue ?? (targetInvoiceForAlloc.total ?? targetInvoiceForAlloc.amount)).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedInvoiceId}
                  onChange={e => {
                    setSelectedInvoiceId(e.target.value);
                    const inv = invoices.find(i => (i.id || i.invoiceId) === e.target.value);
                    if (inv && targetPaymentForAlloc) {
                      const unapplied = safeNum(targetPaymentForAlloc.unappliedBalance ?? (targetPaymentForAlloc.amount - (targetPaymentForAlloc.totalAllocated || 0)));
                      const due = safeNum(inv.amountDue ?? (inv.total ?? inv.amount));
                      setAllocAmount(Math.min(unapplied, due).toString());
                    }
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none"
                >
                  <option value="">-- Select Outstanding Invoice --</option>
                  {invoices.filter(i => safeNum(i.amountDue ?? (i.total ?? i.amount)) > 0).map(i => (
                    <option key={i.id || i.invoiceId} value={i.id || i.invoiceId}>
                      #{i.invoiceNumber} - Balance Due: ${safeNum(i.amountDue ?? (i.total ?? i.amount)).toFixed(2)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Source Payment */}
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Source Payment with Unapplied Credit *</label>
              {targetPaymentForAlloc ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-0.5">
                  <div className="font-bold text-slate-900">Payment #{targetPaymentForAlloc.paymentId || targetPaymentForAlloc.id}</div>
                  <div className="text-[11px] text-slate-500">
                    Total Amount: ${safeNum(targetPaymentForAlloc.amount).toFixed(2)} &bull; Available Credit: <span className="font-bold text-purple-600">${safeNum(targetPaymentForAlloc.unappliedBalance ?? (targetPaymentForAlloc.amount - (targetPaymentForAlloc.totalAllocated || 0))).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedPaymentId}
                  onChange={e => {
                    setSelectedPaymentId(e.target.value);
                    const pay = payments.find(p => (p.paymentId || p.id) === e.target.value);
                    if (pay && targetInvoiceForAlloc) {
                      const unapplied = safeNum(pay.unappliedBalance ?? (pay.amount - (pay.totalAllocated || 0)));
                      const due = safeNum(targetInvoiceForAlloc.amountDue ?? (targetInvoiceForAlloc.total ?? targetInvoiceForAlloc.amount));
                      setAllocAmount(Math.min(unapplied, due).toString());
                    }
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold text-slate-900 outline-none"
                >
                  <option value="">-- Select Payment with Unapplied Balance --</option>
                  {payments.filter(p => safeNum(p.unappliedBalance ?? (p.amount - (p.totalAllocated || 0))) > 0).map(p => (
                    <option key={p.paymentId || p.id} value={p.paymentId || p.id}>
                      Payment #{p.paymentId || p.id} - Credit Available: ${safeNum(p.unappliedBalance ?? (p.amount - (p.totalAllocated || 0))).toFixed(2)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Allocation Amount */}
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Amount to Allocate ($) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={allocAmount}
                onChange={e => setAllocAmount(e.target.value)}
                placeholder="50.00"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-900 outline-none focus:bg-white"
              />
              <span className="text-[11px] text-slate-500 mt-0.5 block">Must not exceed available payment credit or invoice balance due.</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsApplyModalOpen(false)}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 font-bold rounded-lg cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmittingAlloc || !allocAmount}
              className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg cursor-pointer disabled:opacity-50"
            >
              {isSubmittingAlloc ? 'Allocating...' : 'Confirm Allocation'}
            </button>
          </div>
        </form>
      </Modal>
    )}
      {showUpload && (
        <Modal onClose={() => setShowUpload(false)} ariaLabel="Upload Receipt">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Upload Receipt</h3>
                <p className="text-caption text-slate-400 mt-0.5">Add a receipt for this applicant</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowUpload(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Receipt File *</p>
                <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileChange} className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-caption file:font-bold file:bg-blue-50 file:text-brand hover:file:bg-blue-100" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Amount *</p>
                  <input type="number" min="0.01" step="0.01" value={uploadAmount} onChange={e => setUploadAmount(e.target.value)} placeholder="0.00" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
                  {uploadAmount && parseFloat(uploadAmount) <= 0 && <p className="text-caption text-rose-500 mt-1">Amount must be greater than 0</p>}
                </div>
                <div>
                  <p className="text-caption font-semibold text-slate-500 mb-1">Currency</p>
                  <select value={uploadCurrency} onChange={e => setUploadCurrency(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                    {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Category</p>
                <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <p className="text-caption font-semibold text-slate-500 mb-1">Description</p>
                <input value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} placeholder="What is this payment for?" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
              </div>
              {uploadProgress !== null && (
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full btn-primary rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => setShowUpload(false)} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
              <button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadAmount || parseFloat(uploadAmount) <= 0} className="px-4 py-1.5 btn-primary  disabled:bg-blue-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1">
                {uploading && <Loader className="h-3.5 w-3.5 animate-spin" />}
                <span>Upload</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Preview Modal */}
      {previewReceipt && (
        <Modal onClose={() => setPreviewReceipt(null)} ariaLabel="Receipt Preview">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Receipt Preview</h3>
                <p className="text-caption text-slate-400 mt-0.5">{previewReceipt.fileName}</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setPreviewReceipt(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="bg-slate-100 rounded-xl p-8 flex items-center justify-center">
                {isImagePreview(previewReceiptUrl, previewReceipt.fileName) ? (
                  <img src={previewReceiptUrl} alt="Receipt" className="max-h-64 rounded-lg object-contain" />
                ) : isPdfPreview(previewReceiptUrl, previewReceipt.fileName) ? (
                  <iframe src={previewReceiptUrl} className="w-full h-[40vh] rounded-xl border-0 bg-white" title="Receipt" />
                ) : (
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-slate-400 mx-auto mb-2" />
                    <p className="text-caption font-semibold text-slate-500">{previewReceipt.fileName}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-caption font-semibold text-slate-400">Amount</span><p className="font-bold">{previewReceipt.currency} {previewReceipt.amount.toFixed(2)}</p></div>
                <div><span className="text-caption font-semibold text-slate-400">Status</span><p className="font-bold">{previewReceipt.status}</p></div>
                <div><span className="text-caption font-semibold text-slate-400">Category</span><p className="font-bold">{previewReceipt.category}</p></div>
                <div><span className="text-caption font-semibold text-slate-400">Uploaded</span><p className="font-bold">{formatFullDateTime(previewReceipt.uploadedAt)}</p></div>
              </div>
              {previewReceipt.description && <p className="text-caption text-slate-500">{previewReceipt.description}</p>}
              {previewReceipt.status === 'Pending' && !isEnhancedReferrer && (
                <div className="flex justify-end space-x-2 pt-1">
                  <button onClick={() => { handleStatusChange(previewReceipt, 'Approved'); setPreviewReceipt(null); }} className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-caption font-bold rounded-xl transition-all cursor-pointer">Approve</button>
                  <button onClick={() => { handleStatusChange(previewReceipt, 'Rejected'); setPreviewReceipt(null); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-caption font-bold rounded-xl transition-all cursor-pointer">Reject</button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}


    {invoicePreview.open && (
      <DocPreviewModal
        open={invoicePreview.open}
        onClose={() => setInvoicePreview({ open: false, storagePath: '', fileName: '' })}
        storagePath={invoicePreview.storagePath}
        fileName={invoicePreview.fileName}
      />
    )}

    {/* Invoice Interactive Preview Modal */}
    <InvoicePreviewModal
      isOpen={!!selectedInvoiceForPreview}
      onClose={() => setSelectedInvoiceForPreview(null)}
      invoice={selectedInvoiceForPreview}
      branding={branding}
      isAdminView={true}
    />

    {/* Invoice Builder Modal */}
    <InvoiceBuilderModal
      isOpen={isInvoiceBuilderOpen}
      onClose={() => setIsInvoiceBuilderOpen(false)}
      onSave={handleSaveInvoiceRecord}
      existingInvoice={null}
      applications={[app]}
      products={products}
      preselectedApplicationId={app.applicationId}
    />

    <QuoteBuilderModal
      isOpen={isQuoteBuilderOpen}
      onClose={() => setIsQuoteBuilderOpen(false)}
      applications={[app]}
      products={products}
      preselectedApplicationId={app.applicationId}
      onQuoteSaved={() => {
        setIsQuoteBuilderOpen(false);
        fetchQuotes();
        notifyToast.success("Quote created successfully");
      }}
    />

    {selectedQuoteForPreview && (
      <QuotePreviewModal
        isOpen={isQuotePreviewOpen}
        onClose={() => {
          setIsQuotePreviewOpen(false);
          setSelectedQuoteForPreview(null);
        }}
        quote={selectedQuoteForPreview}
        onConverted={() => {
          fetchQuotes();
          fetchInvoices();
        }}
      />
    )}
  </>);
});

/* ───────────────────── SHARED COMPONENTS ───────────────────── */

function SectionCard({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{title}</h3>
        {action && <div className="flex items-center space-x-2">{action}</div>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-[13px] font-medium text-slate-800">{value ?? '—'}</p>
    </div>
  );
}

function EditInline({ value, onChange, placeholder = '' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-white"
    />
  );
}

function SelectInline({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-white"
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

/* ───────────────────── PROFILE TAB ───────────────────── */

const ProfileTab = React.memo(function ProfileTab({ app, appCtx, maskValue, canViewContact, canEditProfile = true }: { app: Application; appCtx: any; maskValue?: any; canViewContact?: boolean; canEditProfile?: boolean }) {
  // Local edit state for personal details
  const [localPersonal, setLocalPersonal] = useState<any>(null);
  const [isEditingLocalPersonal, setIsEditingLocalPersonal] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const currentTags = (app as any).tags || [];

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag || currentTags.includes(tag)) { setTagInput(''); return; }
    appCtx.handleUpdateApplicantTags?.(app.applicationId, [...currentTags, tag]);
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    appCtx.handleUpdateApplicantTags?.(app.applicationId, currentTags.filter((t: string) => t !== tag));
  };

  const startEditPersonal = () => {
    setLocalPersonal({
      firstName: app.personalDetails?.firstName || '',
      lastName: app.personalDetails?.lastName || '',
      middleName: app.personalDetails?.middleName || '',
      fullNameLegal: app.personalDetails?.fullNameLegal || '',
      dob: app.personalDetails?.dob || '',
      gender: app.personalDetails?.gender || '',
      maritalStatus: app.personalDetails?.maritalStatus || '',
      phone: app.personalDetails?.phone || '',
      countryCode: app.personalDetails?.countryCode || '',
      mobileNumber: app.personalDetails?.mobileNumber || '',
      bestTimeToCall: app.personalDetails?.bestTimeToCall || '',
      countryOfCitizenship: app.personalDetails?.countryOfCitizenship || '',
      countryOfBirth: app.personalDetails?.countryOfBirth || '',
      cityOfBirth: app.personalDetails?.cityOfBirth || '',
      legalPermanentResidenceCountry: app.personalDetails?.legalPermanentResidenceCountry || '',
      ssnOrTaxId: app.personalDetails?.ssnOrTaxId || '',
      address: app.personalDetails?.address || { street: '', apartment: '', city: '', state: '', zip: '', country: '' },
      previousTravelHistory: app.personalDetails?.previousTravelHistory || '',
      previousVisaHistory: app.personalDetails?.previousVisaHistory || '',
      firstTimeTraveling: app.personalDetails?.firstTimeTraveling ?? undefined,
      immediateFamilyOverseas: app.personalDetails?.immediateFamilyOverseas || [],
      objective: app.personalDetails?.objective || '',
      primaryLanguage: app.personalDetails?.primaryLanguage || '',
      otherLanguages: app.personalDetails?.otherLanguages || '',
      computerSkills: app.personalDetails?.computerSkills || '',
      personalSkills: app.personalDetails?.personalSkills || '',
      countriesResided: app.personalDetails?.countriesResided || '',
      currentCountryOfResidence: app.personalDetails?.currentCountryOfResidence || '',
      leisureActivities: app.personalDetails?.leisureActivities || '',
      hospitalityExperience: app.personalDetails?.hospitalityExperience || '',
      fullTimeStudent: app.personalDetails?.fullTimeStudent ?? undefined,
      j1ProgramParticipation: app.personalDetails?.j1ProgramParticipation || [],
      criminalConviction: app.personalDetails?.criminalConviction ?? undefined,
      criminalConvictionExplanation: app.personalDetails?.criminalConvictionExplanation || '',
      insurancePreexistingAcknowledged: app.personalDetails?.insurancePreexistingAcknowledged ?? undefined,
      insuranceAdditionalPurchased: app.personalDetails?.insuranceAdditionalPurchased ?? undefined,
    });
    setIsEditingLocalPersonal(true);
  };

  // Resolve program name from the programs list
  const resolvedProgramName = useMemo(() => {
    const p = (appCtx.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId);
    return p?.name || app.programType || app.programId || '—';
  }, [appCtx.programsList, app.programId, app.programType]);

  const resolvedTerm = app.programTerm || app.assignment?.programTerm || app.programPreferences?.programTerm || '—';

  return (
    <div className="space-y-6">
      {/* Program & Term — visible at a glance */}
      <SectionCard title="Program">
        <div className="space-y-2">
          <InfoItem label="Program" value={resolvedProgramName} />
          <InfoItem label="Term / Year" value={resolvedTerm} />
        </div>
      </SectionCard>

      {/* Tags */}
      <SectionCard title="Tags">
        <div className="flex flex-wrap items-center gap-1.5">
          {currentTags.length === 0 && <span className="text-caption text-slate-400">No tags</span>}
          {currentTags.map((tag: string) => (
            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100 rounded-md">
              {tag}
              <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500 cursor-pointer" title="Remove tag">✕</button>
            </span>
          ))}
          <form onSubmit={e => { e.preventDefault(); handleAddTag(); }} className="inline-flex items-center ml-1">
            <input type="text" value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              placeholder="+ tag"
              className="w-16 text-[10px] bg-transparent border-b border-dashed border-slate-300 focus:border-purple-400 outline-none text-slate-500 placeholder-slate-300"
            />
          </form>
        </div>
      </SectionCard>
      {/* Personal Details — Grouped */}
      <SectionCard title="Personal Details" action={
        !isEditingLocalPersonal && canEditProfile ? (
          <button onClick={startEditPersonal} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {isEditingLocalPersonal && localPersonal ? (
          <div className="space-y-5">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Identity</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-caption font-bold text-slate-400 mb-1">First Name</p><input value={localPersonal.firstName} onChange={e => { const fn = e.target.value; setLocalPersonal({ ...localPersonal, firstName: fn, fullNameLegal: [fn, localPersonal.middleName, localPersonal.lastName].filter(Boolean).join(' ') }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Last Name</p><input value={localPersonal.lastName} onChange={e => { const ln = e.target.value; setLocalPersonal({ ...localPersonal, lastName: ln, fullNameLegal: [localPersonal.firstName, localPersonal.middleName, ln].filter(Boolean).join(' ') }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Middle Name</p><input value={localPersonal.middleName || ''} onChange={e => { const mn = e.target.value; setLocalPersonal({ ...localPersonal, middleName: mn, fullNameLegal: [localPersonal.firstName, mn, localPersonal.lastName].filter(Boolean).join(' ') }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <div className="md:col-span-2"><p className="text-caption font-bold text-slate-400 mb-1">Full Name</p><input value={localPersonal.fullNameLegal || ''} readOnly className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 text-slate-600" title="Auto-computed from first, middle, and last name" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Date of Birth</p><input type="date" value={localPersonal.dob} onChange={e => setLocalPersonal({ ...localPersonal, dob: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Gender</p>
                  <select value={localPersonal.gender} onChange={e => setLocalPersonal({ ...localPersonal, gender: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                    <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Marital Status</p>
                  <select value={localPersonal.maritalStatus} onChange={e => setLocalPersonal({ ...localPersonal, maritalStatus: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                    <option value="">Select</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Contact</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-caption font-bold text-slate-400 mb-1">Phone</p><input type="tel" value={localPersonal.phone} onChange={e => setLocalPersonal({ ...localPersonal, phone: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />{localPersonal.phone && !isValidPhone(localPersonal.phone) && <p className="text-caption text-rose-500 mt-1">Enter a valid phone number e.g. +1-876-555-0199</p>}</div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Country Code</p><input value={localPersonal.countryCode || ''} onChange={e => setLocalPersonal({ ...localPersonal, countryCode: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. +1" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Mobile Number</p><input type="tel" value={localPersonal.mobileNumber || ''} onChange={e => setLocalPersonal({ ...localPersonal, mobileNumber: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Best Time to Call</p>
                  <select value={localPersonal.bestTimeToCall || ''} onChange={e => setLocalPersonal({ ...localPersonal, bestTimeToCall: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                    <option value="">Select</option><option>Morning</option><option>Afternoon</option><option>Evening</option><option>Any</option>
                  </select>
                </div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Email</p><p className="text-sm text-slate-800 pt-2">{app.userEmail}</p></div>
              </div>
              <div className="mt-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Street</p>
                <input value={localPersonal.address.street} onChange={e => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, street: e.target.value } })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
                <div className="mt-2"><p className="text-caption font-bold text-slate-400 mb-1">Apartment</p><input value={localPersonal.address.apartment || ''} onChange={e => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, apartment: e.target.value } })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Apt, Suite, Unit #" /></div>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div><p className="text-caption font-bold text-slate-400 mb-1">City</p><input value={localPersonal.address.city} onChange={e => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, city: e.target.value } })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                  <div><p className="text-caption font-bold text-slate-400 mb-1">State</p><select value={localPersonal.address.state || ''} onChange={e => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, state: e.target.value } })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl"><option value="">— Select —</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div><p className="text-caption font-bold text-slate-400 mb-1">Zip</p><input inputMode="numeric" maxLength={10} value={localPersonal.address.zip || ''} onChange={e => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, zip: e.target.value.replace(/\D/g, '').slice(0, 10) } })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                </div>
                <div className="mt-2"><CountrySelect label="Country" value={localPersonal.address.country} onChange={value => setLocalPersonal({ ...localPersonal, address: { ...localPersonal.address, country: value } })} /></div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Legal / Immigration</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <CountrySelect label="Citizenship" value={localPersonal.countryOfCitizenship} onChange={value => setLocalPersonal({ ...localPersonal, countryOfCitizenship: value })} />
                <CountrySelect label="Country of Birth" value={localPersonal.countryOfBirth} onChange={value => setLocalPersonal({ ...localPersonal, countryOfBirth: value })} />
                <div><p className="text-caption font-bold text-slate-400 mb-1">City of Birth</p><input value={localPersonal.cityOfBirth || ''} onChange={e => setLocalPersonal({ ...localPersonal, cityOfBirth: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                <CountrySelect label="Legal Permanent Residence" value={localPersonal.legalPermanentResidenceCountry} onChange={value => setLocalPersonal({ ...localPersonal, legalPermanentResidenceCountry: value })} />
                <div><p className="text-caption font-bold text-slate-400 mb-1">SSN / Tax ID</p><input type="password" autoComplete="off" value={localPersonal.ssnOrTaxId || ''} onChange={e => setLocalPersonal({ ...localPersonal, ssnOrTaxId: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Travel History</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-caption font-bold text-slate-400 mb-1">Previous Travel</p><textarea rows={2} value={localPersonal.previousTravelHistory || ''} onChange={e => setLocalPersonal({ ...localPersonal, previousTravelHistory: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Describe previous international travel experience" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Visa History</p><textarea rows={2} value={localPersonal.previousVisaHistory || ''} onChange={e => setLocalPersonal({ ...localPersonal, previousVisaHistory: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Describe any previous visa history" /></div>
                <div><label className="flex items-center space-x-2"><input type="checkbox" checked={localPersonal.firstTimeTraveling || false} onChange={e => setLocalPersonal({ ...localPersonal, firstTimeTraveling: e.target.checked })} className="cursor-pointer" /><span className="text-caption font-bold text-slate-400">First time traveling internationally</span></label></div>
              </div>
              <div className="mt-3">
                <p className="text-caption font-bold text-slate-400 mb-1">Immediate Family Overseas</p>
                {(localPersonal.immediateFamilyOverseas || []).map((m: any, i: number) => (
                  <div key={i} className="flex items-center space-x-2 mt-1">
                    <input value={m.name || ''} onChange={e => { const nv = [...(localPersonal.immediateFamilyOverseas || [])]; nv[i] = { ...nv[i], name: e.target.value }; setLocalPersonal({ ...localPersonal, immediateFamilyOverseas: nv }); }} placeholder="Name" className="w-1/3 px-3 py-2 text-sm border border-slate-200 rounded-xl" />
                    <input value={m.relationship || ''} onChange={e => { const nv = [...(localPersonal.immediateFamilyOverseas || [])]; nv[i] = { ...nv[i], relationship: e.target.value }; setLocalPersonal({ ...localPersonal, immediateFamilyOverseas: nv }); }} placeholder="Relationship" className="w-1/4 px-3 py-2 text-sm border border-slate-200 rounded-xl" />
                    <CountrySelect value={m.country || ''} onChange={value => { const nv = [...(localPersonal.immediateFamilyOverseas || [])]; nv[i] = { ...nv[i], country: value }; setLocalPersonal({ ...localPersonal, immediateFamilyOverseas: nv }); }} />
                    <button aria-label="Remove family member" title="Remove family member" onClick={() => { const nv = [...(localPersonal.immediateFamilyOverseas || [])]; nv.splice(i, 1); setLocalPersonal({ ...localPersonal, immediateFamilyOverseas: nv }); }} className="p-1.5 text-rose-400 hover:text-rose-600 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setLocalPersonal({ ...localPersonal, immediateFamilyOverseas: [...(localPersonal.immediateFamilyOverseas || []), { name: '', relationship: '', country: '' }] })} className="mt-1 text-caption font-semibold text-brand hover:text-brand cursor-pointer">+ Add family member</button>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Additional Information</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="md:col-span-2"><p className="text-caption font-bold text-slate-400 mb-1">Objective</p><textarea rows={2} value={localPersonal.objective || ''} onChange={e => setLocalPersonal({ ...localPersonal, objective: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Describe goals and motivation" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Primary Language</p><input value={localPersonal.primaryLanguage || ''} onChange={e => setLocalPersonal({ ...localPersonal, primaryLanguage: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. English" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Other Languages</p><input value={localPersonal.otherLanguages || ''} onChange={e => setLocalPersonal({ ...localPersonal, otherLanguages: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Spanish, French" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Computer Skills</p><input value={localPersonal.computerSkills || ''} onChange={e => setLocalPersonal({ ...localPersonal, computerSkills: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. MS Office, Photoshop" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Current Country of Residence</p><input value={localPersonal.currentCountryOfResidence || ''} onChange={e => setLocalPersonal({ ...localPersonal, currentCountryOfResidence: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. United States" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Countries Resided</p><input value={localPersonal.countriesResided || ''} onChange={e => setLocalPersonal({ ...localPersonal, countriesResided: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Canada, UK" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Personal Skills</p><input value={localPersonal.personalSkills || ''} onChange={e => setLocalPersonal({ ...localPersonal, personalSkills: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Leadership, teamwork" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Leisure Activities</p><input value={localPersonal.leisureActivities || ''} onChange={e => setLocalPersonal({ ...localPersonal, leisureActivities: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Photography, hiking" /></div>
                <div><p className="text-caption font-bold text-slate-400 mb-1">Hospitality Experience</p>
                  <select value={localPersonal.hospitalityExperience || ''} onChange={e => setLocalPersonal({ ...localPersonal, hospitalityExperience: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                    <option value="">— Select —</option>
                    <option>No experience</option>
                    <option>Less than 1 year</option>
                    <option>1-2 years</option>
                    <option>3-5 years</option>
                    <option>5-10 years</option>
                    <option>10+ years</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Student &amp; J-1 Programs</p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-slate-700">Full-Time Student</span>
                  <label className="flex items-center space-x-1.5 text-sm cursor-pointer"><input type="checkbox" checked={localPersonal.fullTimeStudent === true} onChange={e => setLocalPersonal({ ...localPersonal, fullTimeStudent: e.target.checked })} className="cursor-pointer" /><span>Yes</span></label>
                </div>
                <div>
                  <p className="text-caption font-bold text-slate-400 mb-1">J-1 Program Participation</p>
                  {(localPersonal.j1ProgramParticipation || []).map((j: any, i: number) => (
                    <div key={i} className="flex items-start space-x-2 mt-1">
                      <div className="flex-1"><input value={j.programName || ''} onChange={e => { const nv = [...(localPersonal.j1ProgramParticipation || [])]; nv[i] = { ...nv[i], programName: e.target.value }; setLocalPersonal({ ...localPersonal, j1ProgramParticipation: nv }); }} placeholder="Program name" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                      <div className="w-28"><input type="date" value={j.startDate || ''} onChange={e => { const nv = [...(localPersonal.j1ProgramParticipation || [])]; nv[i] = { ...nv[i], startDate: e.target.value }; setLocalPersonal({ ...localPersonal, j1ProgramParticipation: nv }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                      <div className="w-28"><input type="date" value={j.endDate || ''} onChange={e => { const nv = [...(localPersonal.j1ProgramParticipation || [])]; nv[i] = { ...nv[i], endDate: e.target.value }; setLocalPersonal({ ...localPersonal, j1ProgramParticipation: nv }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
                      <button aria-label="Remove participation entry" title="Remove participation entry" onClick={() => { const nv = [...(localPersonal.j1ProgramParticipation || [])]; nv.splice(i, 1); setLocalPersonal({ ...localPersonal, j1ProgramParticipation: nv }); }} className="p-1.5 text-rose-400 hover:text-rose-600 cursor-pointer mt-1"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={() => setLocalPersonal({ ...localPersonal, j1ProgramParticipation: [...(localPersonal.j1ProgramParticipation || []), { programName: '', startDate: '', endDate: '' }] })} className="mt-1 text-caption font-semibold text-brand hover:text-brand cursor-pointer">+ Add J-1 program</button>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 uppercase text-[10px]">Criminal &amp; Health Background</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5">Have you ever been convicted of a crime?</label>
                  <select
                    className="w-full max-w-xs bg-white border border-slate-300 focus:border-brand focus:ring-1 focus:ring-brand-primary rounded-xl px-3 py-2 text-sm outline-none text-slate-800 font-medium shadow-2xs"
                    value={localPersonal.criminalConviction === undefined ? '' : String(localPersonal.criminalConviction)}
                    onChange={(e) =>
                      setLocalPersonal({
                        ...localPersonal,
                        criminalConviction: e.target.value === '' ? undefined : e.target.value === 'true',
                        criminalConvictionExplanation: e.target.value !== 'true' ? '' : localPersonal.criminalConvictionExplanation,
                      })
                    }
                  >
                    <option value="">— Select —</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                {localPersonal.criminalConviction === true && (
                  <div><p className="text-caption font-bold text-slate-400 mb-1">If Yes, please explain</p><textarea rows={2} value={localPersonal.criminalConvictionExplanation || ''} onChange={e => setLocalPersonal({ ...localPersonal, criminalConvictionExplanation: e.target.value })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Explain..." /></div>
                )}
                <div className="flex flex-col space-y-2">
                  <label className="flex items-start space-x-2 text-sm cursor-pointer"><input type="checkbox" checked={localPersonal.insurancePreexistingAcknowledged === true} onChange={e => setLocalPersonal({ ...localPersonal, insurancePreexistingAcknowledged: e.target.checked })} className="cursor-pointer mt-0.5" /><span className="text-slate-700">I understand my insurance does not cover preexisting conditions and I am responsible for all fees.</span></label>
                  <label className="flex items-start space-x-2 text-sm cursor-pointer"><input type="checkbox" checked={localPersonal.insuranceAdditionalPurchased === true} onChange={e => setLocalPersonal({ ...localPersonal, insuranceAdditionalPurchased: e.target.checked })} className="cursor-pointer mt-0.5" /><span className="text-slate-700">I understand I must purchase additional insurance to cover preexisting conditions.</span></label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
              <button onClick={() => setIsEditingLocalPersonal(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
              <button onClick={() => { appCtx.handleSavePersonal(localPersonal); setIsEditingLocalPersonal(false); }} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <InfoItem label="First Name" value={app.personalDetails?.firstName} />
              <InfoItem label="Last Name" value={app.personalDetails?.lastName} />
              <InfoItem label="Middle Name" value={app.personalDetails?.middleName} />
              <InfoItem label="Legal Full Name" value={app.personalDetails?.fullNameLegal} />
              <InfoItem label="Date of Birth" value={app.personalDetails?.dob} />
              <InfoItem label="Gender" value={app.personalDetails?.gender} />
              <InfoItem label="Citizenship" value={app.personalDetails?.countryOfCitizenship} />
              <InfoItem label="Country of Birth" value={app.personalDetails?.countryOfBirth} />
              <InfoItem label="City of Birth" value={app.personalDetails?.cityOfBirth} />
              <InfoItem label="Legal Permanent Residence" value={app.personalDetails?.legalPermanentResidenceCountry} />
              <InfoItem label="Phone" value={app.personalDetails?.phone} />
              <InfoItem label="Country Code" value={app.personalDetails?.countryCode} />
              <InfoItem label="Mobile Number" value={app.personalDetails?.mobileNumber} />
              <InfoItem label="Best Time to Call" value={app.personalDetails?.bestTimeToCall} />
              <InfoItem label="Email" value={app.userEmail} />
              <InfoItem label="Marital Status" value={app.personalDetails?.maritalStatus} />
              <InfoItem label="SSN / Tax ID" value={app.personalDetails?.ssnOrTaxId ? '••••' : '—'} />
            </div>
            {app.personalDetails?.address && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Address</p>
                <p className="text-[13px] font-medium text-slate-800 bg-slate-50 rounded-lg p-3">
                  {app.personalDetails.address.street}
                  {app.personalDetails.address.apartment && `, ${app.personalDetails.address.apartment}`}
                  <br />
                  {app.personalDetails.address.city}, {app.personalDetails.address.state} {app.personalDetails.address.zip}
                  <br />
                  {app.personalDetails.address.country}
                </p>
              </div>
            )}
            {/* Travel History (read-only) */}
            {(app.personalDetails?.previousTravelHistory || app.personalDetails?.previousVisaHistory || app.personalDetails?.firstTimeTraveling !== undefined || (app.personalDetails?.immediateFamilyOverseas || []).length > 0) && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Travel History</p>
                <div className="grid grid-cols-1 gap-3">
                  {app.personalDetails.previousTravelHistory && (
                    <div><p className="text-caption font-bold text-slate-400 mb-0.5">Previous Travel</p><p className="text-[13px] text-slate-800 bg-slate-50 rounded-lg p-3">{app.personalDetails.previousTravelHistory}</p></div>
                  )}
                  {app.personalDetails.previousVisaHistory && (
                    <div><p className="text-caption font-bold text-slate-400 mb-0.5">Visa History</p><p className="text-[13px] text-slate-800 bg-slate-50 rounded-lg p-3">{app.personalDetails.previousVisaHistory}</p></div>
                  )}
                  {app.personalDetails.firstTimeTraveling !== undefined && (
                    <InfoItem label="First Time Traveling" value={app.personalDetails.firstTimeTraveling ? 'Yes' : 'No'} />
                  )}
                  {(app.personalDetails.immediateFamilyOverseas || []).length > 0 && (
                    <div>
                      <p className="text-caption font-bold text-slate-400 mb-1">Immediate Family Overseas</p>
                      <div className="space-y-1">
                        {app.personalDetails.immediateFamilyOverseas?.map((m, i) => (
                          <p key={i} className="text-[13px] text-slate-800 bg-slate-50 rounded-lg p-2">{m.name} — {m.relationship} — {m.country}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Additional Information (read-only) */}
            {(app.personalDetails?.objective || app.personalDetails?.primaryLanguage || app.personalDetails?.computerSkills || app.personalDetails?.leisureActivities || app.personalDetails?.currentCountryOfResidence) && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Additional Information</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {app.personalDetails.objective && <div className="md:col-span-2"><InfoItem label="Objective" value={app.personalDetails.objective} /></div>}
                  {app.personalDetails.primaryLanguage && <InfoItem label="Primary Language" value={app.personalDetails.primaryLanguage} />}
                  {app.personalDetails.otherLanguages && <InfoItem label="Other Languages" value={`${app.personalDetails.otherLanguages}${app.personalDetails.otherLanguageDetail ? ` (${app.personalDetails.otherLanguageDetail})` : ""}`} />}
                  {app.personalDetails.computerSkills && <InfoItem label="Skills" value={app.personalDetails.computerSkills} />}
                  {app.personalDetails.personalSkills && <InfoItem label="Personal Skills" value={app.personalDetails.personalSkills} />}
                  {app.personalDetails.currentCountryOfResidence && <InfoItem label="Current Country of Residence" value={app.personalDetails.currentCountryOfResidence} />}
                  {app.personalDetails.countriesResided && <InfoItem label="Countries Resided" value={app.personalDetails.countriesResided} />}
                  {app.personalDetails.leisureActivities && <InfoItem label="Leisure Activities" value={app.personalDetails.leisureActivities} />}
                  {app.personalDetails.hospitalityExperience && <InfoItem label="Hospitality Experience" value={app.personalDetails.hospitalityExperience} />}
                </div>
              </div>
            )}
            {/* Student & J-1 Programs (read-only) */}
            {(app.personalDetails?.fullTimeStudent !== undefined || (app.personalDetails?.j1ProgramParticipation || []).length > 0) && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Student &amp; J-1 Programs</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {app.personalDetails.fullTimeStudent !== undefined && <InfoItem label="Full-Time Student" value={app.personalDetails.fullTimeStudent ? 'Yes' : 'No'} />}
                  {(app.personalDetails.j1ProgramParticipation || []).length > 0 && (
                    <div className="md:col-span-2">
                      <p className="text-caption font-bold text-slate-400 mb-1">J-1 Program Participation</p>
                      <div className="space-y-1">
                        {app.personalDetails.j1ProgramParticipation?.map((j: any, i: number) => (
                          <p key={i} className="text-[13px] text-slate-800 bg-slate-50 rounded-lg p-2">{j.programName}{j.startDate ? ` (${j.startDate}` : ''}{j.endDate ? ` — ${j.endDate}` : ''}{j.startDate ? ')' : ''}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Criminal & Health Background (read-only) */}
            {(app.personalDetails?.criminalConviction !== undefined || app.personalDetails?.insurancePreexistingAcknowledged !== undefined || app.personalDetails?.insuranceAdditionalPurchased !== undefined) && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Criminal &amp; Health Background</p>
                <div className="grid grid-cols-1 gap-3">
                  {app.personalDetails.criminalConviction !== undefined && (
                    <div><InfoItem label="Criminal Conviction" value={app.personalDetails.criminalConviction ? 'Yes' : 'No'} /></div>
                  )}
                  {app.personalDetails.criminalConvictionExplanation && (
                    <div><p className="text-caption font-bold text-slate-400 mb-0.5">Explanation</p><p className="text-[13px] text-slate-800 bg-slate-50 rounded-lg p-3">{app.personalDetails.criminalConvictionExplanation}</p></div>
                  )}
                  {app.personalDetails.insurancePreexistingAcknowledged && (
                    <div className="flex items-start space-x-2"><span className="text-brand mt-0.5">✓</span><p className="text-[13px] text-slate-700">Insurance: preexisting conditions responsibility acknowledged</p></div>
                  )}
                  {app.personalDetails.insuranceAdditionalPurchased && (
                    <div className="flex items-start space-x-2"><span className="text-brand mt-0.5">✓</span><p className="text-[13px] text-slate-700">Insurance: additional coverage for preexisting conditions</p></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Passport & Immigration */}
      <SectionCard title="Passport & Immigration">
        {appCtx.isEditingPassport ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Passport Number</p><input value={appCtx.editPassportNo || ''} onChange={e => appCtx.setEditPassportNo(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <CountrySelect label="Country" value={appCtx.editPassportCountry || ''} onChange={value => appCtx.setEditPassportCountry(value)} />
              <div><p className="text-caption font-bold text-slate-400 mb-1">Issue Date</p><input type="date" value={toDateInputValue(appCtx.editPassportIssue) || ''} onChange={e => appCtx.setEditPassportIssue(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Expiry Date</p><input type="date" value={toDateInputValue(appCtx.editPassportExpiry) || ''} onChange={e => appCtx.setEditPassportExpiry(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Visa Status</p>
                <select value={appCtx.editPassVisaStatus || 'None'} onChange={e => appCtx.setEditPassVisaStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20">
                  <option value="None">None</option><option>Pending</option><option>Approved</option><option>Denied</option>
                </select>
              </div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">DS-2019 No.</p><input value={appCtx.editDs2019 || ''} onChange={e => appCtx.setEditDs2019(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">SEVIS ID</p><input value={appCtx.editSevisId || ''} onChange={e => appCtx.setEditSevisId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Intended Arrival</p><input type="date" value={toDateInputValue(appCtx.editIntendedArrival) || ''} onChange={e => appCtx.setEditIntendedArrival(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">U.S. Destination</p><input value={appCtx.editUsDestination || ''} onChange={e => appCtx.setEditUsDestination(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" placeholder="City, State" /></div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={() => appCtx.handleSavePassport({})} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingPassport(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Passport Number" value={app.passportImmigration?.passportNumber} />
              <InfoItem label="Country" value={app.passportImmigration?.passportCountry} />
              <InfoItem label="Issue Date" value={app.passportImmigration?.issueDate} />
              <InfoItem label="Expiry Date" value={app.passportImmigration?.expirationDate} />
              <InfoItem label="Visa Status" value={app.passportImmigration?.visaStatus} />
              <InfoItem label="DS-2019 No." value={app.passportImmigration?.ds2019Number} />
              <InfoItem label="SEVIS ID" value={app.passportImmigration?.sevisId} />
              <InfoItem label="Intended Arrival" value={app.passportImmigration?.intendedArrival} />
              <InfoItem label="U.S. Destination" value={app.passportImmigration?.usDestination} />
            </div>
            {canEditProfile && (
              <button onClick={() => appCtx.setIsEditingPassport(true)} className="mt-3 text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1">
                <Edit3 className="h-3 w-3" /><span>Edit</span>
              </button>
            )}
          </div>
        )}
      </SectionCard>

      {/* Emergency Contacts */}
      <EmergencyContactsSection app={app} appCtx={appCtx} maskValue={maskValue} canViewContact={canViewContact} canEditProfile={canEditProfile} />

      {/* Program Preferences */}
      <SectionCard title="Program Preferences">
        {appCtx.isEditingProgram ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Preferred Industry</p><input value={appCtx.editProgIndustry || ''} onChange={e => appCtx.setEditProgIndustry(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Preferred State</p><select value={appCtx.editProgState || ''} onChange={e => appCtx.setEditProgState(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl"><option value="">— Select —</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Desired Start</p><input type="date" value={appCtx.editProgStart || ''} onChange={e => appCtx.setEditProgStart(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Duration (months)</p><input type="number" min="1" value={appCtx.editProgDuration || ''} onChange={e => appCtx.setEditProgDuration(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">English Proficiency</p><input value={appCtx.editProgEnglish || ''} onChange={e => appCtx.setEditProgEnglish(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Fluent, Intermediate" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Special Skills</p><input value={appCtx.editProgSkills || ''} onChange={e => appCtx.setEditProgSkills(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="e.g. Customer service, sales" /></div>
              <div><label className="flex items-center space-x-2"><input type="checkbox" checked={appCtx.editProgRelocate ?? true} onChange={e => appCtx.setEditProgRelocate(e.target.checked)} className="cursor-pointer" /><span className="text-caption font-bold text-slate-400">Willing to relocate</span></label></div>
              <div className="md:col-span-2"><p className="text-caption font-bold text-slate-400 mb-1">Additional Notes</p><textarea rows={2} value={appCtx.editProgNotes || ''} onChange={e => appCtx.setEditProgNotes(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" placeholder="Any additional notes" /></div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={() => appCtx.handleSaveProgram({})} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingProgram(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Preferred Industry" value={app.programPreferences?.preferredIndustry} />
              <InfoItem label="Preferred State" value={app.programPreferences?.preferredState} />
              <InfoItem label="Desired Start" value={app.programPreferences?.startDateAvailability} />
              <InfoItem label="Relocate" value={typeof app.programPreferences?.willingnessToRelocate === 'boolean' ? (app.programPreferences.willingnessToRelocate ? 'Yes' : 'No') : '—'} />
              <InfoItem label="Program Category" value={app.programPreferences?.programType} />
              <InfoItem label="Duration (months)" value={app.programPreferences?.programDuration} />
              <InfoItem label="English Proficiency" value={app.programPreferences?.englishProficiency} />
              <InfoItem label="Special Skills" value={app.programPreferences?.specialSkills} />
              <InfoItem label="Additional Notes" value={app.programPreferences?.additionalNotes} />
            </div>
            {Array.isArray((app as any).selectedSkills) && (app as any).selectedSkills.length > 0 && (
              <div className="mt-4">
                <p className="text-caption font-bold text-slate-400 mb-1.5">Skills &amp; Abilities</p>
                <div className="flex flex-wrap gap-1.5">
                  {(app as any).selectedSkills.map((s: any) => (
                    <span key={s.skillId} className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 text-xs rounded-full font-semibold">
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {canEditProfile && (
              <button onClick={() => appCtx.setIsEditingProgram(true)} className="mt-3 text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1">
                <Edit3 className="h-3 w-3" /><span>Edit</span>
              </button>
            )}
          </div>
        )}
      </SectionCard>

      {/* Education */}
      <EducationSection app={app} appCtx={appCtx} canEditProfile={canEditProfile} />

      {/* Work Experience */}
      <WorkSection app={app} appCtx={appCtx} canEditProfile={canEditProfile} />
    </div>
  );
});

/* ───────────────────── EMERGENCY CONTACTS SECTION ───────────────────── */

function EmergencyContactsSection({ app, appCtx, maskValue, canViewContact, canEditProfile = true }: { app: Application; appCtx: any; maskValue?: (v?: string, kind?: 'email' | 'phone' | 'text') => string; canViewContact?: boolean; canEditProfile?: boolean }) {
  const [localEc, setLocalEc] = useState<any[]>([]);
  const [editingEc, setEditingEc] = useState(false);

  const formatValue = (val?: string, kind: 'email' | 'phone' | 'text' = 'text') => {
    if (maskValue) return maskValue(val, kind) || '—';
    // Never render a still-encrypted block object (React error #31).
    return safePiiText(val, '—');
  };

  if (!editingEc) {
    return (
      <SectionCard title="Emergency Contacts">
        <div>
          {(app.emergencyContacts || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {app.emergencyContacts.map((ec: any, i: number) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Contact #{i + 1}</p>
                  <p className="text-sm font-bold text-slate-900 break-words">{formatValue(ec.fullName, 'text')}</p>
                  <p className="text-caption text-slate-500 break-words">{ec.relationship || 'Relationship not provided'}</p>
                  <p className="text-sm font-semibold text-slate-700 mt-2 break-words">{formatValue(ec.phone, 'phone')}</p>
                  {ec.email && <p className="text-caption text-slate-500 mt-1 break-all">{formatValue(ec.email, 'email')}</p>}
                  {ec.address && <p className="text-caption text-slate-500 mt-0.5 break-all">{formatValue(ec.address, 'text')}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">No emergency contacts on file.</p>
          )}
          {canEditProfile && (
            <button onClick={() => { setLocalEc(app.emergencyContacts?.length ? [...app.emergencyContacts] : []); setEditingEc(true); }} className="mt-3 text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1">
              <Edit3 className="h-3 w-3" /><span>{app.emergencyContacts?.length ? 'Edit' : 'Add'}</span>
            </button>
          )}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Emergency Contacts">
      <div className="space-y-4">
        {localEc.length === 0 && (
          <p className="text-sm text-slate-500 italic">No contacts added yet. Click "Add Contact" below.</p>
        )}
        {localEc.map((ec: any, i: number) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-2 relative">
            <button
              onClick={() => setLocalEc(prev => prev.filter((_, idx) => idx !== i))}
              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <p className="text-caption font-bold text-slate-500">Contact #{i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption text-slate-400">Name</p><input value={ec.fullName || ''} onChange={e => { const nv = [...localEc]; nv[i] = { ...nv[i], fullName: e.target.value }; setLocalEc(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Phone</p><input type="tel" value={ec.phone || ''} onChange={e => { const nv = [...localEc]; nv[i] = { ...nv[i], phone: e.target.value }; setLocalEc(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Relationship</p>
                <select value={ec.relationship || ''} onChange={e => { const nv = [...localEc]; nv[i] = { ...nv[i], relationship: e.target.value }; setLocalEc(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                  <option value="">— Select —</option><option>Spouse</option><option>Parent</option><option>Sibling</option><option>Child</option><option>Friend</option><option>Other</option>
                </select>
              </div>
              <div><p className="text-caption text-slate-400">Email</p><input type="email" value={ec.email || ''} onChange={e => { const nv = [...localEc]; nv[i] = { ...nv[i], email: e.target.value }; setLocalEc(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Address</p><input value={ec.address || ''} onChange={e => { const nv = [...localEc]; nv[i] = { ...nv[i], address: e.target.value }; setLocalEc(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
            </div>
          </div>
        ))}
        <button
          onClick={() => setLocalEc(prev => [...prev, { fullName: '', phone: '', relationship: '', email: '', address: '' }])}
          className="text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1"
        >
          <Plus className="h-3.5 w-3.5" /><span>Add Contact</span>
        </button>
        <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
          <button onClick={() => setEditingEc(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
          <button onClick={() => { if (!localEc.some((ec: any) => ec.fullName?.trim() || ec.phone?.trim())) { notifyToast('warning', 'At least one emergency contact must have a name or phone number.'); return; } appCtx.handleSaveEmergency(localEc); setEditingEc(false); }} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
        </div>
      </div>
    </SectionCard>
  );
}

/* ───────────────────── EDUCATION SECTION ───────────────────── */

function EducationSection({ app, appCtx, canEditProfile = true }: { app: Application; appCtx: any; canEditProfile?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  /** Month number → name. */
  const monthName = (m?: string) =>
    m ? ['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(m, 10) - 1] || m : '';
  const fmtMY2 = (m?: string, y?: string) => (m && y ? `${monthName(m)} ${y}` : y || m || '—');
  const fmtEnd2 = (m?: string, y?: string) => (m || y ? fmtMY2(m, y) : 'Present');

  const hasItems = app.educationalBackground && app.educationalBackground.length > 0;

  if (!editing) {
    if (!hasItems) {
      return (
        <SectionCard title="Education" action={
          canEditProfile ? (
            <button onClick={() => { setItems([]); setEditing(true); }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
              <Plus className="h-3.5 w-3.5" /><span>Add</span>
            </button>
          ) : null
        }>
          <p className="text-sm text-slate-500 italic">No education history on file.</p>
        </SectionCard>
      );
    }
    return (
      <SectionCard title="Education" action={
        canEditProfile ? (
          <button onClick={() => { setItems(app.educationalBackground ? app.educationalBackground.map((edu: any) => ({ institution: edu.institutionName || edu.institution || edu.school || '', degree: edu.degreeEarned || edu.degree || edu.qualification || '', field: edu.fieldOfStudy || edu.field || '', gpa: edu.gpa || '', honors: edu.honors || '', institutionAddress: edu.institutionAddress || '', city: edu.city || '', country: edu.country || '', startMonth: edu.startMonth || '', startYear: edu.startYear || '', endMonth: edu.endMonth || '', endYear: edu.endYear || '', courses: edu.courses || '' })) : []); setEditing(true); }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {app.educationalBackground.map((edu: any, i: number) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm mb-3 last:mb-0 min-w-0">
            <p className="text-sm font-bold text-slate-900">{edu.institutionName || edu.institution || edu.school || '—'}</p>
            <p className="text-caption text-slate-500">{edu.degreeEarned || edu.degree || edu.qualification || ''}{edu.fieldOfStudy || edu.field ? ` — ${edu.fieldOfStudy || edu.field}` : ''}</p>
            {(edu.city || edu.country) && <p className="text-caption text-slate-400">{edu.city}{edu.city && edu.country ? ', ' : ''}{edu.country}</p>}
            {edu.institutionAddress && <p className="text-caption text-slate-400">{edu.institutionAddress}</p>}
            {(edu.startMonth || edu.startYear) ? <p className="text-caption text-slate-400">{fmtMY2(edu.startMonth, edu.startYear)} — {fmtEnd2(edu.endMonth, edu.endYear)}</p> : edu.startDate ? <p className="text-caption text-slate-400">{edu.startDate} — {edu.endDate || 'Present'}</p> : null}
            {(edu.gpa || edu.honors || edu.courses) && <p className="text-caption text-slate-400 mt-1">{edu.courses ? `Courses: ${edu.courses}` : ''}{edu.courses && (edu.gpa || edu.honors) ? ' · ' : ''}{edu.gpa ? `GPA: ${edu.gpa}` : ''}{edu.gpa && edu.honors ? ' · ' : ''}{edu.honors ? `Honors: ${edu.honors}` : ''}</p>}
          </div>
        ))}
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Education">
      <div className="space-y-4">
        {items.length === 0 && (
          <p className="text-sm text-slate-500 italic">No entries yet. Click "Add Entry" below.</p>
        )}
        {items.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-2 relative">
            <button
              onClick={() => setItems(prev => prev.filter((_, idx) => idx !== i))}
              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <p className="text-caption font-bold text-slate-500">School #{i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Institution</p><input value={item.institution || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], institution: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Institution Address</p><input value={item.institutionAddress || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], institutionAddress: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Degree</p>
                <select value={item.degree || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], degree: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                  <option value="">— Select —</option><option>Associate Degree</option><option>Bachelor's Degree</option><option>Master's Degree</option><option>Doctorate / PhD</option><option>Diploma</option><option>Certificate</option><option>High School Diploma</option><option>Other</option>
                </select>
              </div>
              <div><p className="text-caption text-slate-400">Field of Study</p><input value={item.field || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], field: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Start Date</p><input type="month" value={item.startMonth ? `${item.startYear || ''}-${String(item.startMonth).padStart(2, '0')}` : ''} onChange={e => { const [y, m] = e.target.value.split('-'); const nv = [...items]; nv[i] = { ...nv[i], startMonth: m, startYear: y }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">End Date</p><div className="flex items-center space-x-2"><input type="month" value={item.endMonth ? `${item.endYear || ''}-${String(item.endMonth).padStart(2, '0')}` : ''} onChange={e => { const [y, m] = e.target.value.split('-'); const nv = [...items]; nv[i] = { ...nv[i], endMonth: m, endYear: y }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /><label className="flex items-center space-x-1 text-caption text-slate-500 whitespace-nowrap"><input type="checkbox" checked={!item.endMonth && !item.endYear} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], endMonth: '', endYear: '' }; setItems(nv); }} className="cursor-pointer" /><span>Still Attending</span></label></div></div>
              <div><p className="text-caption text-slate-400">GPA</p><input type="number" min="0" max="4" step="0.01" value={item.gpa || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], gpa: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Honors</p><input value={item.honors || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], honors: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">City</p><input value={item.city || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], city: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Country</p><input value={item.country || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], country: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Courses</p><input value={item.courses || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], courses: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
            </div>
          </div>
        ))}
        <button
          onClick={() => setItems(prev => [...prev, { institution: '', degree: '', field: '', institutionAddress: '', gpa: '', honors: '', city: '', country: '', startMonth: '', startYear: '', endMonth: '', endYear: '', courses: '' }])}
          className="text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1"
        >
          <Plus className="h-3.5 w-3.5" /><span>Add Entry</span>
        </button>
        <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
          <button onClick={() => setEditing(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
          <button onClick={() => { appCtx.handleSaveEducation(items.map((i: any) => ({ institutionName: i.institution, degreeEarned: i.degree, fieldOfStudy: i.field, institutionAddress: i.institutionAddress || '', gpa: i.gpa || '', honors: i.honors || '', city: i.city || '', country: i.country || '', startMonth: i.startMonth || '', startYear: i.startYear || '', endMonth: i.endMonth || '', endYear: i.endYear || '', courses: i.courses || '' }))); setEditing(false); }} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
        </div>
      </div>
    </SectionCard>
  );
}

/* ───────────────────── WORK EXPERIENCE SECTION ───────────────────── */

function WorkSection({ app, appCtx, canEditProfile = true }: { app: Application; appCtx: any; canEditProfile?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  const monthNameW = (m?: string) =>
    m ? ['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(m, 10) - 1] || m : '';
  const fmtMYw = (m?: string, y?: string) => (m && y ? `${monthNameW(m)} ${y}` : y || m || '—');
  const fmtEndW = (m?: string, y?: string) => (m || y ? fmtMYw(m, y) : 'Present');

  const hasItems = app.workExperience && app.workExperience.length > 0;

  if (!editing) {
    if (!hasItems) {
      return (
        <SectionCard title="Work Experience" action={
          canEditProfile ? (
            <button onClick={() => { setItems([]); setEditing(true); }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
              <Plus className="h-3.5 w-3.5" /><span>Add</span>
            </button>
          ) : null
        }>
          <p className="text-sm text-slate-500 italic">No employment history on file.</p>
        </SectionCard>
      );
    }
    return (
      <SectionCard title="Work Experience" action={
        canEditProfile ? (
          <button onClick={() => { setItems(app.workExperience ? app.workExperience.map((w: any) => ({ employer: w.employerName || w.employer || w.company || '', role: w.jobTitle || w.role || w.position || '', location: w.employerAddress || w.location || '', jobDescription: w.jobDescription || '', supervisorName: w.supervisorName || '', supervisorContact: w.supervisorContact || '', salary: w.salary || '', mainResponsibilities: w.mainResponsibilities || '', additionalResponsibilities: w.additionalResponsibilities || '', city: w.city || '', country: w.country || '', startMonth: w.startMonth || '', startYear: w.startYear || '', endMonth: w.endMonth || '', endYear: w.endYear || '' })) : []); setEditing(true); }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {app.workExperience.map((work: any, i: number) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm mb-3 last:mb-0 min-w-0">
            <p className="text-sm font-bold text-slate-900">{work.employerName || work.employer || work.company || '—'}</p>
            <p className="text-caption text-slate-500">{work.jobTitle || work.role || work.position || ''}</p>
            {(work.city || work.country) && <p className="text-caption text-slate-400">{work.city}{work.city && work.country ? ', ' : ''}{work.country}</p>}
            {work.employerAddress && <p className="text-caption text-slate-400">{work.employerAddress}</p>}
            {(work.startMonth || work.startYear) ? <p className="text-caption text-slate-400">{fmtMYw(work.startMonth, work.startYear)} — {fmtEndW(work.endMonth, work.endYear)}</p> : work.startDate ? <p className="text-caption text-slate-400">{work.startDate} — {work.endDate || 'Present'}</p> : null}
            {work.mainResponsibilities && <p className="text-caption text-slate-500 mt-1"><span className="font-semibold">Main tasks:</span> {work.mainResponsibilities}</p>}
            {work.additionalResponsibilities && <p className="text-caption text-slate-500"><span className="font-semibold">Other tasks:</span> {work.additionalResponsibilities}</p>}
            {!work.mainResponsibilities && work.jobDescription && <p className="text-caption text-slate-500 mt-1 italic">{work.jobDescription}</p>}
            {(work.supervisorName || work.supervisorContact) && <p className="text-caption text-slate-400 mt-1">Supervisor: {work.supervisorName || ''}{work.supervisorName && work.supervisorContact ? ' · ' : ''}{work.supervisorContact || ''}</p>}
          </div>
        ))}
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Work Experience">
      <div className="space-y-4">
        {items.length === 0 && (
          <p className="text-sm text-slate-500 italic">No entries yet. Click "Add Entry" below.</p>
        )}
        {items.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-2 relative">
            <button
              onClick={() => setItems(prev => prev.filter((_, idx) => idx !== i))}
              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <p className="text-caption font-bold text-slate-500">Placement #{i + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Employer</p><input value={item.employer || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], employer: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Role</p><input value={item.role || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], role: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Employer Address</p><input value={item.location || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], location: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Job Description</p><textarea rows={2} value={item.jobDescription || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], jobDescription: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Start Date</p><input type="month" value={item.startMonth ? `${item.startYear || ''}-${String(item.startMonth).padStart(2, '0')}` : ''} onChange={e => { const [y, m] = e.target.value.split('-'); const nv = [...items]; nv[i] = { ...nv[i], startMonth: m, startYear: y }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">End Date</p><div className="flex items-center space-x-2"><input type="month" value={item.endMonth ? `${item.endYear || ''}-${String(item.endMonth).padStart(2, '0')}` : ''} onChange={e => { const [y, m] = e.target.value.split('-'); const nv = [...items]; nv[i] = { ...nv[i], endMonth: m, endYear: y }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /><label className="flex items-center space-x-1 text-caption text-slate-500 whitespace-nowrap"><input type="checkbox" checked={!item.endMonth && !item.endYear} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], endMonth: '', endYear: '' }; setItems(nv); }} className="cursor-pointer" /><span>Current</span></label></div></div>
              <div><p className="text-caption text-slate-400">Supervisor Name</p><input value={item.supervisorName || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], supervisorName: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Supervisor Contact</p><input type="tel" value={item.supervisorContact || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], supervisorContact: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Salary</p><div className="flex"><span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-xs font-bold text-slate-500">$</span><input type="number" min="0" step="0.01" value={item.salary || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], salary: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-r-xl" /></div></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Main Tasks</p><textarea rows={2} value={item.mainResponsibilities || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], mainResponsibilities: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div className="md:col-span-2"><p className="text-caption text-slate-400">Other Tasks</p><textarea rows={2} value={item.additionalResponsibilities || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], additionalResponsibilities: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">City</p><input value={item.city || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], city: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
              <div><p className="text-caption text-slate-400">Country</p><input value={item.country || ''} onChange={e => { const nv = [...items]; nv[i] = { ...nv[i], country: e.target.value }; setItems(nv); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" /></div>
            </div>
          </div>
        ))}
        <button
          onClick={() => setItems(prev => [...prev, { employer: '', role: '', location: '', jobDescription: '', supervisorName: '', supervisorContact: '', salary: '', mainResponsibilities: '', additionalResponsibilities: '', city: '', country: '', startMonth: '', startYear: '', endMonth: '', endYear: '' }])}
          className="text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1"
        >
          <Plus className="h-3.5 w-3.5" /><span>Add Entry</span>
        </button>
        <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
          <button onClick={() => setEditing(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
          <button onClick={() => { appCtx.handleSaveWork(items.map((i: any) => ({ employerName: i.employer, jobTitle: i.role, employerAddress: i.location, jobDescription: i.jobDescription || '', supervisorName: i.supervisorName || '', supervisorContact: i.supervisorContact || '', salary: i.salary || '', mainResponsibilities: i.mainResponsibilities || '', additionalResponsibilities: i.additionalResponsibilities || '', city: i.city || '', country: i.country || '', startMonth: i.startMonth || '', startYear: i.startYear || '', endMonth: i.endMonth || '', endYear: i.endYear || '' }))); setEditing(false); }} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
        </div>
      </div>
    </SectionCard>
  );
}

/* ───────────────────── PLACEMENT TAB ───────────────────── */

const PlacementTab = React.memo(function PlacementTab({ app, appCtx, batchesCtx, isEnhancedReferrer, currentUserProfile }: { app: Application; appCtx: any; batchesCtx?: any; isEnhancedReferrer?: boolean; currentUserProfile?: any }) {
  // Fine-grained gates for placement fields. Enhanced referrers are not
  // workspace members and keep their legacy read-only placement behavior.
  const canA = (action: string, level: 'view' | 'edit' = 'edit') =>
    isEnhancedReferrer ? true : hasApplicantAction(currentUserProfile, action, level);
  const canEditProgram = canA('applicant.program', 'edit') && canA('applicant.term', 'edit');
  const canEditPartner = canA('applicant.partnerSponsor', 'edit');
  const canEditJobs = canA('applicant.jobs', 'edit');
  const canEditPlacement = !isEnhancedReferrer && (hasApplicantAction(currentUserProfile, 'applicant.placement', 'edit') || canEditModule('applicants', currentUserProfile));
  const canEditBatch = !isEnhancedReferrer && currentUserProfile?.permissions?.batch === 'edit';
  const providers: ServiceProvider[] = appCtx.providers || [];
  // Resolve provider/sponsor from providers list by stored partner ID
  // (single source of truth — if the provider renames, the display updates)
  const selectedProviderId = (app as any).assignedPartner || app.serviceProvider?.providerId || '';
  const selectedProvider = providers.find((p: any) => p.providerId === selectedProviderId) || app.serviceProvider;
  const isApplicationActive = (app as any)?.activeStatus !== false;
  const availablePrograms: any[] = Array.isArray(appCtx.programsList) ? appCtx.programsList : [];
  const applicantId = app.applicationId;
  const isPlacementMutable = app.placementStatus !== 'Confirmed' && app.placementStatus !== 'Approved';

  // Public Batch ID first, then the name — the one label format for
  // every batch dropdown/selector in the portal (D-5): never the
  // Firestore doc id, never the bare name alone.
  const batchPublicLabel = (b: any): string => {
    const displayId = b?.batchId?.startsWith('BAT-')
      ? b.batchId
      : b?.numericId
        ? `BAT-${b.numericId}`
        : b?.batchId || '—';
    const rawName = typeof b?.name === 'string' ? b.name : '';
    return rawName && !rawName.startsWith('BAT-') ? `${displayId} - ${rawName}` : displayId;
  };

  // Batches containing this applicant
  const batchList: any[] = batchesCtx?.batches || [];
  const applicantBatches = useMemo(() =>
    batchList.filter((b: any) => b.applicantIds?.includes(applicantId)),
    [batchList, applicantId]
  );
  const availableBatches = useMemo(() =>
    batchList.filter((b: any) => !b.applicantIds?.includes(applicantId)),
    [batchList, applicantId]
  );
  const [selectedAddBatch, setSelectedAddBatch] = useState('');

  const handleAddToBatch = async () => {
    if (!selectedAddBatch || !batchesCtx?.handleUpdateBatchMembers) return;
    const batch = batchList.find((b: any) => b.batchId === selectedAddBatch);
    if (!batch) return;
    const newIds = [...(batch.applicantIds || []), applicantId];
    await batchesCtx.handleUpdateBatchMembers(selectedAddBatch, newIds, `Added applicant ${applicantId}`);
    setSelectedAddBatch('');
  };

  const handleRemoveFromBatch = async (batchId: string) => {
    if (!batchesCtx?.handleUpdateBatchMembers) return;
    const batch = batchList.find((b: any) => b.batchId === batchId);
    if (!batch) return;
    if (!(await confirmDialog(`Remove this applicant from the batch "${batch.name || 'this batch'}"? The application itself is not changed.`, { confirmLabel: 'Remove' }))) return;
    const newIds = (batch.applicantIds || []).filter((id: string) => id !== applicantId);
    await batchesCtx.handleUpdateBatchMembers(batchId, newIds, `Removed applicant ${applicantId}`);
  };

  return (
    <div className="space-y-6">
      {/* Placements (Primary & Secondary) — multi-entry management.
          Enhanced referrers see the same placement list read-only so they can
          review the primary + secondary jobs of their referred candidates. */}
      <AdminPlacementsPanel app={app} appCtx={appCtx} canEdit={!isEnhancedReferrer && canEditJobs} />

      {/* Program Assignment — view-only for enhanced referrers */}
      <SectionCard title="Program Assignment">
        {!isEnhancedReferrer && appCtx.isEditingProgram ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-caption font-bold text-slate-400 mb-1">Program</p>
                <SearchableSelect
                  value={appCtx.editProgram || app.programId || app.programType || ''}
                  onChange={v => appCtx.setEditProgram(v)}
                  options={availablePrograms.map((p: any) => ({ value: p.id, label: p.name || p.id }))}
                  placeholder="Search programs..."
                  clearLabel="Select a program..."
                  emptyLabel="No programs found"
                  disabled={!isApplicationActive}
                />
              </div>
              <div>
                <p className="text-caption font-bold text-slate-400 mb-1">Pricing Variable</p>
                <select
                  value={appCtx.editPricingVariableId || ''}
                  onChange={e => appCtx.setEditPricingVariableId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-brand-primary outline-none"
                  disabled={!isApplicationActive}
                >
                  <option value="">-- Select Variable --</option>
                  {(() => {
                    const selectedProg = availablePrograms.find((p: any) => p.id === appCtx.editProgram || p.programId === appCtx.editProgram);
                    const vars: any[] = selectedProg?.pricingVariables || [];
                    return vars.filter((v: any) => v.visibility === 'visible').map((v: any) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.code || 'No code'}) — ${Number(v.sellingPrice || 0).toFixed(2)}
                      </option>
                    ));
                  })()}
                </select>
              </div>
              <div>
                <p className="text-caption font-bold text-slate-400 mb-1">Term / Year</p>
                <select value={appCtx.editProgramTerm || ''} onChange={e => appCtx.setEditProgramTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white  focus:ring-2 focus:ring-brand-primary outline-none" disabled={!isApplicationActive}>
                  <option value="">Select term...</option>
                  {(() => {
                    const selectedProg = availablePrograms.find((p: any) => p.id === appCtx.editProgram || p.programId === appCtx.editProgram);
                    const progTerms: any[] = selectedProg?.terms || [];
                    if (progTerms.length > 0) {
                      return progTerms
                        .filter((t: any) => t?.isOpen !== false)
                        .map((t: any) => {
                          const isObj = typeof t === 'object' && t !== null;
                          const value = isObj ? (t.label || t.name || t.termId || '') : t;
                          const label = isObj ? (t.label || t.name || t.termId || '') : t;
                          return <option key={value} value={value}>{label}</option>;
                        });
                    }
                    return ['2026', '2027', '2028', '2029', '2030'].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ));
                  })()}
                </select>
              </div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={() => appCtx.handleSaveProgram({ programType: appCtx.editProgram, pricingVariableId: appCtx.editPricingVariableId, programTerm: appCtx.editProgramTerm })} className="px-4 py-2 btn-primary text-white text-sm font-semibold rounded-xl transition-all cursor-pointer" disabled={!isApplicationActive}>Save</button>
              <button onClick={() => appCtx.setIsEditingProgram(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InfoItem label="Assigned Program" value={(() => { const p = availablePrograms.find((x: any) => x.id === app.programId || x.programId === app.programId); return p?.name || app.programType || app.programPreferences?.programType || app.programId || 'Not assigned'; })()} />
            <InfoItem label="Pricing Variable" value={(app as any).pricingVariableName ? `${(app as any).pricingVariableName} (${(app as any).pricingVariableCode || 'No code'}) — $${Number((app as any).pricingVariableSellingPrice || 0).toFixed(2)}` : '—'} />
            <InfoItem label="Term / Year" value={app.programTerm || app.assignment?.programTerm || app.programPreferences?.programTerm || '—'} />
            {!isEnhancedReferrer && canEditProgram && (
              <button onClick={() => {
                appCtx.setEditProgram(app.programId || app.programType || '');
                appCtx.setEditPricingVariableId((app as any).pricingVariableId || '');
                const currentProg = availablePrograms.find((p: any) => p.id === (app.programId || app.programType) || p.programId === (app.programId || app.programType));
                const storedTerm = app.programTerm || app.assignment?.programTerm || app.programPreferences?.programTerm || '';
                const termMatch = (currentProg?.terms || []).find((t: any) => typeof t === 'object' && t && t.termId === storedTerm);
                appCtx.setEditProgramTerm(termMatch?.label || termMatch?.name || storedTerm);
                appCtx.setIsEditingProgram(true);
              }} className="mt-3 text-caption font-semibold text-brand hover:text-brand cursor-pointer flex items-center space-x-1" disabled={!isApplicationActive}>
                <Edit3 className="h-3 w-3" /><span>Change Program</span>
              </button>
            )}
          </div>
        )}
      </SectionCard>

      {/* Sponsor / Partner Assignment — hidden completely for referrers */}
      {!isEnhancedReferrer && (
      <SectionCard title="Sponsor Assignment">
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <p className="text-caption font-bold text-slate-400 mb-1">Sponsor / Partner</p>
              <SearchableSelect
                value={selectedProviderId}
                onChange={(v) => appCtx.handleAssignProvider?.(v)}
                disabled={!isApplicationActive || isEnhancedReferrer || !canEditPartner}
                options={providers.map((provider) => ({
                  value: provider.providerId,
                  label: provider.name,
                }))}
                placeholder="Search sponsors / partners…"
                clearLabel="Select a sponsor / partner"
                emptyLabel="No sponsors / partners found"
              />
            </div>
            <div className="lg:w-72 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500">Current Sponsor</p>
              <p className="mt-1 text-sm font-bold text-blue-900">{selectedProvider?.name || 'Not assigned'}</p>
            </div>
          </div>

          {(app as any).sponsorIneligible && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 font-medium flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong>Ineligible Sponsor Assignment:</strong> {(app as any).sponsorIneligibleReason || 'Current sponsor is not linked via an active Actual Cost Determination (ACD) to the selected Program and Pricing Variable.'}
              </div>
            </div>
          )}

          {selectedProvider ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
              <InfoItem label="Company" value={selectedProvider.companyName} />
              <InfoItem label="Contact" value={selectedProvider.contactName} />
              <InfoItem label="Email" value={selectedProvider.contactEmail} />
              <InfoItem label="Phone" value={selectedProvider.contactPhone} />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Assign this applicant to a visa sponsor or exchange-program partner from the configured provider list.
            </div>
          )}

          {!isApplicationActive && (
            <p className="text-caption font-semibold text-slate-500">Reactivate this application before changing the sponsor assignment.</p>
          )}
        </div>
      </SectionCard>
      )}

      {/* Host Employer / Assignment — editable for enhanced referrers if placement is pending */}
      <SectionCard title="Host Employer / Assignment" action={
        (!appCtx.isEditingAssignment && (isEnhancedReferrer ? isPlacementMutable : canEditJobs)) ? (
          <button onClick={() => {
            appCtx.setEditAssignmentEmployer(app.assignment?.employerName || '');
            appCtx.setEditAssignmentRole(app.assignment?.roleTitle || '');
            appCtx.setEditAssignmentCity(app.assignment?.city || '');
            appCtx.setEditAssignmentState(app.assignment?.state || '');
            appCtx.setEditAssignmentCountry(app.assignment?.country || 'United States');
            appCtx.setIsEditingAssignment(true);
          }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {appCtx.isEditingAssignment ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Employer Name</p><EditInline value={appCtx.editAssignmentEmployer} onChange={appCtx.setEditAssignmentEmployer} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Role Title</p><EditInline value={appCtx.editAssignmentRole} onChange={appCtx.setEditAssignmentRole} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">City</p><EditInline value={appCtx.editAssignmentCity} onChange={appCtx.setEditAssignmentCity} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">State</p><select value={appCtx.editAssignmentState || ''} onChange={e => appCtx.setEditAssignmentState(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"><option value="">— Select —</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Country</p><CountrySelect value={appCtx.editAssignmentCountry || ''} onChange={value => appCtx.setEditAssignmentCountry(value)} /></div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={appCtx.handleSaveAssignment} className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingAssignment(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Employer" value={app.assignment?.employerName} />
              <InfoItem label="Role" value={app.assignment?.roleTitle} />
              <InfoItem label="City" value={app.assignment?.city} />
              <InfoItem label="State" value={app.assignment?.state} />
              <InfoItem label="Country" value={app.assignment?.country} />
            </div>
          </div>
        )}
      </SectionCard>

      {/* Itinerary — Visa Details */}
      <SectionCard title="Visa Details" action={
        (canEditPlacement && !appCtx.isEditingVisa) ? (
          <button onClick={() => {
            appCtx.setEditVisaNo(app.visaDetails?.visaNumber || app.visaDetails?.visaApplicationNumber || '');
            appCtx.setEditVisaStatus(app.visaDetails?.visaApprovalStatus || 'Pending');
            appCtx.setEditVisaDate(app.visaDetails?.embassyAppointmentDate || '');
            appCtx.setEditVisaLocation(app.visaDetails?.embassyLocation || '');
            appCtx.setIsEditingVisa(true);
          }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {appCtx.isEditingVisa ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Visa Number</p><EditInline value={appCtx.editVisaNo} onChange={appCtx.setEditVisaNo} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Visa Approval Status</p>
                <select value={appCtx.editVisaStatus} onChange={e => appCtx.setEditVisaStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"><option>Pending</option><option>Approved</option><option>Denied</option><option>Rejected</option></select>
              </div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Embassy Appointment Date</p><input type="datetime-local" value={appCtx.editVisaDate} onChange={e => appCtx.setEditVisaDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Embassy Location</p><EditInline value={appCtx.editVisaLocation} onChange={appCtx.setEditVisaLocation} /></div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={appCtx.handleSaveVisaDetails} className="px-4 py-2 btn-primary text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingVisa(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoItem label="Visa Number" value={app.visaDetails?.visaNumber || app.visaDetails?.visaApplicationNumber} />
            <InfoItem label="Visa Type" value={app.visaDetails?.visaType || 'J-1'} />
            <InfoItem label="Issue Date" value={app.visaDetails?.issueDate ? new Date(app.visaDetails.issueDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'} />
            <InfoItem label="Expiration Date" value={app.visaDetails?.expiryDate ? new Date(app.visaDetails.expiryDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'} />
            <InfoItem label="Visa Approval Status" value={app.visaDetails?.visaApprovalStatus} />
            <div className="md:col-span-2">
              <InfoItem label="Conditions &amp; Annotations" value={app.visaDetails?.visaConditions} />
            </div>
          </div>
        )}
      </SectionCard>

      {/* Itinerary — Flight Details */}
      <SectionCard title="Flight Details" action={
        (canEditPlacement && !appCtx.isEditingFlight) ? (
          <button onClick={() => {
            appCtx.setEditFlightAirline(app.flightDetails?.airline || '');
            appCtx.setEditFlightNo(app.flightDetails?.flightNumber || '');
            appCtx.setEditFlightDepDateTime(app.flightDetails?.departureDateTime || '');
            appCtx.setEditFlightArrDateTime(app.flightDetails?.arrivalDateTime || '');
            appCtx.setEditFlightDepAirport(app.flightDetails?.departureAirport || '');
            appCtx.setEditFlightArrAirport(app.flightDetails?.arrivalAirport || '');
            appCtx.setEditFlightNotes(app.flightDetails?.additionalNotes || '');
            appCtx.setIsEditingFlight(true);
          }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {appCtx.isEditingFlight ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Airline</p><EditInline value={appCtx.editFlightAirline} onChange={appCtx.setEditFlightAirline} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Flight Number</p><EditInline value={appCtx.editFlightNo} onChange={appCtx.setEditFlightNo} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Departure Date &amp; Time</p><input type="datetime-local" value={appCtx.editFlightDepDateTime} onChange={e => appCtx.setEditFlightDepDateTime(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Arrival Date &amp; Time</p><input type="datetime-local" value={appCtx.editFlightArrDateTime} onChange={e => appCtx.setEditFlightArrDateTime(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">From Airport</p><EditInline value={appCtx.editFlightDepAirport} onChange={appCtx.setEditFlightDepAirport} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">To Airport</p><EditInline value={appCtx.editFlightArrAirport} onChange={appCtx.setEditFlightArrAirport} /></div>
            </div>
            <div>
              <p className="text-caption font-bold text-slate-400 mb-1">Additional Notes</p>
              <textarea value={appCtx.editFlightNotes} onChange={e => appCtx.setEditFlightNotes(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary" />
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={appCtx.handleSaveFlightDetails} className="px-4 py-2 btn-primary text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingFlight(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Airline" value={app.flightDetails?.airline} />
              <InfoItem label="Flight #" value={app.flightDetails?.flightNumber} />
              <InfoItem label="Departure" value={app.flightDetails?.departureDateTime ? new Date(app.flightDetails.departureDateTime).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
              <InfoItem label="Arrival" value={app.flightDetails?.arrivalDateTime ? new Date(app.flightDetails.arrivalDateTime).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
              <InfoItem label="From" value={app.flightDetails?.departureAirport} />
              <InfoItem label="To" value={app.flightDetails?.arrivalAirport} />
            </div>
            {app.flightDetails?.additionalNotes && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-caption font-bold text-slate-400 mb-1">Notes</p>
                <p className="text-sm text-slate-700">{app.flightDetails.additionalNotes}</p>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Itinerary — Embassy Appointment */}
      <SectionCard title="Embassy Appointment" action={
        (canEditPlacement && !appCtx.isEditingEmbassy) ? (
          <button onClick={() => {
            appCtx.setEditEmbassyDate(app.embassyDetails?.embassyAppointmentDate || '');
            appCtx.setEditEmbassyLocation(app.embassyDetails?.embassyLocation || '');
            appCtx.setEditEmbassyResult(app.embassyDetails?.interviewResult || 'Pending');
            appCtx.setIsEditingEmbassy(true);
          }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {appCtx.isEditingEmbassy ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">Appointment Date &amp; Time</p><input type="datetime-local" value={appCtx.editEmbassyDate} onChange={e => appCtx.setEditEmbassyDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Location</p><EditInline value={appCtx.editEmbassyLocation} onChange={appCtx.setEditEmbassyLocation} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Interview Result</p>
                <select value={appCtx.editEmbassyResult} onChange={e => appCtx.setEditEmbassyResult(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"><option>Pending</option><option>Approved</option><option>Denied</option><option>Rejected</option></select>
              </div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={appCtx.handleSaveEmbassyDetails} className="px-4 py-2 btn-primary text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingEmbassy(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="Date &amp; Time" value={app.embassyDetails?.embassyAppointmentDate ? new Date(app.embassyDetails.embassyAppointmentDate).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
            <InfoItem label="Location" value={app.embassyDetails?.embassyLocation} />
            <InfoItem label="Interview Result" value={app.embassyDetails?.interviewResult} />
          </div>
        )}
      </SectionCard>

      {/* Itinerary — DS-160 Information */}
      <SectionCard title="DS-160 Information" action={
        (canEditPlacement && !appCtx.isEditingDs160) ? (
          <button onClick={() => {
            appCtx.setEditDs160No(app.ds160Details?.ds160Number || app.ds160Details?.ds160ConfirmationNumber || '');
            appCtx.setEditDs160Status(app.ds160Details?.ds160SubmissionStatus || 'Pending');
            appCtx.setIsEditingDs160(true);
          }} className="flex items-center space-x-1 px-3 py-1.5 text-caption font-bold text-brand hover:text-brand bg-blue-50 hover:bg-blue-100 rounded-lg transition-all cursor-pointer">
            <Edit3 className="h-3.5 w-3.5" /><span>Edit</span>
          </button>
        ) : null
      }>
        {appCtx.isEditingDs160 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-caption font-bold text-slate-400 mb-1">DS-160 Number</p><EditInline value={appCtx.editDs160No} onChange={appCtx.setEditDs160No} /></div>
              <div><p className="text-caption font-bold text-slate-400 mb-1">Submission Status</p>
                <select value={appCtx.editDs160Status} onChange={e => appCtx.setEditDs160Status(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"><option>Pending</option><option>Submitted</option><option>Completed</option></select>
              </div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={appCtx.handleSaveDs160Details} className="px-4 py-2 btn-primary text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Save</button>
              <button onClick={() => appCtx.setIsEditingDs160(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="DS-160 Number" value={app.ds160Details?.ds160Number || app.ds160Details?.ds160ConfirmationNumber} />
            <InfoItem label="Submission Status" value={app.ds160Details?.ds160SubmissionStatus} />
          </div>
        )}
      </SectionCard>

      {/* Placement Status — view-only for enhanced referrers */}
      {(() => {
        const placementCount = (Array.isArray((app as any).placements) ? (app as any).placements.length : 0);
        const entries = normalizePlacements((app as any).placements);
        const summary = getPlacementSummary({ placements: entries, placementStatus: app.placementStatus, selfPlacement: (app as any).selfPlacement });
        const effective: string = placementCount > 0 ? summary.currentStatus : (app.placementStatus || 'Pending');
        const placed = effective === 'Placed' || effective === 'Confirmed' || effective === 'Approved';
        return (
      <SectionCard title="Placement Status">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Current Status: <span className={`px-2.5 py-0.5 rounded-full text-caption font-bold uppercase ${placed ? 'bg-emerald-50 text-emerald-700' : effective === 'Cancelled' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-700'}`}>{placementCount > 0 ? effective : (app.placementStatus || 'Pending')}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {placementCount > 0
                ? (effective === 'Cancelled'
                    ? 'No active placement — kept in the placement history above. Add a new placement to re-place this applicant.'
                    : placed
                      ? 'The applicant has a confirmed placement above. Use the placement history section to manage or cancel records.'
                      : 'The applicant has a pending placement above. Approve it from the placement history section to confirm it.')
                : app.placementStatus === 'Confirmed'
                  ? 'Placement is locked. Applicants cannot edit placement details.'
                  : 'Placement is pending. Confirm to lock details for applicants.'}
            </p>
          </div>
          {placementCount === 0 && !isEnhancedReferrer && (
          <div className="flex items-center space-x-2">
            {app.placementStatus === 'Confirmed' ? (
              <button onClick={() => appCtx.handleUpdatePlacementStatus?.('Pending')}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5">
                <Clock className="h-3.5 w-3.5" /><span>Set Pending</span>
              </button>
            ) : (
              <button onClick={() => appCtx.handleUpdatePlacementStatus?.('Confirmed')}
                className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5">
                <CheckCircle className="h-3.5 w-3.5" /><span>Confirm Placement</span>
              </button>
            )}
          </div>
          )}
        </div>
      </SectionCard>
        );
      })()}
      {/* Talent Pool publishing — THE one staff path (admin only).
          Publishing = the talentPoolEnabled flag via
          POST /api/admin/applications/:id/talent-pool. */}
      <SectionCard title="Talent Pool">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {app.talentPoolEnabled === true ? (
                <span className="px-2.5 py-0.5 rounded-full text-caption font-bold uppercase bg-emerald-50 text-emerald-700">In the Talent Pool</span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-caption font-bold uppercase bg-slate-100 text-slate-600">Not in the Talent Pool</span>
              )}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {app.talentPoolEnabled === true
                ? 'Employers can see this applicant in the Talent Pool and invite them to apply for jobs.'
                : 'Employers cannot see this applicant in the Talent Pool.'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {app.talentPoolEnabled === true ? (
              <ConfirmButton
                label={<span className="flex items-center space-x-1.5"><EyeOff className="h-3.5 w-3.5" /><span>Remove from Talent Pool</span></span>}
                confirmLabel="Click again to confirm removal"
                onConfirm={() => appCtx.handleUpdateTalentPoolEnabled?.(false)}
                className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5"
              />
            ) : (
              <button
                onClick={() => {
                  appCtx.handleUpdateTalentPoolEnabled?.(true);
                }}
                className="px-4 py-2 btn-primary  text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center space-x-1.5"
              >
                <Eye className="h-3.5 w-3.5" /><span>Publish to Talent Pool</span>
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 border-t border-slate-100 pt-2">
          Employers only see pool applicants who have <strong>not been placed yet</strong> — once an applicant is placed, they leave the pool automatically.
        </p>
      </SectionCard>

      {/* Batch Membership — hidden for enhanced referrers */}
      {batchesCtx && !isEnhancedReferrer && (
      <SectionCard title="Batch Membership">
        <div className="space-y-3">
          {/* Currently enrolled batches */}
          {applicantBatches.length > 0 ? (
            <div className="space-y-2">
              <p className="text-caption font-bold text-slate-400">Enrolled in {applicantBatches.length} batch{applicantBatches.length > 1 ? 'es' : ''}</p>
              {applicantBatches.map((b: any) => (
                <div key={b.batchId} className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Layers className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-sm font-semibold text-slate-800">{batchPublicLabel(b)}</span>
                    <span className="text-[10px] font-bold uppercase text-slate-400 px-2 py-0.5 bg-white rounded-full">{b.status}</span>
                  </div>
                  {canEditBatch && (
                    <button onClick={() => handleRemoveFromBatch(b.batchId)}
                      className="px-2.5 py-1 text-caption font-bold text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer flex items-center space-x-1">
                      <X className="h-3 w-3" /><span>Remove</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-sm text-slate-400 py-1">
              <Layers className="h-4 w-4" />
              <span>Not enrolled in any batch</span>
            </div>
          )}

          {/* Add to batch */}
          {canEditBatch && availableBatches.length > 0 && (
            <div className="flex items-center space-x-2 pt-1">
              <div className="flex-1">
                <SearchableSelect
                  value={selectedAddBatch}
                  onChange={v => setSelectedAddBatch(v)}
                  options={availableBatches.map((b: any) => ({ value: b.batchId, label: batchPublicLabel(b) }))}
                  placeholder="Search batches…"
                  clearLabel="Add to batch..."
                  emptyLabel="No batches found"
                />
              </div>
              <button onClick={handleAddToBatch} disabled={!selectedAddBatch}
                className="px-3 py-2 btn-primary  disabled:bg-slate-300 text-white text-sm font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1">
                <Plus className="h-3.5 w-3.5" /><span>Add</span>
              </button>
            </div>
          )}
        </div>
      </SectionCard>
      )}

    </div>
  );
});

// Admin multi-entry placements management panel. Injected into ApplicantDetail.tsx
// (the file defines SectionCard / InfoItem / EditInline / Modal in the same scope).

export function AdminPlacementsPanel({ app, appCtx, canEdit }: { app: Application; appCtx: any; canEdit: boolean }) {
  const applicantId = app.applicationId;
  const appAny = app as any;
  const entries = normalizePlacements(appAny.placements);
  const summary = getPlacementSummary({ placements: entries, placementStatus: app.placementStatus, selfPlacement: appAny.selfPlacement });
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showHistoryFor, setShowHistoryFor] = useState<string | null>(null);
  const [statusDrafts, setStatusDrafts] = useState<Record<string, string>>({});
  const [cancelNotes, setCancelNotes] = useState<Record<string, string>>({});
  const [shareFor, setShareFor] = useState<string | null>(null);
  const [sharingBusy, setSharingBusy] = useState(false);
  const isApplicationActive = appAny?.activeStatus !== false;
  const canManage = canEdit && isApplicationActive;

  const toggleShare = (id: string | null) => setShareFor(prev => (prev === id ? null : id));

  const commitPlacements = (nextPlacements: any[], successMsg: string) => {
    const updated: any = { ...app, placements: nextPlacements || [], updatedAt: new Date().toISOString() };
    const placed = (nextPlacements || []).some((e: any) => e.status === 'Placed');
    updated.placementStatus = placed ? 'Confirmed' : app.placementStatus;
    appCtx.setSelectedApp?.(updated);
    appCtx.setApplications?.((list: any[]) => list.map((x: any) => (x.applicationId === applicantId ? updated : x)));
    notifyToast('success', successMsg);
  };

  const runPlacementRequest = async (path: string, options: any, successMsg: string) => {
    setBusy(true);
    try {
      const data = await safeFetch(path, options);
      if (data?.placements) commitPlacements(data.placements, successMsg);
      return data;
    } catch (e: any) {
      console.error('Placement request failed:', e);
      notifyToast('error', e.message || 'Request failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleChangeStatus = async (entry: any) => {
    if (busy) return;
    const next = statusDrafts[entry.placementId] || entry.status;
    if (next === entry.status) {
      if (entry.status === 'Cancelled' && !cancelNotes[entry.placementId]?.trim()) return;
      if (entry.status !== 'Cancelled') return;
    }
    if (next === 'Cancelled' && entry.status !== 'Cancelled' && !cancelNotes[entry.placementId]?.trim() && !(await confirmDialog('Mark this placement as Cancelled without a reason note?'))) return;
    const payload: any = { status: next };
    if (next === 'Cancelled') payload.reason = cancelNotes[entry.placementId]?.trim() || undefined;
    await runPlacementRequest(`/api/applications/${applicantId}/placements/${entry.placementId}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, next === 'Cancelled' ? 'Placement marked as Cancelled.' : `Placement status changed to ${next}.`);
  };

  const handleSetPrimary = async (entry: any) => {
    if (busy) return;
    if (!(await confirmDialog(`Make ${entry.companyName} — ${entry.jobTitle} the Primary Placement? Any other active primary will become a Secondary Job.`))) return;
    await runPlacementRequest(`/api/applications/${applicantId}/placements/${entry.placementId}/primary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }, 'Primary placement updated.');
  };

  const handleRemove = async (entry: any) => {
    if (busy) return;
    const extra = entry.status === 'Placed' || entry.status === 'Cancelled'
      ? '\n\nThis will permanently remove the record for everyone — it will no longer appear in the applicant\'s history.'
      : '\n\nThis permanently removes the record and cannot be undone.';
    if (!(await confirmDialog(`Delete the placement record for ${entry.companyName}${entry.jobTitle ? ` (${entry.jobTitle})` : ''}?${extra}`))) return;
    await runPlacementRequest(`/api/applications/${applicantId}/placements/${entry.placementId}`, { method: 'DELETE' }, 'Placement record deleted.');
  };

  const handleDownloadPlacementPdf = async (entry: any) => {
    if (sharingBusy) return;
    setSharingBusy(true);
    try {
      const blob = await downloadPdf(`/api/applications/${applicantId}/placements/${entry.placementId}/pdf`);
      triggerBlobDownload(blob, buildApplicantPdfFileName('PlacementDetails', app.applicationId || applicantId, getSafeApplicantName(app)));
      notifyToast('success', 'Placement PDF downloaded.');
      setShareFor(null);
    } catch (e: any) {
      console.error('Placement PDF download failed:', e);
      notifyToast('error', e?.message || 'Could not download the placement PDF.');
    } finally {
      setSharingBusy(false);
    }
  };

  const handleEmailPlacement = async (entry: any) => {
    if (sharingBusy) return;
    if (!(await confirmDialog(`Email the placement details for ${entry.companyName}${entry.jobTitle ? ` — ${entry.jobTitle}` : ''} to the applicant?`))) return;
    setSharingBusy(true);
    try {
      const data = await safeFetch(`/api/applications/${applicantId}/placements/${entry.placementId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      notifyToast('success', `Placement details emailed to ${data?.emailedTo || 'the applicant'}.`);
      setShareFor(null);
    } catch (e: any) {
      console.error('Placement email failed:', e);
      notifyToast('error', e?.message || 'Failed to email placement details.');
    } finally {
      setSharingBusy(false);
    }
  };

  const sorted = sortPlacementsForDisplay(entries);

  return (
    <SectionCard
      title={`Placements — ${entries.length === 0 ? 'No Records' : `${entries.length} Record${entries.length === 1 ? '' : 's'}`}`}
      action={canManage ? (
        <button onClick={() => setShowAdd(true)} disabled={busy}
          className="group inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 px-3 py-1.5 text-[11px] font-bold text-white shadow-md shadow-emerald-600/20 transition-all duration-200 hover:from-emerald-600 hover:to-emerald-800 hover:shadow-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-50">
          <Plus className="h-3 w-3 transition-transform duration-200 group-hover:rotate-90" />
          <span>Add Placement</span>
        </button>
      ) : undefined}
    >
      {!isApplicationActive && (
        <div className="mb-2.5 flex items-center gap-2 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-[11px] font-semibold text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Reactivate this application before managing placements.
        </div>
      )}

      {/* Summary bar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Status</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${
            summary.currentStatus === 'Placed'
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200'
              : summary.currentStatus === 'Cancelled'
                ? 'bg-rose-100 text-rose-600 ring-rose-200'
                : summary.currentStatus === 'Pending Approval'
                  ? 'bg-amber-100 text-amber-700 ring-amber-200'
                  : 'bg-slate-100 text-slate-600 ring-slate-200'
          }`}>
            {summary.currentStatus === 'Placed' && <CheckCircle className="h-2.5 w-2.5" />}
            {summary.currentStatus === 'Pending Approval' && <Clock className="h-2.5 w-2.5" />}
            {summary.currentStatus}
          </span>
        </div>
        {summary.activePrimary ? (
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-slate-500">
            <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />
            <span className="shrink-0 text-slate-400">Primary:</span>
            <strong className="truncate text-slate-700">{summary.activePrimary.companyName}</strong>
            {summary.activePrimary.jobTitle && (
              <>
                <span className="text-slate-300">·</span>
                <span className="truncate text-slate-500">{summary.activePrimary.jobTitle}</span>
              </>
            )}
          </div>
        ) : (
          <span className="text-[11px] italic text-slate-400">No active primary placement</span>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-6 py-6 text-center">
          <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-100">
            <Briefcase className="h-3.5 w-3.5 text-slate-300" />
          </div>
          <p className="text-xs font-bold text-slate-500">
            {canManage ? 'No placement records yet' : 'No placement records for this applicant'}
          </p>
          <p className="mt-0.5 max-w-xs text-[11px] leading-relaxed text-slate-400">
            {canManage
              ? 'Use “Add Placement” to link an existing portal job or add the employer/company details manually.'
              : 'Placement records added for this applicant will appear here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((entry) => {
            const isPrimaryType = entry.type === 'primary';
            const activeNow = isPlacementActive(entry);
            const isActivePrimary = isPrimaryType && activeNow;
            const draftStatus = statusDrafts[entry.placementId] ?? entry.status;
            const showCancelNote = draftStatus === 'Cancelled' && entry.status !== 'Cancelled';
            const timeline = entry.history || [];
            const locationText = [entry.city, entry.state, entry.country].filter(Boolean).join(', ');
            const isCancelled = entry.status === 'Cancelled';

            const infoPills: { label: string; value: any; icon: any }[] = [];
            if (entry.startDate) infoPills.push({ label: 'Placement Date', value: entry.startDate, icon: CalendarDays });
            if (entry.payRate) infoPills.push({ label: 'Pay', value: entry.payRate, icon: DollarSign });
            if (entry.hoursPerWeek) infoPills.push({ label: 'Hours / Week', value: entry.hoursPerWeek, icon: Clock });
            if (locationText) infoPills.push({ label: 'Location', value: locationText, icon: MapPin });
            if (entry.supervisorName) infoPills.push({ label: 'Supervisor', value: entry.supervisorName, icon: User });
            if (entry.contactName) infoPills.push({ label: 'Contact', value: entry.contactName, icon: User });
            if (isCancelled && entry.cancelledAt) infoPills.push({ label: 'Cancelled On', value: new Date(entry.cancelledAt).toLocaleDateString(), icon: CalendarDays });

            const sourceMeta = [
              entry.source === 'self' || entry.createdByRole === 'applicant' ? 'Self-reported' : entry.createdByRole === 'admin' ? 'Added by team' : entry.createdByRole === 'employer' ? 'Added by employer' : '',
              entry.createdByName ? String(entry.createdByName) : '',
              entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : '',
            ].filter(Boolean);

            const cardCls = isActivePrimary
              ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/70 via-white to-white hover:border-emerald-300'
              : isCancelled
                ? 'border-slate-200/90 bg-slate-50/70 hover:border-slate-300'
                : 'border-slate-200 bg-white/80 hover:border-blue-300/80';

            const barCls = isActivePrimary
              ? 'from-emerald-400 to-teal-500'
              : isCancelled
                ? 'from-slate-300 to-slate-200'
                : 'from-blue-400 to-sky-500';

            const avatarCls = isActivePrimary
              ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm shadow-emerald-500/20'
              : entry.type === 'primary'
                ? 'bg-gradient-to-br from-slate-100 to-slate-50 text-slate-400 ring-1 ring-slate-200'
                : 'bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-500 ring-1 ring-blue-100';

            const statusCls = entry.status === 'Placed'
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : isCancelled
                ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-200'
                : entry.status === 'Pending Approval'
                  ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                  : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';

            return (
              <div key={entry.placementId}
                className={`group relative rounded-xl border shadow-sm transition-all duration-150 hover:shadow-md hover:shadow-slate-900/5 ${cardCls}`}>
                <div className={`pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b ${barCls}`} />

                <div className="flex flex-col gap-2 p-2.5 pl-4 sm:p-3 sm:pl-5">
                  {/* Compact header */}
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                    <div className="flex min-w-0 items-start gap-2">
                      <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${avatarCls}`}>
                        {isActivePrimary ? <Star className="h-3 w-3 fill-current" /> : entry.type === 'primary' ? <Star className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1">
                          {isActivePrimary ? (
                            <span className="inline-flex items-center gap-0.5 rounded bg-success px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-white">
                              <Star className="h-2.5 w-2.5 fill-current" />Primary
                            </span>
                          ) : entry.type === 'primary' ? (
                            <span className="rounded bg-emerald-100 px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200/60">Former Primary</span>
                          ) : (
                            <span className="rounded bg-slate-200/80 px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-slate-600">Secondary Job</span>
                          )}
                          <span className={`rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${statusCls}`}>{entry.status}</span>
                        </div>
                        <h4 className="mt-0.5 truncate text-sm font-extrabold tracking-tight text-slate-900">{entry.companyName}</h4>
                        {entry.jobTitle && <p className="truncate text-xs font-medium text-slate-500">{entry.jobTitle}</p>}
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        {entry.source === 'portal-job' && entry.jobId && (
                          <span className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-indigo-600 ring-1 ring-indigo-100">
                            <Link2 className="h-2.5 w-2.5" />Portal Job
                          </span>
                        )}
                        {canManage && (
                          <>
                            <button type="button" onClick={() => toggleShare(entry.placementId)} disabled={sharingBusy}
                              title="Share — download as PDF or email details to the applicant"
                              className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-bold text-slate-500 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
                              <Share2 className="h-2.5 w-2.5" />Share
                            </button>
                            {!isCancelled && (
                              <button type="button" onClick={() => setEditingId(entry.placementId)} disabled={busy}
                                title="Edit placement details"
                                className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-bold text-slate-500 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50">
                                <Edit3 className="h-2.5 w-2.5" />Edit
                              </button>
                            )}
                            <button type="button" onClick={() => handleRemove(entry)} disabled={busy}
                              title="Delete this placement record permanently"
                              className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-bold text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50">
                              <Trash2 className="h-2.5 w-2.5" />Delete
                            </button>
                          </>
                        )}
                      </div>
                      {sourceMeta.length > 0 && (
                        <span className="text-[11px] font-semibold text-slate-400">{sourceMeta.join(' · ')}</span>
                      )}
                    </div>
                  </div>

                  {/* Inline share drawer */}
                  {canManage && shareFor === entry.placementId && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-600">Share placement details</span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button type="button" onClick={() => handleDownloadPlacementPdf(entry)} disabled={sharingBusy}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-[11px] font-bold text-indigo-700 shadow-sm ring-1 ring-indigo-200 transition hover:bg-indigo-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">
                          <Download className="h-3 w-3" />{sharingBusy ? 'Working…' : 'Download PDF'}
                        </button>
                        <button type="button" onClick={() => handleEmailPlacement(entry)} disabled={sharingBusy}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-[11px] font-bold text-indigo-700 shadow-sm ring-1 ring-indigo-200 transition hover:bg-indigo-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">
                          <Mail className="h-3 w-3" />Email Applicant
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Slim detail grid */}
                  {infoPills.length > 0 && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
                      {infoPills.map((d, i) => (
                        <div key={i} className="flex min-w-0 items-start gap-1.5">
                          <d.icon className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
                          <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{d.label}</p>
                            <p className="truncate text-xs font-semibold text-slate-600">{d.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Housing arrangements — recorded by the applicant or by the
                      team; once confirmed, only admins may change them. */}
                  {(entry.housing || canManage) && (
                    <HousingArrangementEditor
                      applicationId={applicantId}
                      entry={entry}
                      canEdit={canManage && entry.status !== 'Cancelled'}
                      dense
                      onUpdated={(next) => commitPlacements(next, 'Housing details updated.')}
                    />
                  )}

                  {/* Notes + cancellation reason (slim) */}
                  {(entry.notes || entry.placementNotes) && (
                    <div className="flex items-start gap-2 rounded-lg border border-slate-100 bg-white/70 px-2.5 py-2">
                      <FileText className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
                      <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-slate-600">{entry.placementNotes || entry.notes}</p>
                    </div>
                  )}

                  {isCancelled && entry.cancelReason && (
                    <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50/80 px-2.5 py-2">
                      <Ban className="mt-0.5 h-3 w-3 shrink-0 text-rose-400" />
                      <p className="text-[11px] leading-relaxed text-rose-700"><strong>Cancellation reason:</strong> {entry.cancelReason}</p>
                    </div>
                  )}

                  {/* Status history (collapsible, slim) */}
                  {timeline.length > 1 && (
                    <div className="rounded-lg border border-slate-100 bg-slate-50/40">
                      <button type="button"
                        onClick={() => setShowHistoryFor(showHistoryFor === entry.placementId ? null : entry.placementId)}
                        className="flex w-full cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 text-left transition hover:bg-slate-50">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                          <History className="h-3 w-3 text-slate-400" />Status history
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400">
                          {timeline.length} event{timeline.length === 1 ? '' : 's'}
                          {showHistoryFor === entry.placementId ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </span>
                      </button>
                      {showHistoryFor === entry.placementId && (
                        <ol className="space-y-2 px-3 pb-2.5 pt-0.5">
                          {timeline.map((h: any, i: number) => (
                            <li key={i} className="relative flex items-start gap-2 pl-0.5">
                              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ring-2 ${i === timeline.length - 1 ? 'bg-emerald-500 ring-emerald-100' : 'bg-slate-300 ring-slate-100'}`} />
                              <div className="min-w-0">
                                <p className="text-[11px] text-slate-600">
                                  <span className="font-bold uppercase tracking-wide text-slate-700">{h.status}</span>
                                  {h.at ? <span className="text-slate-400"> · {new Date(h.at).toLocaleString()}</span> : null}
                                </p>
                                {h.note && <p className="mt-0.5 text-[10px] leading-relaxed text-slate-500">“{h.note}”</p>}
                                {h.by && <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{h.by}</p>}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}

                  {/* Slim management strip */}
                  {canManage && (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isActivePrimary && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200/60">
                            <CheckCircle className="h-2.5 w-2.5" />Current Primary
                          </span>
                        )}
                        {isCancelled && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-600 ring-1 ring-rose-200/60">
                            <Ban className="h-2.5 w-2.5" />Cancelled
                          </span>
                        )}
                        {!isCancelled && !isActivePrimary && (
                          <button type="button" onClick={() => handleSetPrimary(entry)} disabled={busy}
                            className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-emerald-200 bg-white px-2 py-1 text-[10px] font-bold text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50">
                            <Star className="h-2.5 w-2.5" />Set as Primary
                          </button>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        {showCancelNote && (
                          <input
                            value={cancelNotes[entry.placementId] || ''}
                            onChange={(e) => setCancelNotes(prev => ({ ...prev, [entry.placementId]: e.target.value }))}
                            placeholder="Cancellation reason"
                            className="w-40 rounded-md border border-rose-200 bg-white px-2 py-1 text-[11px] outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                          />
                        )}
                        {entry.status === 'Pending Approval' ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                runPlacementRequest(`/api/applications/${applicantId}/placements/${entry.placementId}/status`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'Placed', note: 'Approved by admin' }),
                                }, 'Placement approved and marked as Placed.');
                              }}
                              disabled={busy}
                              className="cursor-pointer rounded-md bg-success px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-40"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const reason = await promptDialog('Optional rejection reason for applicant (leave blank for none):');
                                if (reason === null) return;
                                runPlacementRequest(`/api/applications/${applicantId}/placements/${entry.placementId}/status`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: 'Rejected', reason: reason.trim() || undefined }),
                                }, 'Placement request rejected.');
                              }}
                              disabled={busy}
                              className="cursor-pointer rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-40"
                            >
                              Reject
                            </button>
                          </>
                        ) : null}
                        <select value={draftStatus}
                          onChange={(e) => setStatusDrafts(prev => ({ ...prev, [entry.placementId]: e.target.value }))}
                          className="cursor-pointer rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-700 shadow-sm outline-none transition focus:border-slate-400">
                          <option value="Pending Approval">Pending Approval</option>
                          <option value="Pending Placement">Pending Placement</option>
                          <option value="Placed">Placed</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                        <button type="button" onClick={() => handleChangeStatus(entry)}
                          disabled={busy || (draftStatus === entry.status && (entry.status !== 'Cancelled' || !cancelNotes[entry.placementId]?.trim()))}
                          className="cursor-pointer rounded-md bg-brand px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:bg-brand-active disabled:cursor-not-allowed disabled:opacity-40">
                          Save Status
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AdminPlacementAddModal app={app} busy={busy} onClose={() => setShowAdd(false)} onSubmit={(path, body) => runPlacementRequest(path, body, 'Placement added.')} />}
      {editingId && <AdminPlacementEditModal app={app} placement={entries.find((e: any) => e.placementId === editingId) || null} busy={busy} onClose={() => setEditingId(null)} onSubmit={(path, body) => runPlacementRequest(path, body, 'Placement details updated.')} />}
    </SectionCard>
  );
}

/* ── Shared presentational helpers for the placement modals ── */
function PModalShell({ icon: Icon, accent, title, subtitle, onClose, children, footer }: {
  icon: any; accent: string; title: string; subtitle?: string;
  onClose: () => void; children: React.ReactNode; footer: React.ReactNode;
}) {
  return (
    <Modal onClose={onClose} ariaLabel={title}>
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10">
        <div className="max-h-[85vh] overflow-y-auto overscroll-contain bg-white">
          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 rounded-t-2xl border-b border-slate-100 bg-white/95 px-6 py-4 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-lg ${accent}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-900">{title}</h3>
                {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-6 py-5">{children}</div>
          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2.5 rounded-b-2xl border-t border-slate-100 bg-white/95 px-6 py-4 backdrop-blur-md">
            {footer}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function PFormSection({ icon: Icon, title, hint, children }: {
  icon: any; title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-3.5 flex items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div>
          <h4 className="text-[13px] font-bold text-slate-800">{title}</h4>
          {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function PFormField({ label, required, children }: {
  label: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}{required && <span className="ml-0.5 text-rose-500">*</span>}
      </p>
      {children}
    </div>
  );
}

const placementFieldCls = 'w-full rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand-primary/60';

/* ── Admin: Add placement (search existing portal job or manual entry) ── */

function AdminPlacementAddModal({ app, busy, onClose, onSubmit }: {
  app: Application; busy: boolean;
  onClose: () => void; onSubmit: (path: string, body: any) => Promise<any>;
}) {
  const applicantId = app.applicationId;
  const entries = normalizePlacements((app as any).placements);
  const hasPrimary = hasActivePrimaryPlacement(entries);
  const [mode, setMode] = useState<'search' | 'manual'>('search');
  const [jobs, setJobs] = useState<any[] | null>(null);
  const [query, setQuery] = useState('');
  const [chosenJob, setChosenJob] = useState<any | null>(null);
  const [form, setForm] = useState<any>({ country: 'United States' });
  const [type, setType] = useState<'primary' | 'secondary'>(hasPrimary ? 'secondary' : 'primary');
  const [saving, setSaving] = useState(false);
  const set = (k: string) => (v: string) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (mode === 'search' && jobs === null) {
      safeFetch('/api/jobs').then((d: any) => {
        const list: any[] = Array.isArray(d?.jobs) ? d.jobs : Array.isArray(d) ? d : [];
        setJobs(list);
      }).catch(() => setJobs([]));
    }
  }, [mode, jobs]);

  const jobResults = (jobs || []).filter((j: any) => {
    const q = query.toLowerCase();
    if (!q) return false;
    const company = String(j.employerName || j.employer?.companyName || '').toLowerCase();
    const title = String(j.title || '').toLowerCase();
    const city = String(j.city || j.locationCity || '').toLowerCase();
    return company.includes(q) || title.includes(q) || city.includes(q);
  }).slice(0, 12);

  const chooseJob = (job: any) => {
    setChosenJob(job);
    const company = job.employerName || job.employer?.companyName || '';
    const title = job.title || '';
    let city = job.city || job.locationCity || '';
    let state = job.state || '';
    let country = job.country || 'United States';
    const loc = String(job.location || '');
    if (loc && loc.includes(',')) {
      const parts = loc.split(',').map((s: string) => s.trim());
      city = city || parts[0] || '';
      state = state || parts.slice(1).join(', ') || '';
    }
    setForm((f: any) => ({ ...f, companyName: company, jobTitle: title, city, state, country }));
    setMode('manual');
  };

  const submit = async () => {
    if (saving) return;
    if (!form.companyName?.trim() || !form.jobTitle?.trim()) {
      notifyToast('error', 'Employer/company and job title are required.');
      return;
    }
    const jobId = chosenJob?.id || chosenJob?.jobId || '';
    const payload: any = {
      sourceJobId: jobId || undefined,
      type,
      placement: {
        companyName: form.companyName?.trim(),
        jobTitle: form.jobTitle?.trim(),
        city: form.city?.trim(),
        state: form.state?.trim(),
        country: form.country?.trim(),
        startDate: form.startDate,
        payRate: form.payRate?.trim(),
        hoursPerWeek: form.hoursPerWeek?.trim(),
        supervisorName: form.supervisorName?.trim(),
        supervisorEmail: form.supervisorEmail?.trim(),
        contactName: form.contactName?.trim(),
        contactEmail: form.contactEmail?.trim(),
        notes: form.notes?.trim(),
        placementNotes: form.placementNotes?.trim(),
      },
    };
    // Remove empty optional fields to avoid storing undefined in Firestore
    if (!payload.placement.supervisorEmail) delete payload.placement.supervisorEmail;
    if (!payload.placement.supervisorName) delete payload.placement.supervisorName;
    if (!payload.placement.contactEmail) delete payload.placement.contactEmail;
    if (!payload.placement.contactName) delete payload.placement.contactName;
    setSaving(true);
    try {
      const ok = await onSubmit(`/api/applications/${applicantId}/placements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <PModalShell
      icon={Briefcase}
      accent="bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25"
      title="Add Placement"
      subtitle="Link an existing portal job or enter the placement details manually"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}
            className="cursor-pointer rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving || busy}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 transition-all hover:-translate-y-px hover:from-emerald-600 hover:to-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Add Placement'}
          </button>
        </>
      }
    >
      {/* Context hint */}
      <div className={`mb-5 flex items-start gap-3 rounded-2xl border p-4 ${hasPrimary ? 'border-blue-200/70 bg-blue-50/40' : 'border-emerald-200/70 bg-emerald-50/40'}`}>
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm ${hasPrimary ? 'bg-brand-primary' : 'bg-emerald-500'}`}>
          <AlertCircle className="h-4 w-4" />
        </div>
        <div>
          <p className="text-[13px] font-bold text-slate-800">{hasPrimary ? 'This applicant already has an active Primary Placement' : 'This applicant has no active Primary Placement'}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
            {hasPrimary
              ? 'New placements are added as Secondary Jobs unless you designate the new record as the Primary Placement.'
              : 'The new placement record should be the Primary Placement.'}
          </p>
        </div>
      </div>

      {/* Record type */}
      <div className="mb-5">
        <PFormSection icon={Layers} title="Placement Type" hint="Choose how this record should be classified">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => setType('secondary')}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all ${type === 'secondary' ? 'border-brand bg-slate-50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60'}`}>
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${type === 'secondary' ? 'border-brand bg-brand' : 'border-slate-300 bg-white'}`}>
                {type === 'secondary' && <Check className="h-3 w-3 text-white" />}
              </span>
              <span>
                <span className="block text-sm font-bold text-slate-800">Secondary Job</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">An additional job placed alongside the applicant's current primary placement.</span>
              </span>
            </button>
            <button type="button"
              onClick={async () => { if (hasPrimary && !(await confirmDialog('Designating a new Primary Placement will demote the current active primary to Secondary.'))) return; setType('primary'); }}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all ${type === 'primary' ? 'border-emerald-500 bg-emerald-50/60 shadow-md shadow-emerald-500/10' : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30'}`}>
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${type === 'primary' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'}`}>
                {type === 'primary' && <Check className="h-3 w-3 text-white" />}
              </span>
              <span>
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
                  <Star className={`h-3.5 w-3.5 ${type === 'primary' ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />Primary Placement
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{hasPrimary ? 'Replaces the current primary — it will be demoted to a Secondary Job.' : 'No primary yet — this becomes the primary placement.'}</span>
              </span>
            </button>
          </div>
        </PFormSection>
      </div>

      {/* Source */}
      <PFormSection icon={Search} title="Placement Source" hint="Search the portal job board or enter details manually">
        <div className="mb-4 inline-flex rounded-xl bg-slate-100 p-1">
          <button type="button" onClick={() => { setMode('search'); setChosenJob(null); }}
            className={`rounded-lg px-4 py-2 text-xs font-bold transition-all cursor-pointer ${mode === 'search' && !chosenJob ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            Search Portal Jobs
          </button>
          <button type="button" onClick={() => { setMode('manual'); setChosenJob(null); }}
            className={`rounded-lg px-4 py-2 text-xs font-bold transition-all cursor-pointer ${mode === 'manual' || chosenJob ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            Manual Entry
          </button>
        </div>

        {mode === 'search' && !chosenJob ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search jobs by employer, title or city..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50/60 py-2.5 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand-primary/60"
              />
            </div>
            {jobs === null ? (
              <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 py-6 text-xs font-semibold text-slate-400">
                <Loader className="h-4 w-4 animate-spin" /> Loading available jobs…
              </div>
            ) : jobs?.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-center text-xs text-slate-500">
                No active portal jobs are available. Use <strong>Manual Entry</strong> instead.
              </div>
            ) : query.trim().length === 0 ? (
              <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-4 py-6 text-center text-xs text-slate-500">
                <Search className="h-4 w-4" /> Start typing to search existing jobs on the portal…
              </div>
            ) : jobResults.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-center text-xs text-slate-500">
                No matches found. Use <strong>Manual Entry</strong> to add the placement details directly.
              </div>
            ) : (
              <div className="max-h-60 space-y-2 overflow-y-auto pr-1">
                {jobResults.map((job) => {
                  const jCompany = String(job.employerName || job.employer?.companyName || 'Employer');
                  const jTitle = String(job.title || '');
                  const jLoc = String(job.city || job.locationCity || job.location || '');
                  return (
                    <button key={job.id || job.jobId} type="button" onClick={() => chooseJob(job)}
                      className="group/job flex w-full cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow-md">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-100 to-sky-100 text-sm font-black text-indigo-500 ring-1 ring-indigo-100">
                        {(jCompany.charAt(0) || 'E').toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-slate-800">{jCompany}</span>
                        {jTitle && <span className="block truncate text-xs font-medium text-slate-500">{jTitle}</span>}
                        {jLoc && <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500"><MapPin className="h-3 w-3" />{jLoc}</span>}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-indigo-600 transition group-hover/job:bg-brand-primary group-hover/job:text-white">
                        <Link2 className="h-3 w-3" />Select
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {chosenJob && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-indigo-200/80 bg-indigo-50/60 px-3.5 py-2.5">
                <span className="inline-flex min-w-0 items-center gap-2 text-xs font-semibold text-indigo-700">
                  <Link2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Linked to portal job: {chosenJob.employerName || chosenJob.employer?.companyName} — {chosenJob.title}</span>
                </span>
                <button type="button" onClick={() => setChosenJob(null)}
                  className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-indigo-400 transition hover:bg-indigo-100 hover:text-indigo-700">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* Company & position */}
            <PFormSection icon={Briefcase} title="Company & Position">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PFormField label="Employer / Company" required>
                  <input value={form.companyName || ''} onChange={(e) => set('companyName')(e.target.value)} placeholder="e.g. Host Company Inc." className={placementFieldCls} />
                </PFormField>
                <PFormField label="Job / Position" required>
                  <input value={form.jobTitle || ''} onChange={(e) => set('jobTitle')(e.target.value)} placeholder="e.g. Guest Services Associate" className={placementFieldCls} />
                </PFormField>
              </div>
            </PFormSection>

            {/* Location & schedule */}
            <PFormSection icon={MapPin} title="Location & Schedule">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <PFormField label="City">
                  <input value={form.city || ''} onChange={(e) => set('city')(e.target.value)} placeholder="e.g. Orlando" className={placementFieldCls} />
                </PFormField>
                <PFormField label="State">
                  <select value={form.state || ''} onChange={(e) => set('state')(e.target.value)} className={`${placementFieldCls} cursor-pointer`}>
                    <option value="">— Select —</option>
                    {US_STATES.map((st: string) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </PFormField>
                <PFormField label="Country">
                  <CountrySelect value={form.country || 'United States'} onChange={set('country')} />
                </PFormField>
                <PFormField label="Placement Date">
                  <input type="date" value={form.startDate || ''} onChange={(e) => set('startDate')(e.target.value)} className={placementFieldCls} />
                </PFormField>
              </div>
            </PFormSection>

            {/* Compensation */}
            <PFormSection icon={DollarSign} title="Compensation">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PFormField label="Pay Rate">
                  <input value={form.payRate || ''} onChange={(e) => set('payRate')(e.target.value)} placeholder="e.g. $16/hour" className={placementFieldCls} />
                </PFormField>
                <PFormField label="Hours / Week">
                  <input value={form.hoursPerWeek || ''} onChange={(e) => set('hoursPerWeek')(e.target.value)} placeholder="e.g. 40" className={placementFieldCls} />
                </PFormField>
              </div>
            </PFormSection>

            {/* Contacts */}
            <PFormSection icon={User} title="On-site Contacts">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PFormField label="Supervisor Name">
                  <input value={form.supervisorName || ''} onChange={(e) => set('supervisorName')(e.target.value)} className={placementFieldCls} />
                </PFormField>
                <PFormField label="Supervisor Email">
                  <input type="email" value={form.supervisorEmail || ''} onChange={(e) => set('supervisorEmail')(e.target.value)} placeholder="supervisor@company.com" className={placementFieldCls} />
                </PFormField>
                <PFormField label="Contact Name">
                  <input value={form.contactName || ''} onChange={(e) => set('contactName')(e.target.value)} className={placementFieldCls} />
                </PFormField>
                <PFormField label="Contact Email">
                  <input type="email" value={form.contactEmail || ''} onChange={(e) => set('contactEmail')(e.target.value)} placeholder="contact@company.com" className={placementFieldCls} />
                </PFormField>
              </div>
            </PFormSection>

            {/* Notes */}
            <PFormSection icon={FileText} title="Notes">
              <div className="space-y-3">
                <PFormField label="Notes">
                  <textarea value={form.notes || ''} onChange={(e) => set('notes')(e.target.value)} rows={2} placeholder="Visible to the applicant" className={`${placementFieldCls} resize-y`} />
                </PFormField>
                <PFormField label="Placement Notes (team only)">
                  <textarea value={form.placementNotes || ''} onChange={(e) => set('placementNotes')(e.target.value)} rows={2} placeholder="Internal notes for your team" className={`${placementFieldCls} resize-y`} />
                </PFormField>
              </div>
            </PFormSection>
          </div>
        )}
      </PFormSection>
    </PModalShell>
  );
}

/* ── Admin: Edit a placement's details ── */
function AdminPlacementEditModal({ app, placement, busy, onClose, onSubmit }: {
  app: Application; placement: any | null; busy: boolean;
  onClose: () => void; onSubmit: (path: string, body: any) => Promise<any>;
}) {
  const applicantId = app.applicationId;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any | null>(null);
  useEffect(() => {
    if (placement) {
      setForm({
        companyName: placement.companyName || '',
        jobTitle: placement.jobTitle || '',
        city: placement.city || '',
        state: placement.state || '',
        country: placement.country || 'United States',
        startDate: placement.startDate || '',
        payRate: placement.payRate || '',
        hoursPerWeek: placement.hoursPerWeek || '',
        supervisorName: placement.supervisorName || '',
        supervisorEmail: placement.supervisorEmail || '',
        contactName: placement.contactName || '',
        contactEmail: placement.contactEmail || '',
        notes: placement.notes || '',
        placementNotes: placement.placementNotes || '',
      });
    }
  }, [placement]);
  if (!placement) return null;
  const set = (k: string) => (v: string) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (saving || !form) return;
    if (!form.companyName?.trim() || !form.jobTitle?.trim()) {
      notifyToast('error', 'Employer/company and job title are required.');
      return;
    }
    // Remove empty optional fields to avoid storing undefined in Firestore
    if (!form.supervisorEmail) delete form.supervisorEmail;
    if (!form.supervisorName) delete form.supervisorName;
    if (!form.contactEmail) delete form.contactEmail;
    if (!form.contactName) delete form.contactName;
    setSaving(true);
    try {
      const ok = await onSubmit(`/api/applications/${applicantId}/placements/${placement.placementId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement: form }),
      });
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <PModalShell
      icon={Edit3}
      accent="bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/25"
      title="Edit Placement"
      subtitle="Update the placement details for this record"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}
            className="cursor-pointer rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving || busy}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-px hover:from-blue-600 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      {/* Current record summary */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3 shadow-sm">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-100">
          <Briefcase className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold text-slate-900">{placement.companyName}</p>
          {placement.jobTitle && <p className="truncate text-xs font-semibold text-slate-500">{placement.jobTitle}</p>}
        </div>
        <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${
          placement.status === 'Placed'
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            : placement.status === 'Cancelled'
              ? 'bg-rose-50 text-rose-600 ring-rose-200'
              : placement.status === 'Pending Approval'
                ? 'bg-amber-50 text-amber-700 ring-amber-200'
                : 'bg-blue-50 text-blue-700 ring-blue-200'
        }`}>{placement.status}</span>
      </div>

      <div className="space-y-5">
        {/* Company & position */}
        <PFormSection icon={Briefcase} title="Company & Position">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <PFormField label="Employer / Company" required>
              <input value={form?.companyName || ''} onChange={(e) => set('companyName')(e.target.value)} placeholder="e.g. Host Company Inc." className={placementFieldCls} />
            </PFormField>
            <PFormField label="Job / Position" required>
              <input value={form?.jobTitle || ''} onChange={(e) => set('jobTitle')(e.target.value)} placeholder="e.g. Guest Services Associate" className={placementFieldCls} />
            </PFormField>
          </div>
        </PFormSection>

        {/* Location & schedule */}
        <PFormSection icon={MapPin} title="Location & Schedule">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <PFormField label="City">
              <input value={form?.city || ''} onChange={(e) => set('city')(e.target.value)} placeholder="e.g. Orlando" className={placementFieldCls} />
            </PFormField>
            <PFormField label="State">
              <select value={form?.state || ''} onChange={(e) => set('state')(e.target.value)} className={`${placementFieldCls} cursor-pointer`}>
                <option value="">— Select —</option>
                {US_STATES.map((st: string) => <option key={st} value={st}>{st}</option>)}
              </select>
            </PFormField>
            <PFormField label="Country">
              <CountrySelect value={form?.country || 'United States'} onChange={set('country')} />
            </PFormField>
            <PFormField label="Placement Date">
              <input type="date" value={form?.startDate || ''} onChange={(e) => set('startDate')(e.target.value)} className={placementFieldCls} />
            </PFormField>
          </div>
        </PFormSection>

        {/* Compensation */}
        <PFormSection icon={DollarSign} title="Compensation">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <PFormField label="Pay Rate">
              <input value={form?.payRate || ''} onChange={(e) => set('payRate')(e.target.value)} placeholder="e.g. $16/hour" className={placementFieldCls} />
            </PFormField>
            <PFormField label="Hours / Week">
              <input value={form?.hoursPerWeek || ''} onChange={(e) => set('hoursPerWeek')(e.target.value)} placeholder="e.g. 40" className={placementFieldCls} />
            </PFormField>
          </div>
        </PFormSection>

        {/* Contacts */}
        <PFormSection icon={User} title="On-site Contacts">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <PFormField label="Supervisor Name">
              <input value={form?.supervisorName || ''} onChange={(e) => set('supervisorName')(e.target.value)} className={placementFieldCls} />
            </PFormField>
            <PFormField label="Supervisor Email">
              <input type="email" value={form?.supervisorEmail || ''} onChange={(e) => set('supervisorEmail')(e.target.value)} placeholder="supervisor@company.com" className={placementFieldCls} />
            </PFormField>
            <PFormField label="Contact Name">
              <input value={form?.contactName || ''} onChange={(e) => set('contactName')(e.target.value)} className={placementFieldCls} />
            </PFormField>
            <PFormField label="Contact Email">
              <input type="email" value={form?.contactEmail || ''} onChange={(e) => set('contactEmail')(e.target.value)} placeholder="contact@company.com" className={placementFieldCls} />
            </PFormField>
          </div>
        </PFormSection>

        {/* Notes */}
        <PFormSection icon={FileText} title="Notes">
          <div className="space-y-3">
            <PFormField label="Notes">
              <textarea value={form?.notes || ''} onChange={(e) => set('notes')(e.target.value)} rows={2} placeholder="Visible to the applicant" className={`${placementFieldCls} resize-y`} />
            </PFormField>
            <PFormField label="Placement Notes (team only)">
              <textarea value={form?.placementNotes || ''} onChange={(e) => set('placementNotes')(e.target.value)} rows={2} placeholder="Internal notes for your team" className={`${placementFieldCls} resize-y`} />
            </PFormField>
          </div>
        </PFormSection>
      </div>
    </PModalShell>
  );
}

/* ───────────────────── DOCUMENTS TAB ───────────────────── */

const DocumentsTab = React.memo(function DocumentsTab({ app, appCtx, isEnhancedReferrer, currentUserProfile }: { app: Application; appCtx: any; isEnhancedReferrer?: boolean; currentUserProfile?: any }) {
  const canEditDocs = isEnhancedReferrer || hasApplicantAction(currentUserProfile, 'applicant.documents', 'edit');
  const [showSpecialRequestModal, setShowSpecialRequestModal] = useState(false);
  const isClosed = (app as any)?.submissionStatus === 'Closed';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentMetadata | null>(null);
  const [preVetConfirmDoc, setPreVetConfirmDoc] = useState<DocumentMetadata | null>(null);
  const [preVetLoading, setPreVetLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [docxHtml, setDocxHtml] = useState<string>('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('All');
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ open: boolean; storagePath: string; fileName: string; mimeType?: string }>({ open: false, storagePath: '', fileName: '' });
  const [validationPopup, setValidationPopup] = useState<{
    status: 'checking' | 'preApproved' | 'rejected' | 'pendingReview';
    fileName?: string;
    rejectionReasons?: string[];
  } | null>(null);
  const [reviewingDocId, setReviewingDocId] = useState<string | null>(null);
  const [reviewResult, setReviewResult] = useState<{
    decision: 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW';
    reason: string;
    docId: string;
    applicationId: string;
    applicantEmail?: string;
    applicantName?: string;
    docName?: string;
    aiReview?: any;
    identityStatus?: string;
  } | null>(null);
  const [reviewAllProgress, setReviewAllProgress] = useState<{ current: number; total: number; docName: string } | null>(null);
  // Guarded bulk AI review: results are collected first and shown as a
  // summary — NOTHING is applied until the admin confirms (approvals in
  // one confirm; every rejection needs its own per-document confirm).
  type AiReviewOutcome = {
    docId: string;
    docName: string;
    decision: 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW';
    reason: string;
    identityStatus?: string;
    aiReview?: any;
    error?: string;
    /** Background-job outcomes only: whether the job already applied
     *  this approval, left it for the admin, or skipped it because a
     *  reviewer decided the document first. */
    appliedState?: 'auto_applied' | 'pending' | 'skipped_decided';
    appliedAt?: string;
    note?: string;
    engine?: string;
  };
  const [bulkReviewSummary, setBulkReviewSummary] = useState<AiReviewOutcome[] | null>(null);
  const [bulkAppliedIds, setBulkAppliedIds] = useState<Set<string>>(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkRejectingDocId, setBulkRejectingDocId] = useState<string | null>(null);
  // AI Review Result popup: the Apply button guards itself the instant
  // it is clicked (double-fire protection; the handler is guarded too).
  const [applyingAiResult, setApplyingAiResult] = useState(false);
  const [aiApplyError, setAiApplyError] = useState<string | null>(null);
  // Background review job (bulk_document_review_job): trigger + status +
  // re-entry. The summary above is reused for a finished job's stored
  // outcomes; bulkSummaryJobId marks that mode.
  const [bulkSummaryJobId, setBulkSummaryJobId] = useState<string | null>(null);
  const [reviewJob, setReviewJob] = useState<{ id: string; status: string; progressPercent: number; totalItems: number; completedItems: number } | null>(null);
  const [reviewJobBusy, setReviewJobBusy] = useState(false);
  // Review confirmation popup state (ask before running AI review on uploaded docs)
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [pendingReviewDocs, setPendingReviewDocs] = useState<string[]>([]);
  const [reviewingAfterUpload, setReviewingAfterUpload] = useState(false);
  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState<{ file: File; customName: string; docType: string }[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const availablePrograms: any[] = Array.isArray(appCtx.programsList) ? appCtx.programsList : [];
  // Document Required multi-tag state
  const [docRequiredInput, setDocRequiredInput] = useState('');
  const [savingDocRequired, setSavingDocRequired] = useState(false);
  const [showProgramDocsModal, setShowProgramDocsModal] = useState(false);
  const [showCustomDocModal, setShowCustomDocModal] = useState(false);
  const [selectedProgramDocs, setSelectedProgramDocs] = useState<string[]>([]);
  const [customDocName, setCustomDocName] = useState('');

  // Resolve the preview URL whenever the preview document changes
  React.useEffect(() => {
    setDocxHtml('');
    if (appCtx.previewDoc) {
      setPreviewLoading(true);
      setPreviewUrl('');
      resolveFirebaseDocumentUrl(appCtx.previewDoc.storagePath, appCtx.previewDoc.downloadUrl)
        .then(url => setPreviewUrl(url))
        .catch(() => setPreviewUrl(appCtx.previewDoc.downloadUrl || appCtx.previewDoc.storagePath || ''))
        .finally(() => setPreviewLoading(false));
    } else {
      setPreviewUrl('');
      setPreviewLoading(false);
    }
  }, [appCtx.previewDoc]);

  const pendingDocs = (appCtx.docsList || []).filter((d: DocumentMetadata) => d.status === 'Pending' || d.status === 'PendingAdminReview').length;
  // Only a full human/admin 'Approved' counts as approved — SystemPreApproved
  // and PreVetted are intermediate states and must not inflate this count
  // (the workflow documents gate counts full approvals only).
  const approvedDocs = (appCtx.docsList || []).filter((d: DocumentMetadata) => d.status === 'Approved').length;
  // Honest scan-engine badge: shown only when the extraction record says
  // which engine actually read the document (legacy records without an
  // engine field show nothing rather than a guess).
  const scanEngineLabel = (d: DocumentMetadata): string | null => {
    const engine = (d as any).extraction?.engine as string | undefined;
    switch (engine) {
      case 'document-ai': return 'Scanned by Document AI';
      case 'document-ai-id': return 'Scanned by Document AI (ID proofing)';
      case 'tesseract-ocr': return 'Scanned by built-in OCR';
      case 'pdf-text': return 'Text read by built-in PDF parser';
      case 'docx-text': return 'Text read by built-in Word parser';
      case 'unreadable': return 'Could not be read automatically';
      default: return null;
    }
  };
  const docIdentityMismatch = (d: DocumentMetadata): boolean =>
    (d as any).judgment?.identityStatus === 'mismatch' || (d as any).aiReview?.identityStatus === 'mismatch';
  const rejectedDocs = (appCtx.docsList || []).filter((d: DocumentMetadata) => d.status === 'Rejected').length;
  const totalDocs = (appCtx.docsList || []).length;
  const documentTypes = Array.from(new Set((appCtx.docsList || []).map((d: DocumentMetadata) => d.type).filter(Boolean)));
  const filteredDocs = (appCtx.docsList || []).filter((d: DocumentMetadata) => {
    const q = documentSearch.trim().toLowerCase();
    const matchesSearch = !q || [d.name, d.type, d.status].some(v => String(v || '').toLowerCase().includes(q));
    const matchesType = documentTypeFilter === 'All' || d.type === documentTypeFilter;
    return matchesSearch && matchesType;
  });
  const addBulkFiles = (files: File[]) => {
    // Same allowlist the server enforces — refuse unreadable types here
    // with the shared plain-language copy instead of at upload time.
    const accepted: File[] = [];
    const rejected: string[] = [];
    files.forEach(f => {
      if (isAcceptableDocumentFileName(f.name)) accepted.push(f);
      else rejected.push(f.name);
    });
    if (rejected.length > 0) {
      notifyToast('warning', unsupportedTypeMessage(rejected[0]) + (rejected.length > 1 ? ` (${rejected.length} files were skipped.)` : ''));
    }
    if (accepted.length > 0) {
      const newFiles = accepted.map(f => ({ file: f, customName: f.name.replace(/\.[^/.]+$/, ''), docType: appCtx.adminDocType }));
      setBulkFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleAdminDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) addBulkFiles(Array.from(files));
  };

  const handleBulkFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) addBulkFiles(Array.from(files));
    e.target.value = '';
  };

  const handleBulkFileRemove = (index: number) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBulkFileNameChange = (index: number, name: string) => {
    setBulkFiles(prev => prev.map((item, i) => i === index ? { ...item, customName: name } : item));
  };

  const handleBulkFileDocTypeChange = (index: number, docType: string) => {
    setBulkFiles(prev => prev.map((item, i) => i === index ? { ...item, docType } : item));
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0 || !app) return;
    setBulkUploading(true);
    const uploadedDocIds: string[] = [];
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < bulkFiles.length; i++) {
      setBulkProgress({ current: i + 1, total: bulkFiles.length });
      const { file, customName, docType } = bulkFiles[i];
      const ext = file.name.split('.').pop() || '';
      const finalName = `${customName}.${ext}`;
      const renamedFile = new File([file], finalName, { type: file.type });
      // Set doc type so handleAdminUploadDocument uses the right type
      appCtx.setAdminDocType(docType);
      try {
        // Upload without running AI validation — we'll ask first
        await appCtx.handleAdminUploadDocument(renamedFile, undefined, true);
        // Find the just-uploaded doc in the list
        const uploadedDoc = (appCtx.docsList || []).find(
          (d: DocumentMetadata) => d.name === finalName && d.type === docType
        );
        if (uploadedDoc) {
          uploadedDocIds.push(uploadedDoc.docId);
        }
        successCount++;
      } catch {
        failCount++;
      }
    }
    setBulkUploading(false);
    setBulkProgress(null);
    setBulkFiles([]);
    appCtx.setAdminUploadedFile(null);
    if (failCount === 0) {
      setBulkResult(`Successfully uploaded ${successCount} file(s)!`);
    } else {
      setBulkResult(`${successCount} uploaded, ${failCount} failed`);
    }
    setTimeout(() => setBulkResult(null), 5000);
    // Show review confirmation popup if any docs were uploaded
    if (uploadedDocIds.length > 0) {
      setPendingReviewDocs(uploadedDocIds);
      setShowReviewPrompt(true);
    }
  };

  // Runs ONE AI review and returns its outcome. Applies NOTHING — every
  // apply path goes through an explicit admin confirmation (per-document
  // modal, or the guarded bulk summary).
  const runAiReviewForDoc = async (doc: DocumentMetadata, programId: string, token: string | undefined): Promise<AiReviewOutcome> => {
    try {
      const res = await fetch('/api/documents/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ docId: doc.docId, applicationId: app.applicationId || app.id || app.applicantId || '', programId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Review failed');
      return {
        docId: doc.docId,
        docName: doc.name,
        decision: (data.decision === 'APPROVED' || data.decision === 'REJECTED') ? data.decision : 'NEEDS_REVIEW',
        reason: data.reason || '',
        identityStatus: data.identity?.status,
        aiReview: data.aiReview || null,
      };
    } catch (err: any) {
      return {
        docId: doc.docId,
        docName: doc.name,
        decision: 'NEEDS_REVIEW',
        reason: 'The AI review could not be completed for this document.',
        error: err?.message || 'Review failed',
      };
    }
  };

  const handleRunReviewOnUploaded = async () => {
    if (pendingReviewDocs.length === 0 || !app) return;
    setShowReviewPrompt(false);
    setReviewingAfterUpload(true);
    const token = await auth.currentUser?.getIdToken();
    const outcomes: AiReviewOutcome[] = [];
    for (let i = 0; i < pendingReviewDocs.length; i++) {
      const docId = pendingReviewDocs[i];
      const doc = (appCtx.docsList || []).find((d: DocumentMetadata) => d.docId === docId);
      if (!doc) continue;
      setValidationPopup({ status: 'checking', fileName: doc.name });
      outcomes.push(await runAiReviewForDoc(doc, app.programId || '', token));
    }
    setValidationPopup(null);
    setReviewingAfterUpload(false);
    setPendingReviewDocs([]);
    // Guarded apply: show the summary; the admin confirms what applies.
    if (outcomes.length > 0) {
      setBulkAppliedIds(new Set());
      setBulkSummaryJobId(null);
      setBulkReviewSummary(outcomes);
    }
  };

  const handleBulkApprovePending = () => {
    // Identity-mismatched documents can NEVER be bulk-approved — their
    // latest AI review recorded the mismatch; they need individual review.
    const hasIdentityMismatch = (d: DocumentMetadata) =>
      (d as any).judgment?.identityStatus === 'mismatch' || (d as any).aiReview?.identityStatus === 'mismatch';
    const candidates = (appCtx.docsList || [])
      .filter((d: DocumentMetadata) => d.status === 'Pending' || d.status === 'PendingAdminReview' || d.status === 'SystemPreApproved');
    const eligible = candidates.filter(d => !hasIdentityMismatch(d));
    const excluded = candidates.length - eligible.length;
    eligible.forEach((d: DocumentMetadata) => appCtx.handleReviewDocument(d.docId, 'Approved', ''));
    if (excluded > 0) {
      notifyToast('warning', `${excluded} document(s) were excluded from bulk approval because their identity cross-check mismatched the application. Review them individually.`);
    }
  };

  const handleBulkReviewPending = async () => {
    const pid = app.programId || '';
    if (!pid) { notifyToast('warning', 'No program assigned to this application.'); return; }
    const pendingDocs = (appCtx.docsList || []).filter(
      (d: DocumentMetadata) => d.status !== 'Approved' && d.status !== 'Rejected'
    );
    if (pendingDocs.length === 0) { notifyToast('info', 'No documents pending review.'); return; }
    setReviewAllProgress({ current: 0, total: pendingDocs.length, docName: '' });
    const token = await auth.currentUser?.getIdToken();
    const outcomes: AiReviewOutcome[] = [];
    for (let i = 0; i < pendingDocs.length; i++) {
      const doc = pendingDocs[i];
      setReviewAllProgress({ current: i + 1, total: pendingDocs.length, docName: doc.name });
      outcomes.push(await runAiReviewForDoc(doc, pid, token));
    }
    setReviewAllProgress(null);
    // Guarded apply: summary + explicit confirmation (approvals in one
    // confirm; rejections one document at a time). Previously this loop
    // auto-applied every AI verdict as a full human-stamped decision.
    setBulkAppliedIds(new Set());
    setBulkSummaryJobId(null);
    setBulkReviewSummary(outcomes);
  };

  // ── Background review job (bulk_document_review_job) ─────────────
  // The admin triggers the job and can leave the page: the server
  // reviews every pending document, auto-applies approvals that pass
  // every check, and stores the rest in the job summary this panel
  // re-opens below.
  const reviewJobApplicationId = app.applicationId || (app as any).id || (app as any).applicantId || '';
  const [reviewJobError, setReviewJobError] = useState('');
  const authHeadersForJob = async (): Promise<Record<string, string>> => {
    const token = await auth.currentUser?.getIdToken();
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workspaceHeader() };
  };
  const refreshReviewJob = async (): Promise<any> => {
    if (!reviewJobApplicationId) return null;
    try {
      const headers = await authHeadersForJob();
      const res = await fetch(`/api/admin/documents/bulk-review-job/latest?applicationId=${encodeURIComponent(reviewJobApplicationId)}`, { headers });
      if (!res.ok) return null;
      const data = await res.json();
      setReviewJob(data.job ? {
        id: data.job.id,
        status: data.job.status,
        progressPercent: data.job.progressPercent || 0,
        totalItems: data.job.totalItems || 0,
        completedItems: data.job.completedItems || 0,
      } : null);
      return data;
    } catch { return null; }
  };
  useEffect(() => { refreshReviewJob(); /* load any existing background review for this application */ }, [reviewJobApplicationId]);
  useEffect(() => {
    if (!reviewJob || (reviewJob.status !== 'queued' && reviewJob.status !== 'processing' && reviewJob.status !== 'validating')) return;
    const t = setInterval(() => { refreshReviewJob(); }, 8000);
    return () => clearInterval(t);
  }, [reviewJob?.id, reviewJob?.status]);
  const handleReviewInBackground = async () => {
    setReviewJobBusy(true);
    setReviewJobError('');
    try {
      const headers = { 'Content-Type': 'application/json', ...(await authHeadersForJob()) };
      const res = await fetch('/api/admin/documents/bulk-review-job', {
        method: 'POST',
        headers,
        body: JSON.stringify({ applicationId: reviewJobApplicationId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setReviewJobError(data.error || 'Could not start the background review.'); return; }
      setReviewJob({ id: data.jobId, status: data.status, progressPercent: 0, totalItems: data.totalItems || 0, completedItems: 0 });
    } catch (err: any) {
      setReviewJobError(err?.message || 'Could not start the background review.');
    } finally { setReviewJobBusy(false); }
  };
  const openReviewJobSummary = async () => {
    const data = await refreshReviewJob();
    const items = Array.isArray(data?.items) ? data.items : [];
    const outcomes = items.map((i: any) => mapBulkReviewJobItem(i)).filter(Boolean) as unknown as AiReviewOutcome[];
    setBulkAppliedIds(new Set(outcomes.filter(o => o.appliedState === 'auto_applied').map(o => o.docId)));
    setBulkSummaryJobId(data?.job?.id || reviewJob?.id || null);
    setBulkReviewSummary(outcomes);
  };
  // Get required documents for the applicant's program as plain string labels
  const programRequiredDocs = React.useMemo(() => {
    const pid = app.programId || '';
    const pname = app.programType || '';
    const found = pid
      ? availablePrograms.find((p: any) => p.id === pid || p.programId === pid)
      : availablePrograms.find((p: any) =>
          p.name?.toLowerCase() === pname.toLowerCase() ||
          p.id?.toLowerCase() === pname.toLowerCase()
        );
    const rawDocs: any[] = found?.requiredDocuments || [];
    return rawDocs.map((d: any) => (typeof d === 'string' ? d : d?.label || d?.name || d?.id || '')).filter(Boolean);
  }, [availablePrograms, app.programType, app.programId]);

  // Build doc type options from program required docs + documentRequired tags + 'Other',
  // matching exactly what's shown in the required document tags above.
  // No filtering — admins can upload any doc type regardless of existing docs.
  const docTypeOptions = React.useMemo(() => {
    const tags = parseApplicationDocumentExtras((app as any).documentRequired, programRequiredDocs);
    // Include program-required docs even if they haven't been added to
    // documentRequired yet, so the dropdown always reflects the program
    // definition from Firestore.
    return [...new Set([...tags, ...programRequiredDocs, 'Other'])];
  }, [(app as any).documentRequired, programRequiredDocs]);

  const displayDocs = ((app as any).documentRequired || '').trim() || programRequiredDocs.join(', ');
  // Tag lists are built WITHOUT splitting: program labels come straight
  // from the program config array (a label may itself contain commas),
  // admin extras go through the shared parser. (Splitting displayDocs on
  // commas shredded comma-bearing labels into phantom tags.)
  const extraDocTags = parseApplicationDocumentExtras((app as any).documentRequired, programRequiredDocs);
  const displayDocTags = [...new Set([...programRequiredDocs, ...extraDocTags])];

  // Sync adminDocType when docTypeOptions changes so the default stays valid
  React.useEffect(() => {
    if (docTypeOptions.length > 0 && !docTypeOptions.includes(appCtx.adminDocType)) {
      appCtx.setAdminDocType(docTypeOptions[0]);
    }
  }, [docTypeOptions, appCtx.adminDocType]);

  // NOTE: documentRequired holds ONLY admin-attached per-application
  // extras. It used to be auto-populated with the program's labels
  // (comma-joined) whenever empty — that stamp poisoned every consumer
  // that split it and duplicated the program config, which is the
  // authoritative source the submit gate and Step 7 read live. The
  // auto-populate write was removed; program documents are merged into
  // the displays from programRequiredDocs instead.

  return (
    <div className="space-y-6">
      {/* ── Document Required (Multi-tag) — read-only view for enhanced referrers ── */}
      {isEnhancedReferrer && displayDocTags.length > 0 && (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-caption font-bold text-slate-500">Required Documents</p>
          <span className="text-caption font-semibold text-brand">{displayDocTags.length} document(s)</span>
        </div>
        {/* Render existing tags without delete button */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {displayDocTags.map((tag: string) => {
            // Find matching documents for this tag
            const matchingDocs = (appCtx.docsList || []).filter(
              (d: DocumentMetadata) => d.type === tag && !d.superseded
            );
            const latestDoc = matchingDocs[matchingDocs.length - 1];
            const isApproved = latestDoc?.status === 'Approved';
            const isPending = latestDoc?.status === 'Pending';
            const isRejected = latestDoc?.status === 'Rejected';
            const hasDoc = !!latestDoc;

            return (
              <span key={tag} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                isApproved
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : hasDoc && isPending
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : hasDoc && isRejected
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-brand-active border-blue-200'
              }`}>
                {isApproved ? <CheckCircle className="h-3 w-3" /> : hasDoc ? <Clock className="h-3 w-3" /> : <div className="h-2 w-2 rounded-full border border-current" />}
                {tag}
                <span className={`text-[10px] font-bold ml-0.5 ${
                  isApproved ? 'text-emerald-500' :
                  hasDoc && isPending ? 'text-amber-500' :
                  hasDoc && isRejected ? 'text-red-500' :
                  'text-blue-500'
                }`}>
                  ({isApproved ? 'Received & Approved' : hasDoc && isPending ? 'Received Pending Approval' : hasDoc && isRejected ? 'Rejected' : 'Pending'})
                </span>
              </span>
            );
          })}
        </div>
      </div>
      )}

      {/* ── Document Required (Multi-tag) — hidden for enhanced referrers ── */}
      {!isEnhancedReferrer && (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-caption font-bold text-slate-500">Document Required</p>
            {((app as any).documentRequired || '').trim() && (
              <span className="text-caption font-semibold text-brand">{parseApplicationDocumentExtras((app as any).documentRequired).length} document(s)</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedProgramDocs([]);
                setShowProgramDocsModal(true);
              }}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-all cursor-pointer"
            >
              Add From Program Documents
            </button>
            <button
              onClick={() => {
                setCustomDocName('');
                setShowCustomDocModal(true);
              }}
              className="px-3 py-1.5 btn-primary text-white text-xs font-semibold rounded-lg transition-all cursor-pointer"
            >
              Add Custom Document
            </button>
          </div>
        </div>

        {/* Option A Modal: Add From Program Documents */}
        {showProgramDocsModal && (
          <Modal onClose={() => setShowProgramDocsModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">Add From Program Documents</h3>
                <button onClick={() => setShowProgramDocsModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-slate-500">
                Select documents configured for this applicant's program to add to their Required Documents list. Existing documents are marked and disabled.
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {programRequiredDocs.length === 0 ? (
                  <p className="text-xs text-slate-500 italic py-2">No required documents configured for this program.</p>
                ) : (
                  programRequiredDocs.map((docLabel) => {
                    const existingList = parseApplicationDocumentExtras((app as any).documentRequired).map((s: string) => s.toLowerCase());
                    const isAlreadyAdded = existingList.includes(docLabel.trim().toLowerCase());
                    const isChecked = selectedProgramDocs.includes(docLabel);

                    return (
                      <label
                        key={docLabel}
                        className={`flex items-center justify-between p-3 rounded-xl border ${
                          isAlreadyAdded
                            ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                            : isChecked
                            ? 'bg-blue-50/50 border-brand'
                            : 'bg-white border-slate-200 hover:border-slate-300 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            disabled={isAlreadyAdded}
                            checked={isAlreadyAdded || isChecked}
                            onChange={(e) => {
                              if (isAlreadyAdded) return;
                              if (e.target.checked) {
                                setSelectedProgramDocs([...selectedProgramDocs, docLabel]);
                              } else {
                                setSelectedProgramDocs(selectedProgramDocs.filter(d => d !== docLabel));
                              }
                            }}
                            className="rounded text-brand focus:ring-brand"
                          />
                          <span className="text-xs font-semibold text-slate-800">{docLabel}</span>
                        </div>
                        {isAlreadyAdded ? (
                          <span className="text-[10px] font-bold text-slate-400 bg-slate-200/80 px-2 py-0.5 rounded-full">Already Required</span>
                        ) : isChecked ? (
                          <span className="text-[10px] font-bold text-brand bg-blue-100 px-2 py-0.5 rounded-full">Selected</span>
                        ) : (
                          <span className="text-[10px] font-semibold text-slate-400">Available</span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  onClick={() => setShowProgramDocsModal(false)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  disabled={selectedProgramDocs.length === 0 || savingDocRequired}
                  onClick={async () => {
                    if (selectedProgramDocs.length === 0) return;
                    setSavingDocRequired(true);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await safeFetch(`/api/admin/applications/${app.applicationId}/document-required`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({ documentTypes: selectedProgramDocs }),
                      });
                      if (res.documentRequired !== undefined) {
                        (app as any).documentRequired = res.documentRequired;
                        appCtx.setSelectedApp?.({ ...appCtx.selectedApp, documentRequired: res.documentRequired, updatedAt: new Date().toISOString() });
                      }
                      setShowProgramDocsModal(false);
                      setSelectedProgramDocs([]);
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Failed to add program documents');
                    } finally {
                      setSavingDocRequired(false);
                    }
                  }}
                  className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl disabled:bg-blue-300"
                >
                  {savingDocRequired ? 'Adding...' : `Add Selected (${selectedProgramDocs.length})`}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Option B Modal: Add Custom Document */}
        {showCustomDocModal && (
          <Modal onClose={() => setShowCustomDocModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">Add Custom Document Requirement</h3>
                <button onClick={() => setShowCustomDocModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-slate-500">
                Create a custom required document specifically for this applicant.
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Document Name *</label>
                <input
                  type="text"
                  value={customDocName}
                  onChange={(e) => setCustomDocName(e.target.value)}
                  placeholder="e.g. Police Clearance Certificate, Sponsorship Letter"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  onClick={() => setShowCustomDocModal(false)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  disabled={!customDocName.trim() || savingDocRequired}
                  onClick={async () => {
                    const docName = customDocName.trim();
                    if (!docName) return;

                    const existingList = parseApplicationDocumentExtras((app as any).documentRequired).map((s: string) => s.toLowerCase());
                    if (existingList.includes(docName.toLowerCase())) {
                      notifyToast('warning', 'This document is already required for this applicant.');
                      return;
                    }

                    setSavingDocRequired(true);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await safeFetch(`/api/admin/applications/${app.applicationId}/document-required`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({ documentType: docName }),
                      });
                      if (res.documentRequired !== undefined) {
                        (app as any).documentRequired = res.documentRequired;
                        appCtx.setSelectedApp?.({ ...appCtx.selectedApp, documentRequired: res.documentRequired, updatedAt: new Date().toISOString() });
                      }
                      setShowCustomDocModal(false);
                      setCustomDocName('');
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Failed to add custom document requirement');
                    } finally {
                      setSavingDocRequired(false);
                    }
                  }}
                  className="px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl disabled:bg-blue-300"
                >
                  {savingDocRequired ? 'Adding...' : 'Add Requirement'}
                </button>
              </div>
            </div>
          </Modal>
        )}
        {/* Render existing tags — shows both program default docs and admin-added docs */}
        {displayDocTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 border-t border-slate-100 pt-3">
            {displayDocTags.map((tag: string) => {
              // Find matching documents for this tag
              const matchingDocs = (appCtx.docsList || []).filter(
                (d: DocumentMetadata) => d.type === tag && !d.superseded
              );
              const latestDoc = matchingDocs[matchingDocs.length - 1];
              const isApproved = latestDoc?.status === 'Approved';
              const isPending = latestDoc?.status === 'Pending';
              const isRejected = latestDoc?.status === 'Rejected';
              const hasDoc = !!latestDoc;

              return (
                <span key={tag} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                  isApproved
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : hasDoc && isPending
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : hasDoc && isRejected
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-brand-active border-blue-200'
                }`}>
                  {isApproved ? <CheckCircle className="h-3 w-3" /> : hasDoc ? <Clock className="h-3 w-3" /> : <div className="h-2 w-2 rounded-full border border-current" />}
                  {tag}
                  <span className={`text-[10px] font-bold ml-0.5 ${
                    isApproved ? 'text-emerald-500' :
                    hasDoc && isPending ? 'text-amber-500' :
                    hasDoc && isRejected ? 'text-red-500' :
                    'text-blue-500'
                  }`}>
                    ({isApproved ? 'Received & Approved' : hasDoc && isPending ? 'Received Pending Approval' : hasDoc && isRejected ? 'Rejected' : 'Pending'})
                  </span>
                  {extraDocTags.includes(tag) && (
                  <button
                    onClick={async () => {
                      if (!(await confirmDialog(`Are you sure you want to remove the document requirement "${tag}"?`))) return;
                      const current = parseApplicationDocumentExtras((app as any).documentRequired);
                      const updated = current.filter((t: string) => t !== tag).join('\n');
                      try {
                        await appCtx.adminProfileUpdate?.(app.applicationId, { documentRequired: updated });
                        (app as any).documentRequired = updated;
                        appCtx.setSelectedApp?.({ ...appCtx.selectedApp, documentRequired: updated, updatedAt: new Date().toISOString() });
                        notifyToast('info', `Removed required document: ${tag}`);
                      } catch (err) {
                        console.error('Failed to remove document tag:', err);
                        notifyToast('error', `Failed to remove required document ${tag}`);
                      }
                    }}
                    className="ml-0.5 hover:text-red-600 cursor-pointer"
                    aria-label={`Remove requirement: ${tag}`}
                    title={`Remove requirement: ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Uploaded Documents — with compact inline upload */}
      <SectionCard title="Uploaded Documents" action={
        <div className="flex items-center space-x-1.5">
          {isClosed ? (
            <span className="px-2.5 py-1.5 text-caption font-bold text-slate-500 bg-slate-100 rounded-lg flex items-center gap-1.5">
              <Archive className="h-3.5 w-3.5" />
              Application Closed — uploads disabled
            </span>
          ) : reviewAllProgress ? (
            <span className="px-2.5 py-1.5 text-caption font-bold text-purple-700 bg-purple-50 rounded-lg flex items-center gap-1.5 animate-pulse">
              <Sparkles className="h-3.5 w-3.5" />
              Reviewing {reviewAllProgress.current}/{reviewAllProgress.total} · {reviewAllProgress.docName.slice(0, 20)}
            </span>
          ) : (
            <>
              {!isEnhancedReferrer && canEditDocs && (
              <button
                onClick={handleBulkReviewPending}
                className="px-2.5 py-1.5 rounded-lg text-caption font-bold transition-all flex items-center space-x-1 cursor-pointer bg-white border border-slate-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 text-slate-700"
                title="AI Review all pending documents"
              >
                <Sparkles className="h-3.5 w-3.5" /><span>Review All</span>
              </button>
              )}
              {!isEnhancedReferrer && canEditDocs && (
              <button
                onClick={handleReviewInBackground}
                disabled={reviewJobBusy || (!!reviewJob && (reviewJob.status === 'queued' || reviewJob.status === 'processing' || reviewJob.status === 'validating'))}
                className="px-2.5 py-1.5 rounded-lg text-caption font-bold transition-all flex items-center space-x-1 cursor-pointer bg-white border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 text-slate-700 disabled:opacity-50"
                title="Run the same AI review on the server — you can leave this page. Approvals that pass every check are applied automatically; everything else waits in the summary."
              >
                <Clock className="h-3.5 w-3.5" /><span>{reviewJobBusy ? 'Starting…' : 'Review in background'}</span>
              </button>
              )}
              {!isEnhancedReferrer && canEditDocs && reviewJob && (
                reviewJob.status === 'queued' || reviewJob.status === 'processing' || reviewJob.status === 'validating' ? (
                  <span className="px-2.5 py-1.5 text-caption font-bold text-indigo-700 bg-indigo-50 rounded-lg flex items-center gap-1.5">
                    Background review running — {reviewJob.completedItems}/{reviewJob.totalItems} ({reviewJob.progressPercent}%). You can leave this page.
                  </span>
                ) : (
                  <button
                    onClick={openReviewJobSummary}
                    className="px-2.5 py-1.5 rounded-lg text-caption font-bold transition-all flex items-center space-x-1 cursor-pointer bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white"
                  >
                    <CheckCircle className="h-3.5 w-3.5" /><span>Background results</span>
                  </button>
                )
              )}
              {!isEnhancedReferrer && canEditDocs && reviewJobError && (
                <span className="px-2.5 py-1.5 text-caption font-bold text-red-700 bg-red-50 rounded-lg">{reviewJobError}</span>
              )}
              {isEnhancedReferrer ? (
                // Simplified single-file upload for enhanced referrers (no bulk panel)
                <div className="relative">
                  <input
                    type="file"
                    id="referrer-file-upload"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) appCtx.handleAdminUploadDocument(file);
                      e.target.value = '';
                    }}
                  />
                  <button
                    onClick={() => document.getElementById('referrer-file-upload')?.click()}
                    className="px-2.5 py-1.5 rounded-lg text-caption font-bold transition-all flex items-center space-x-1 cursor-pointer btn-primary  text-white"
                    title="Upload document"
                  >
                    <Upload className="h-3.5 w-3.5" /><span>Upload</span>
                  </button>
                </div>
              ) : canEditDocs ? (
              <button
                onClick={() => setShowUploadPanel(!showUploadPanel)}
                className={`px-2.5 py-1.5 rounded-lg text-caption font-bold transition-all flex items-center space-x-1 cursor-pointer ${
                  showUploadPanel ? 'bg-blue-100 text-brand' : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-700'
                }`}
                title="Upload document"
              >
                <Upload className="h-3.5 w-3.5" /><span>Upload</span>
              </button>
              ) : null}
            </>
          )}
          <div className="relative group">
            <button className="px-2.5 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-caption font-semibold transition-all flex items-center space-x-1 cursor-pointer">
              <Download className="h-3.5 w-3.5" /><span>Download</span>
              <ChevronDown className="h-3 w-3 ml-0.5" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-[8500] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
              <button onClick={() => appCtx.handleDownloadAllAsZip(app, 'all')} className="w-full text-left px-3.5 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 first:rounded-t-xl flex items-center space-x-2 cursor-pointer">
                <Download className="h-3.5 w-3.5 text-slate-400" /><span>All Documents</span>
              </button>
              <button onClick={() => appCtx.handleDownloadAllAsZip(app, 'approved')} className="w-full text-left px-3.5 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center space-x-2 cursor-pointer">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /><span>Approved Only</span>
              </button>
              <button onClick={() => appCtx.handleDownloadAllAsZip(app, 'rejected')} className="w-full text-left px-3.5 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 last:rounded-b-xl flex items-center space-x-2 cursor-pointer">
                <XCircle className="h-3.5 w-3.5 text-rose-500" /><span>Rejected Only</span>
              </button>
            </div>
          </div>
        </div>
      }>
        {/* Inline upload panel — shown when toggled (hidden for referrers) */}
        {!isEnhancedReferrer && !isClosed && showUploadPanel && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-caption font-bold text-slate-600 whitespace-nowrap">Doc type:</label>
                <select
                  value={appCtx.adminDocType}
                  onChange={(e) => appCtx.setAdminDocType(e.target.value)}
                  className="w-40 px-2 py-1.5 text-xs font-semibold border border-blue-300 rounded-lg bg-white outline-none "
                >
                  {docTypeOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleBulkFilesSelected}
                className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:btn-primary file:text-white hover:file:bg-blue-700 cursor-pointer"
              />
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleAdminDrop}
                className="w-full text-caption text-slate-400 border-2 border-dashed border-blue-200 rounded-lg px-3 py-2 text-center cursor-pointer  transition-colors"
              >
                or drag & drop files here
              </div>
            </div>

            {/* Selected files list */}
            {bulkFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-caption font-bold text-slate-600">{bulkFiles.length} file(s) selected</p>
                <div className="max-h-60 overflow-y-auto space-y-1.5">
                  {bulkFiles.map((bf, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded-lg border border-blue-200 px-3 py-2">
                      <div className="flex items-center gap-1.5 shrink-0">
                        <select
                          value={bf.docType}
                          onChange={(e) => handleBulkFileDocTypeChange(idx, e.target.value)}
                          className="w-32 px-1.5 py-1 text-[10px] font-semibold border border-slate-200 rounded-md bg-white outline-none "
                        >
                          {docTypeOptions.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        value={bf.customName}
                        onChange={(e) => handleBulkFileNameChange(idx, e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1 text-xs font-medium border border-slate-200 rounded-md bg-white outline-none "
                        placeholder="File name"
                      />
                      <span className="text-caption text-slate-400 shrink-0">.{bf.file.name.split('.').pop()}</span>
                      <button onClick={() => handleBulkFileRemove(idx)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Remove">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bulk progress */}
            {bulkProgress && (
              <div className="flex items-center gap-2 text-caption text-brand font-semibold">
                <Loader className="h-3.5 w-3.5 animate-spin" />
                <span>Uploading {bulkProgress.current} of {bulkProgress.total}...</span>
              </div>
            )}

            {/* Result message */}
            {bulkResult && (
              <div className={`text-caption font-semibold ${bulkResult.includes('failed') ? 'text-red-600' : 'text-emerald-600'}`}>
                {bulkResult}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkUpload}
                disabled={bulkFiles.length === 0 || bulkUploading}
                className="px-4 py-2 btn-primary  disabled:bg-blue-300 text-white rounded-lg text-caption font-bold transition-all flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed"
              >
                {bulkUploading ? (
                  <><Loader className="h-3.5 w-3.5 animate-spin" /><span>Uploading...</span></>
                ) : (
                  <><Upload className="h-3.5 w-3.5" /><span>Upload All ({bulkFiles.length})</span></>
                )}
              </button>
              {bulkFiles.length > 0 && !bulkUploading && (
                <button onClick={() => { setBulkFiles([]); setBulkResult(null); }} className="px-3 py-2 text-caption font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search" value={documentSearch} onChange={(e) => setDocumentSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl  focus:ring-2 focus:ring-brand-primary outline-none" placeholder="Search documents by name, type, or status..." />
          </div>
          <select value={documentTypeFilter} onChange={(e) => setDocumentTypeFilter(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white  focus:ring-2 focus:ring-brand-primary outline-none">
            <option value="All">All document types</option>
            {documentTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        {(!appCtx.docsList || appCtx.docsList.length === 0) ? (
          <div className="text-center py-10">
            <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <FileText className="h-7 w-7 text-slate-400" />
            </div>
            <p className="text-sm font-bold text-slate-500">No documents uploaded</p>
            <p className="text-caption text-slate-400 mt-1">Use the upload panel below to add documents for this applicant.</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400">No documents match your search or filter.</div>
        ) : (
          <div className="space-y-2">
            {filteredDocs.map((doc: DocumentMetadata) => (
              <div key={doc.docId} className={`group p-3.5 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all ${openMenuId === doc.docId ? 'relative z-[100]' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                      doc.status === 'Approved' ? 'bg-emerald-50 text-emerald-600' :
                      doc.status === 'Rejected' ? 'bg-red-50 text-red-600' :
                      doc.status === 'SystemPreApproved' ? 'bg-green-50 text-green-600' :
                      doc.status === 'PendingAdminReview' ? 'bg-amber-50 text-amber-600' :
                      'bg-amber-50 text-amber-600'
                    }`}>
                      <FileText className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      {appCtx.editingDocId === doc.docId ? (
                        <div className="flex items-center gap-2">
                          <input value={appCtx.editingDocName} onChange={(e) => appCtx.setEditingDocName(e.target.value)} className="px-2 py-1 text-sm border border-slate-200 rounded-lg  outline-none flex-1" autoFocus />
                          <button onClick={() => appCtx.handleRenameDocument(doc.docId)} className="text-emerald-600 hover:text-emerald-700 p-1 cursor-pointer"><Check className="h-4 w-4" /></button>
                          <button onClick={() => appCtx.setEditingDocId(null)} className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <p className="text-sm font-bold text-slate-800 truncate max-w-[260px]">{doc.name}</p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-caption text-slate-400 font-medium">{doc.type}</span>
                        <span className="text-slate-300">·</span>
                        <span className="text-caption text-slate-400">{formatFullDateTime(doc.uploadDate)}</span>
                      </div>
                      {(scanEngineLabel(doc) || docIdentityMismatch(doc)) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {scanEngineLabel(doc) && (
                            <span
                              className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                              title={(doc as any).extraction?.engineNote || 'Engine that read this document'}
                            >
                              {scanEngineLabel(doc)}
                            </span>
                          )}
                          {docIdentityMismatch(doc) && (
                            <span className="inline-flex items-center px-1.5 py-px rounded-md text-[10px] font-bold bg-red-50 text-red-700 ring-1 ring-red-200">
                              Identity mismatch — review individually
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Status badge */}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-bold ${
                      doc.status === 'FurtherReviewRequested' ? 'bg-violet-600 text-white ring-2 ring-violet-300 shadow-sm' :
                      doc.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' :
                      doc.status === 'Rejected' ? 'bg-red-50 text-red-700 ring-1 ring-red-200' :
                      doc.status === 'PreVetted' ? 'bg-purple-50 text-purple-700 ring-1 ring-purple-200' :
                      doc.status === 'SystemPreApproved' ? 'bg-green-50 text-green-700 ring-1 ring-green-200' :
                      doc.status === 'PendingAdminReview' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' :
                      'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                    }`}>{doc.status === 'FurtherReviewRequested' ? '⚑ Further Review Requested' : doc.status === 'Pending' ? 'Received & Pending Approval' : doc.status === 'PreVetted' ? 'Pre-Vetted' : doc.status === 'SystemPreApproved' ? 'System Pre-Approved' : doc.status === 'PendingAdminReview' ? 'Pending Admin Review' : doc.status}</span>
                    {/* Action buttons */}
                    <div className="flex items-center gap-0.5 ml-1">
                      <button onClick={() => setPreviewModal({ open: true, storagePath: doc.storagePath, fileName: doc.name, mimeType: (doc as any).mimeType })} className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer" title="Preview"><Eye className="h-3.5 w-3.5" /></button>
                      <button onClick={() => downloadFile(doc.storagePath, doc.downloadUrl, doc.name)} className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer" title="Download"><Download className="h-3.5 w-3.5" /></button>
                      {(app.active ?? true) && (
                        <>
                          {isEnhancedReferrer ? (
                            /* Enhanced referrers: Pre-Vet button instead of Approve/Reject */
                            doc.status === 'Pending' && (
                              <button
                                onClick={() => setPreVetConfirmDoc(doc)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-all cursor-pointer"
                                title="Mark as Pre-Vetted"
                              >
                                <Shield className="h-3.5 w-3.5 shrink-0" />
                                <span>Pre-Vet</span>
                              </button>
                            )
                          ) : (
                            /* Admin: full review flow */
                            <>
                          {doc.status !== 'Approved' && doc.status !== 'Rejected' && (
                            <button
                              onClick={async () => {
                                const pid = app.programId || '';
                                if (!pid) { notifyToast('warning', 'No program assigned to this application.'); return; }
                                if (!doc.docId) { notifyToast('warning', 'Missing document ID.'); return; }
                                setReviewingDocId(doc.docId);
                                try {
                                  const token = await auth.currentUser?.getIdToken();
                                  const res = await fetch('/api/documents/review', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ docId: doc.docId, applicationId: app.applicationId || app.id || app.applicantId || '', programId: pid }),
                                  });
                                  const data = await res.json();
                                  if (!res.ok) throw new Error(data.error || 'Review failed');
                                  // Just show the result — admin decides what to do with it
                                  setReviewResult({
                                    decision: data.decision,
                                    reason: data.reason || (data.decision === 'APPROVED' ? 'Document meets all program requirements.' : data.decision === 'NEEDS_REVIEW' ? 'The AI could not affirmatively pass this document — a human review is required.' : 'Document did not meet requirements.'),
                                    aiReview: data.aiReview || null,
                                    identityStatus: data.identity?.status,
                                    docId: doc.docId,
                                    applicationId: app.applicationId,
                                    applicantEmail: app.userEmail,
                                    applicantName: `${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim() || undefined,
                                    docName: doc.name,
                                  });
                                } catch (err: any) {
                                  notifyToast('error', 'AI review failed: ' + err.message);
                                } finally {
                                  setReviewingDocId(null);
                                }
                              }}
                              disabled={reviewingDocId === doc.docId}
                              className={`p-1.5 rounded-lg transition-all cursor-pointer disabled:opacity-30 ${reviewingDocId === doc.docId ? 'text-purple-500 animate-pulse bg-purple-50' : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'}`}
                              title="Review Document"
                            >
                              <Sparkles className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button onClick={() => appCtx.handleReviewDocument(doc.docId, 'Approved', '')} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer disabled:opacity-30" disabled={doc.status === 'Approved' || (appCtx.reviewInFlightDocIds || []).includes(doc.docId)} title="Approve"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => { appCtx.setRejectionModalDocId(doc.docId); appCtx.setRejectionReasonInput(''); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer disabled:opacity-30" disabled={doc.status === 'Rejected'} title="Reject"><X className="h-3.5 w-3.5" /></button>
                          </>
                          )}
                        </>
                      )}
                      <button onClick={() => { setConfirmDeleteDoc(doc); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                      <div className="relative">
                        <button onClick={() => setOpenMenuId(openMenuId === doc.docId ? null : doc.docId)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer" title="More">
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                        {openMenuId === doc.docId && (
                          <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-[9999] py-1 overflow-hidden">
                            <button onClick={() => { appCtx.setEditingDocId(doc.docId); appCtx.setEditingDocName(doc.name); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all cursor-pointer flex items-center gap-2">
                              <Edit3 className="h-3.5 w-3.5" /><span>Rename</span>
                            </button>
                            <button
                              onClick={async () => {
                                setOpenMenuId(null);
                                setReviewingDocId(doc.docId);
                                try {
                                  // Step 1: Force re-extract
                                  const token = await auth.currentUser?.getIdToken();
                                  await fetch('/api/documents/extract', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ docId: doc.docId, applicationId: app.applicationId, force: true }),
                                  });
                                  // Step 2: Re-run AI review
                                  const pid = app.programId || appCtx.programs.find(p => p.id === app.programId)?.id || '';
                                  const res = await fetch('/api/documents/review', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ docId: doc.docId, applicationId: app.applicationId || app.id || app.applicantId || '', programId: pid }),
                                  });
                                  const data = await res.json();
                                  if (!res.ok) throw new Error(data.error || 'Review failed');
                                  // Just show the result — admin decides what to do with it
                                  setReviewResult({
                                    decision: data.decision,
                                    reason: data.reason || (data.decision === 'APPROVED' ? 'Document meets all program requirements.' : data.decision === 'NEEDS_REVIEW' ? 'The AI could not affirmatively pass this document — a human review is required.' : 'Document did not meet requirements.'),
                                    aiReview: data.aiReview || null,
                                    identityStatus: data.identity?.status,
                                    docId: doc.docId,
                                    applicationId: app.applicationId,
                                    applicantEmail: app.userEmail,
                                    applicantName: `${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim() || undefined,
                                    docName: doc.name,
                                  });
                                } catch (err: any) {
                                  notifyToast('error', 'Re-scan failed: ' + err.message);
                                } finally {
                                  setReviewingDocId(null);
                                }
                              }}
                              disabled={reviewingDocId === doc.docId}
                              className="w-full text-left px-3 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 transition-all cursor-pointer flex items-center gap-2 disabled:opacity-30"
                              title="Force re-extract and re-run AI review"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${reviewingDocId === doc.docId ? 'animate-spin' : ''}`} /><span>Re-scan</span>
                            </button>
                            <button onClick={() => { setConfirmDeleteDoc(doc); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-all cursor-pointer flex items-center gap-2">
                              <Trash2 className="h-3.5 w-3.5" /><span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Reviewer info + rejection reason */}
                {(doc.status === 'Approved' || doc.status === 'Rejected') && (
                  <div className="mt-2.5 ml-12 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {doc.reviewedByName && (
                      <span className={`text-caption font-semibold ${doc.status === 'Approved' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {doc.status === 'Approved' ? '✓ Approved ' : '✗ Rejected '}by {doc.reviewedByName}
                        {doc.reviewedAt ? ` · ${formatFullDateTime(doc.reviewedAt)}` : ''}
                      </span>
                    )}
                    {(doc as any).aiReview && (
                      <span className="text-caption font-semibold text-purple-600" title={`AI decision: ${(doc as any).aiReview.decision} — ${(doc as any).aiReview.reason || ''}`}>
                        AI decision by {(doc as any).aiReview.model} · applied by {(doc as any).aiReview.appliedByName || 'admin'}
                      </span>
                    )}
                    {doc.status === 'Rejected' && doc.rejectionNote && (
                      <span className="text-caption text-slate-500 italic">Reason: “{doc.rejectionNote}”</span>
                    )}
                  </div>
                )}
                {doc.reviewLogs && doc.reviewLogs.length > 0 && (
                  <div className="mt-2 ml-12 rounded-lg bg-slate-50 border border-slate-100 p-2.5 space-y-1">
                    <p className="text-caption font-bold uppercase tracking-wider text-slate-400 mb-1">Document Logs</p>
                    {doc.reviewLogs.slice().reverse().map(log => (
                      <p key={log.id} className="text-caption text-slate-500">
                        <span className={log.action === 'Approved' ? 'text-emerald-600 font-bold' : 'text-red-600 font-bold'}>{log.action}</span>
                        {' '}by {log.byName} · {formatFullDateTime(log.at)}{(log as any).aiAssisted ? ` · AI-assisted (${(log as any).aiModel || 'AI'})` : ''}{log.note ? ` — ${log.note}` : ''}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Styled Delete Confirmation Modal */}
      {confirmDeleteDoc && (
        <Modal onClose={() => setConfirmDeleteDoc(null)} ariaLabel="Delete Document">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">Delete Document</h3>
                <p className="text-caption text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-slate-700">Are you sure you want to delete <span className="font-bold">"{confirmDeleteDoc.name}"</span>?</p>
            <div className="flex space-x-2 justify-end">
              <button onClick={() => setConfirmDeleteDoc(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
              <button onClick={() => { appCtx.handleAdminDeleteDocument(confirmDeleteDoc.docId, confirmDeleteDoc.name); setConfirmDeleteDoc(null); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all cursor-pointer">Delete</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Pre-Vet Confirmation Modal */}
      {preVetConfirmDoc && (
        <Modal onClose={() => setPreVetConfirmDoc(null)} ariaLabel="Confirm Pre-Vet">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">Pre-Vet Document</h3>
                <p className="text-caption text-slate-500">Confirm document completeness and accuracy.</p>
              </div>
            </div>
            <p className="text-sm text-slate-700">Are you sure you have pre-vetted the document <span className="font-bold">"{preVetConfirmDoc.name}"</span> and want to mark it as Pre-Vetted?</p>
            <div className="flex space-x-2 justify-end">
              <button onClick={() => setPreVetConfirmDoc(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer">Cancel</button>
              <button
                disabled={preVetLoading}
                onClick={async () => {
                  setPreVetLoading(true);
                  try {
                    const token = await auth.currentUser?.getIdToken();
                    const res = await fetch('/api/documents/review-status', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ docId: preVetConfirmDoc.docId, applicationId: app.applicationId, status: 'PreVetted' }),
                    });
                    if (res.ok) {
                      appCtx.setDocsList?.((prev: any[]) =>
                        prev.map((d: any) => d.docId === preVetConfirmDoc.docId ? { ...d, status: 'PreVetted' } : d)
                      );
                    }
                  } catch (err) {
                    console.error('Failed to pre-vet document:', err);
                  } finally {
                    setPreVetLoading(false);
                    setPreVetConfirmDoc(null);
                  }
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-semibold rounded-xl transition-all cursor-pointer"
              >
                {preVetLoading ? 'Saving...' : 'Yes, Pre-Vet'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Rejection Modal */}
      {appCtx.rejectionModalDocId && (
        <Modal onClose={() => appCtx.setRejectionModalDocId(null)} ariaLabel="Rejection Reason">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-800">Rejection Reason</h3>
            <textarea
              value={appCtx.rejectionReasonInput}
              onChange={(e) => appCtx.setRejectionReasonInput(e.target.value)}
              placeholder="Provide a reason for rejection..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl"
            />
            <div className="flex space-x-2 justify-end">
              <button onClick={() => appCtx.setRejectionModalDocId(null)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer">Cancel</button>
              <button disabled={!!appCtx.rejectionModalDocId && (appCtx.reviewInFlightDocIds || []).includes(appCtx.rejectionModalDocId)} onClick={() => appCtx.handleReviewDocument(appCtx.rejectionModalDocId, 'Rejected', appCtx.rejectionReasonInput)} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl cursor-pointer">{!!appCtx.rejectionModalDocId && (appCtx.reviewInFlightDocIds || []).includes(appCtx.rejectionModalDocId) ? 'Rejecting…' : 'Reject'}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Special Request */}
      <SectionCard title="Special Request">
        {/* CTA Button */}
        <div className="mb-4">
          <button
            onClick={() => setShowSpecialRequestModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            <span>Create Special Request</span>
          </button>
        </div>

        {/* Modal Form */}
        {showSpecialRequestModal && (
          <Modal onClose={() => setShowSpecialRequestModal(false)} ariaLabel="Create Special Request">
            <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 space-y-4 text-xs text-slate-700">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h2 className="text-sm font-bold text-slate-900">Create Special Request</h2>
                <button
                  type="button"
                  onClick={() => setShowSpecialRequestModal(false)}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <FormTemplatePickerBar
                onSelectTemplate={(tmpl) => {
                  if (tmpl.title) appCtx.setNewRequestName(tmpl.title);
                  if (tmpl.responseType) appCtx.setNewRequestResponseType(tmpl.responseType);
                  if (tmpl.priority) appCtx.setNewReqDocPriority(tmpl.priority);
                  if (tmpl.description) appCtx.setNewRequestDesc(tmpl.description);
                  if (tmpl.instructions) appCtx.setNewRequestInstructions(tmpl.instructions);
                  if (tmpl.requiredDocs) appCtx.setNewRequestRequiredDocs(tmpl.requiredDocs.join(', '));
                  if (tmpl.dropdownOptions) appCtx.setNewRequestDropdownOptions(tmpl.dropdownOptions.join(', '));
                  if (tmpl.formFields) appCtx.setNewRequestFormFields(tmpl.formFields);
                }}
                getCurrentFormData={() => ({
                  title: appCtx.newRequestName || '',
                  description: appCtx.newRequestDesc || '',
                  instructions: appCtx.newRequestInstructions || '',
                  responseType: appCtx.newRequestResponseType || 'short_text',
                  priority: appCtx.newReqDocPriority || 'Medium',
                  requiredDocs: appCtx.newRequestRequiredDocs ? appCtx.newRequestRequiredDocs.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
                  dropdownOptions: appCtx.newRequestDropdownOptions ? appCtx.newRequestDropdownOptions.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
                  formFields: appCtx.newRequestFormFields || []
                })}
              />

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Request Title</p>
                    <input
                      value={appCtx.newRequestName || ''}
                      onChange={e => appCtx.setNewRequestName(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                      placeholder="e.g. Proof of Funds"
                    />
                  </div>
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Response Format/Type</p>
                    <select
                      value={appCtx.newRequestResponseType || 'short_text'}
                      onChange={e => appCtx.setNewRequestResponseType(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                    >
                      <option value="short_text">Short Text Input</option>
                      <option value="long_text">Long Text Input</option>
                      <option value="date">Date Input</option>
                      <option value="dropdown">Dropdown Selection</option>
                      <option value="file_upload">Multiple File Upload</option>
                      <option value="form">Form (Text + File Upload)</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Priority</p>
                    <select
                      value={appCtx.newReqDocPriority || 'Medium'}
                      onChange={e => appCtx.setNewReqDocPriority(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                    >
                      {['Low', 'Medium', 'High'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Due Date & Time</p>
                    <input
                      type="datetime-local"
                      value={appCtx.newRequestDueDate || ''}
                      onChange={e => appCtx.setNewRequestDueDate(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-caption text-slate-400 font-semibold">Request Description/Instructions</p>
                    <ImproveTextDropdown
                      getValue={() => appCtx.newRequestDesc || ''}
                      onImprove={(improved) => appCtx.setNewRequestDesc(improved)}
                      isHtml={false}
                      context="request description"
                    />
                  </div>
                  <textarea
                    value={appCtx.newRequestDesc || ''}
                    onChange={e => appCtx.setNewRequestDesc(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                    placeholder="Describe the overall request..."
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-caption text-slate-400 font-semibold">Additional Response Instructions (Optional)</p>
                    <ImproveTextDropdown
                      getValue={() => appCtx.newRequestInstructions || ''}
                      onImprove={(improved) => appCtx.setNewRequestInstructions(improved)}
                      isHtml={false}
                      context="additional instructions"
                    />
                  </div>
                  <textarea
                    value={appCtx.newRequestInstructions || ''}
                    onChange={e => appCtx.setNewRequestInstructions(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                    placeholder="Any specific instructions for their response..."
                  />
                </div>
                {appCtx.newRequestResponseType === 'dropdown' && (
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Dropdown Options (Comma-separated, e.g. Yes, No, Maybe)</p>
                    <input
                      type="text"
                      value={appCtx.newRequestDropdownOptions || ''}
                      onChange={e => appCtx.setNewRequestDropdownOptions(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                      placeholder="e.g. Option A, Option B, Option C"
                    />
                  </div>
                )}

                {appCtx.newRequestResponseType === 'form' && (
                  <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <p className="text-caption text-slate-900 font-bold">Custom Form Builder Configuration</p>
                    <FormBuilder
                      fields={appCtx.newRequestFormFields || []}
                      onChange={(updatedFields) => appCtx.setNewRequestFormFields(updatedFields)}
                    />
                  </div>
                )}

                {appCtx.newRequestResponseType === 'file_upload' && (
                  <div>
                    <p className="text-caption text-slate-400 mb-1 font-semibold">Required Documents (Comma-separated, e.g. Passport, Reference 1)</p>
                    <input
                      type="text"
                      value={appCtx.newRequestRequiredDocs || ''}
                      onChange={e => appCtx.setNewRequestRequiredDocs(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-500"
                      placeholder="e.g. Passport, Reference 1, Resume"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowSpecialRequestModal(false)}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl cursor-pointer hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const success = await appCtx.handleCreateDocumentRequest();
                    if (success) {
                      setShowSpecialRequestModal(false);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl cursor-pointer transition-all shadow-md shadow-blue-500/10"
                >
                  Create Special Request
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Request List */}
        {(!appCtx.documentRequests || appCtx.documentRequests.length === 0) ? (
          <p className="text-sm text-slate-500 italic">No Special Requests created yet.</p>
        ) : (
          <div className="space-y-3">
            {appCtx.documentRequests.map((req: any) => {
              const isEditing = appCtx.editingRequestId === req.requestId;
              return (
                <div key={req.requestId} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  {isEditing ? (
                    <div className="space-y-3">
                      <p className="text-caption font-bold text-slate-500">Edit Special Request</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Request Title</p>
                          <input
                            value={appCtx.editingRequestName || ''}
                            onChange={e => appCtx.setEditingRequestName(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                          />
                        </div>
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Response Format/Type</p>
                          <select
                            value={appCtx.editingRequestResponseType || 'short_text'}
                            onChange={e => appCtx.setEditingRequestResponseType(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                          >
                            <option value="short_text">Short Text Input</option>
                            <option value="long_text">Long Text Input</option>
                            <option value="date">Date Input</option>
                            <option value="dropdown">Dropdown Selection</option>
                            <option value="file_upload">Multiple File Upload</option>
                            <option value="form">Form (Text + File Upload)</option>
                          </select>
                        </div>
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Priority</p>
                          <select
                            value={appCtx.editingRequestPriority || 'Medium'}
                            onChange={e => appCtx.setEditingRequestPriority(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                          >
                            {['Low', 'Medium', 'High'].map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Due Date & Time</p>
                          <input
                            type="datetime-local"
                            value={appCtx.editingRequestDueDate || ''}
                            onChange={e => appCtx.setEditingRequestDueDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <p className="text-caption text-slate-400 mb-1">Request Description</p>
                        <textarea
                          value={appCtx.editingRequestDesc || ''}
                          onChange={e => appCtx.setEditingRequestDesc(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                        />
                      </div>
                      <div>
                        <p className="text-caption text-slate-400 mb-1">Additional Response Instructions</p>
                        <textarea
                          value={appCtx.editingRequestInstructions || ''}
                          onChange={e => appCtx.setEditingRequestInstructions(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                        />
                      </div>
                      {appCtx.editingRequestResponseType === 'dropdown' && (
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Dropdown Options (Comma-separated)</p>
                          <input
                            type="text"
                            value={appCtx.editingRequestDropdownOptions || ''}
                            onChange={e => appCtx.setEditingRequestDropdownOptions(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                            placeholder="e.g. Yes, No, Pending"
                          />
                        </div>
                      )}

                      {appCtx.editingRequestResponseType === 'form' && (
                        <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                          <p className="text-caption text-slate-900 font-bold">Custom Form Builder Configuration</p>
                          <FormBuilder
                            fields={appCtx.editingRequestFormFields || []}
                            onChange={(updatedFields) => appCtx.setEditingRequestFormFields(updatedFields)}
                          />
                        </div>
                      )}

                      {appCtx.editingRequestResponseType === 'file_upload' && (
                        <div>
                          <p className="text-caption text-slate-400 mb-1">Required Documents (Comma-separated, e.g. Passport, Reference 1)</p>
                          <input
                            type="text"
                            value={appCtx.editingRequestRequiredDocs || ''}
                            onChange={e => appCtx.setEditingRequestRequiredDocs(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white"
                            placeholder="e.g. Passport, Reference 1, Resume"
                          />
                        </div>
                      )}
                      <div className="flex space-x-2 justify-end">
                        <button
                          onClick={() => appCtx.setEditingRequestId(null)}
                          className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={appCtx.handleUpdateDocumentRequest}
                          className="px-3 py-1.5 btn-primary text-white text-xs font-semibold rounded-xl cursor-pointer"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <p className="text-sm font-semibold text-slate-800">{req.documentName}</p>
                            <span className={`text-caption px-2 py-0.5 rounded-full font-semibold ${
                              req.priority === 'High' ? 'bg-red-50 text-red-700' :
                              req.priority === 'Medium' ? 'bg-amber-50 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>{req.priority} Priority</span>
                            <span className="text-caption px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold uppercase">
                              {req.responseType === 'short_text' ? 'Short Text' : req.responseType === 'long_text' ? 'Long Text' : 'File Upload'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 mt-1"><strong>Description:</strong> {req.description}</p>
                          {req.instructions && <p className="text-xs text-slate-500"><strong>Instructions:</strong> {req.instructions}</p>}
                          {req.dueDate && <p className="text-caption text-slate-400 mt-1">Due: {new Date(req.dueDate).toLocaleString()}</p>}
                        </div>
                        <div className="flex items-center space-x-2 shrink-0">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-caption font-semibold uppercase ${
                            req.status === 'Submitted' || req.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                            req.status === 'Viewed' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                            req.status === 'Overdue' ? 'bg-red-50 text-red-700 border border-red-200 animate-pulse' :
                            req.status === 'Expired' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
                            'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>{req.status || 'Pending'}</span>
                          {req.status !== 'Submitted' && req.status !== 'Completed' && req.status !== 'Expired' && (
                            <>
                              {/* Send Nudge/Reminder Button */}
                              <button
                                onClick={async () => {
                                  try {
                                    const token = await auth.currentUser?.getIdToken();
                                    const res = await fetch(`/api/admin/special-requests/${req.requestId}/reminder`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
                                    });
                                    if (res.ok) {
                                      notifyToast('success', 'Reminder sent successfully!');
                                    } else {
                                      const errData = await res.json().catch(() => ({}));
                                      notifyToast('error', errData.error || 'Failed to send reminder');
                                    }
                                  } catch (err: any) {
                                    notifyToast('error', `Error: ${err.message}`);
                                  }
                                }}
                                className="p-1 text-slate-400 hover:text-amber-600 cursor-pointer"
                                title="Send reminder/nudge"
                              >
                                <Bell className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                            <button
                              onClick={() => {
                                appCtx.setEditingRequestId(req.requestId);
                                appCtx.setEditingRequestName(req.documentName);
                                appCtx.setEditingRequestType(req.documentType || 'Special Request');
                                appCtx.setEditingRequestDesc(req.description || '');
                                appCtx.setEditingRequestDueDate(req.dueDate || '');
                                appCtx.setEditingRequestPriority(req.priority || 'Medium');
                                appCtx.setEditingRequestResponseType(req.responseType || 'short_text');
                                appCtx.setEditingRequestInstructions(req.instructions || '');
                                appCtx.setEditingRequestRequiredDocs(req.requiredDocs ? req.requiredDocs.join(', ') : '');
                                appCtx.setEditingRequestDropdownOptions(req.dropdownOptions ? req.dropdownOptions.join(', ') : '');
                                appCtx.setEditingRequestTextResponseType(req.textResponseType || 'short_text');
                                appCtx.setEditingRequestFormFields(Array.isArray(req.formFields) ? req.formFields : []);
                              }}
                              className="p-1 text-slate-400 hover:text-brand cursor-pointer"
                              title="Edit request"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </button>
                          <button
                            onClick={() => appCtx.handleDeleteDocumentRequest(req.requestId, req.documentName)}
                            className="p-1 text-slate-400 hover:text-red-600 cursor-pointer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Display Submitted Response */}
                      {(req.status === 'Submitted' || req.status === 'Completed') && (
                        <div className="mt-3 p-3 bg-white rounded-xl border border-slate-200 space-y-2">
                          <p className="text-xs font-bold text-slate-700">Submitted Response:</p>
                          {req.responseValue && (
                                <div className="text-xs text-slate-800 bg-slate-50 p-2.5 rounded-lg border border-slate-100 leading-relaxed">
                                  {renderFormattedResponseValue(req.responseValue, req.formFields)}
                            </div>
                          )}
                          {req.responseFiles && Array.isArray(req.responseFiles) && req.responseFiles.length > 0 && (
                            <div className="space-y-1.5">
                              {req.responseFiles.map((file: any, fIdx: number) => (
                                <div key={fIdx} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs">
                                  <span className="font-medium text-slate-700 truncate max-w-[240px]">
                                    {file.docName ? <span className="bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 rounded mr-1.5 text-[10px] uppercase">[{file.docName}]</span> : null}
                                    {file.name}
                                  </span>
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-brand hover:underline font-bold"
                                  >
                                    Download / View File
                                  </a>
                                </div>
                              ))}
                            </div>
                          )}
                          {req.uploadedAt && (
                            <p className="text-[11px] text-slate-500 text-right">Submitted at: {new Date(req.uploadedAt).toLocaleString()}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* ─── PROGRAM PACKAGE (Admin-managed document folder) ─── */}
      <ProgramPackageSection app={app} appCtx={appCtx} adminEmail={appCtx.adminEmail} isEnhancedReferrer={isEnhancedReferrer} />

      {/* ─── AI Document Analyst — hidden for enhanced referrers ─── */}
      {!isEnhancedReferrer && <AiDocumentAnalyst app={app} appCtx={appCtx} />}

      {/* Document Preview Modal */}
      {appCtx.previewDoc && (
        <Modal onClose={() => appCtx.setPreviewDoc(null)} ariaLabel="Document Preview" fullScreen>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <p className="text-sm font-bold text-slate-800">{appCtx.previewDoc.name}</p>
                <p className="text-caption text-slate-400">{appCtx.previewDoc.type} — {appCtx.previewDoc.status}</p>
              </div>
              <div className="flex items-center space-x-2">
                <a href={previewUrl || '#'} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 btn-primary  text-white text-caption font-semibold rounded-xl flex items-center space-x-1">
                  <ExternalLink className="h-3.5 w-3.5" /><span>Open in tab</span>
                </a>
                <button onClick={() => appCtx.setPreviewDoc(null)}
                  className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 cursor-pointer">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-2 bg-slate-100">
              {previewLoading ? (
                <div className="flex items-center justify-center h-[70vh] bg-white rounded-xl">
                  <Loader className="h-8 w-8 text-slate-400 animate-spin" />
                  <p className="text-sm text-slate-500 ml-3">Loading preview...</p>
                </div>
              ) : (() => {
                const rawPath = appCtx.previewDoc.storagePath || '';
                const url = previewUrl || appCtx.previewDoc.downloadUrl || rawPath;
                const name = appCtx.previewDoc.name || '';
                const type = String(appCtx.previewDoc.type || '').toLowerCase();
                const lower = `${name} ${url.split('?')[0]} ${type}`.toLowerCase();
                const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(lower) || type.includes('image');
                const isPdf = /\.pdf/i.test(lower) || type.includes('pdf');
                const isDocx = /\.docx/i.test(lower);
                const isLegacyDoc = /\.doc(?!x)/i.test(lower) || type.includes('word');
                if (isImage) return <img src={url} alt={name} className="max-w-full mx-auto rounded-xl shadow-sm bg-white" />;
                if (isPdf) return <iframe src={url} className="w-full h-[70vh] rounded-xl border-0 bg-white" title={name} />;
                if (isDocx) return <DocxPreview url={url} onHtmlReady={setDocxHtml} html={docxHtml} />;
                if (isLegacyDoc) {
                  return (
                    <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
                      <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm font-bold text-slate-700">.doc files cannot be previewed in-browser.</p>
                      <p className="text-caption text-slate-400 mt-1">Open it in a new tab to download and view locally.</p>
                      <a href={url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center space-x-1.5 btn-primary  text-white px-4 py-2 rounded-xl text-sm font-bold">
                        <ExternalLink className="h-4 w-4" /><span>Open in tab</span>
                      </a>
                    </div>
                  );
                }
                return (
                  <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
                    <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-bold text-slate-700">This file type cannot be embedded safely.</p>
                    <p className="text-caption text-slate-400 mt-1">Open it in a new tab to view it with your browser or installed apps.</p>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center space-x-1.5 btn-primary  text-white px-4 py-2 rounded-xl text-sm font-bold">
                      <ExternalLink className="h-4 w-4" /><span>Open in tab</span>
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </Modal>
      )}

      {/* ── AI Validation Popup ── */}
      <DocumentValidationPopup
        result={validationPopup}
        onClose={() => setValidationPopup(null)}
        onReUpload={() => { setValidationPopup(null); }}
        onRequestReview={() => { setValidationPopup(null); }}
      />

      {/* ── Review Confirmation Popup (ask before running AI review on uploaded docs) ── */}
      {showReviewPrompt && (
        <Modal onClose={() => { setShowReviewPrompt(false); setPendingReviewDocs([]); }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="mx-auto w-fit p-3 rounded-2xl mb-4 bg-blue-50">
              <Sparkles className="h-7 w-7 text-brand" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Run AI Review?</h3>
            <p className="text-sm text-slate-600 mb-6">
              Document(s) uploaded successfully. Would you like to run an AI review on the uploaded document{pendingReviewDocs.length > 1 ? 's' : ''}?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRunReviewOnUploaded}
                disabled={reviewingAfterUpload}
                className="w-full px-4 py-2.5 btn-primary  disabled:bg-blue-300 text-white text-sm font-semibold rounded-xl cursor-pointer disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {reviewingAfterUpload ? (
                  <><Loader className="h-4 w-4 animate-spin" /><span>Reviewing...</span></>
                ) : (
                  <><Sparkles className="h-4 w-4" /><span>Yes, Run Review</span></>
                )}
              </button>
              <button
                onClick={() => { setShowReviewPrompt(false); setPendingReviewDocs([]); }}
                disabled={reviewingAfterUpload}
                className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer disabled:cursor-not-allowed transition-all"
              >
                No, Skip
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── AI Review Result Modal ── */}
      {reviewResult && (
        <Modal onClose={() => setReviewResult(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className={`mx-auto w-fit p-3 rounded-2xl mb-4 ${reviewResult.decision === 'APPROVED' ? 'bg-emerald-50' : reviewResult.decision === 'NEEDS_REVIEW' ? 'bg-amber-50' : 'bg-red-50'}`}>
              {reviewResult.decision === 'APPROVED'
                ? <Check className="h-7 w-7 text-emerald-600" />
                : reviewResult.decision === 'NEEDS_REVIEW'
                  ? <AlertTriangle className="h-7 w-7 text-amber-600" />
                  : <X className="h-7 w-7 text-red-600" />
              }
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2 text-center">
              {reviewResult.decision === 'APPROVED' ? 'AI Review: Approved' : reviewResult.decision === 'NEEDS_REVIEW' ? 'AI Review: Needs Human Review' : 'AI Review: Rejected'}
            </h3>
            <p className="text-sm text-slate-600 mb-2 text-center">{reviewResult.reason}</p>
            {reviewResult.identityStatus === 'mismatch' && (
              <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3 text-center">
                Identity cross-check mismatch — the document's identity does not match this application. This document can never be bulk-approved; review it individually.
              </p>
            )}
            {reviewResult.aiReview?.model && (
              <p className="text-caption text-slate-400 mb-4 text-center">AI model: {reviewResult.aiReview.model}</p>
            )}
            <div className="flex flex-col gap-2">
              {aiApplyError && (
                <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-center">{aiApplyError}</p>
              )}
              {reviewResult.decision !== 'NEEDS_REVIEW' && (
                <button
                  disabled={applyingAiResult}
                  onClick={async () => {
                    // Guard engages synchronously on the click — a second
                    // click while the decision is being recorded cannot
                    // fire again (the handler is guarded server-side of
                    // the state round-trip as well).
                    if (applyingAiResult) return;
                    setApplyingAiResult(true);
                    setAiApplyError(null);
                    try {
                      const outcome = await appCtx.handleReviewDocument(
                        reviewResult.docId,
                        reviewResult.decision === 'APPROVED' ? 'Approved' : 'Rejected',
                        reviewResult.decision === 'REJECTED' ? reviewResult.reason : '',
                        reviewResult.aiReview || null
                      );
                      if (outcome === 'failed') {
                        setAiApplyError('That decision could not be recorded — please try again.');
                        return;
                      }
                      setReviewResult(null);
                    } finally {
                      setApplyingAiResult(false);
                    }
                  }}
                  className="w-full px-4 py-2.5 btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl cursor-pointer transition-all"
                >
                  {applyingAiResult ? 'Applying…' : reviewResult.decision === 'APPROVED' ? 'Apply Approval' : 'Apply Rejection'}
                </button>
              )}
              <button
                onClick={() => {
                  const text = `Document Review Result — ${reviewResult.decision}\n\nDecision: ${reviewResult.decision}\nReason: ${reviewResult.reason}\n\nReviewed by AI assistant${reviewResult.aiReview?.model ? ` (${reviewResult.aiReview.model})` : ''}`;
                  navigator.clipboard.writeText(text).then(() => {
                    notifyToast('success', 'Review result copied to clipboard.');
                  }).catch(() => {
                    // fallback
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                    notifyToast('success', 'Review result copied to clipboard.');
                  });
                }}
                className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer transition-all"
              >
                Copy Log
              </button>
              {reviewResult.applicantEmail && reviewResult.decision !== 'NEEDS_REVIEW' && (
                <button
                  onClick={async () => {
                    const resultText = reviewResult.decision === 'APPROVED'
                      ? `Your document "${reviewResult.docName || ''}" has been approved.`
                      : `Your document "${reviewResult.docName || ''}" has been reviewed: ${reviewResult.reason}`;
                    const ok = await sendSystemNotification({
                      to: reviewResult.applicantEmail,
                      toName: reviewResult.applicantName || reviewResult.applicantEmail,
                      subject: `Document Review Result — ${reviewResult.decision}`,
                      htmlBody: `<p>Dear ${reviewResult.applicantName || 'Applicant'},</p><p>${resultText}</p><p>Regards,<br/>I-SATS Team</p>`,
                      notificationType: 'document_review',
                      applicationId: reviewResult.applicationId,
                    });
                    if (ok) {
                      notifyToast('success', `Notification sent to ${reviewResult.applicantEmail}.`);
                    } else {
                      notifyToast('error', 'Failed to send notification. Please try again.');
                    }
                    setReviewResult(null);
                  }}
                  className="w-full px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-xl cursor-pointer transition-all"
                >
                  Send to Applicant Email
                </button>
              )}
              <button
                onClick={() => setReviewResult(null)}
                className="w-full px-4 py-2.5 bg-transparent hover:bg-slate-50 text-slate-500 text-sm font-medium rounded-xl cursor-pointer transition-all"
              >
                {reviewResult.decision === 'NEEDS_REVIEW' ? 'Close — I will review it myself' : 'Ignore'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Bulk AI Review Summary Modal (guarded apply) ── */}
      {bulkReviewSummary && (
        <Modal onClose={() => { if (!bulkApplying) { setBulkReviewSummary(null); setBulkAppliedIds(new Set()); setBulkSummaryJobId(null); } }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">AI Review Summary</h3>
            <p className="text-sm text-slate-500 mb-4">
              {bulkReviewSummary.filter(o => o.decision === 'APPROVED').length} approved ·{' '}
              {bulkReviewSummary.filter(o => o.decision === 'REJECTED').length} rejected ·{' '}
              {bulkReviewSummary.filter(o => o.decision === 'NEEDS_REVIEW').length} need human review
              {' '}out of {bulkReviewSummary.length} documents.{' '}
              {bulkSummaryJobId
                ? `${bulkReviewSummary.filter(o => o.appliedState === 'auto_applied').length} approval(s) were applied automatically by the background review — the rest are waiting for your decision.`
                : 'Nothing has been applied yet.'}
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
              {bulkReviewSummary.map(o => {
                const applied = bulkAppliedIds.has(o.docId);
                return (
                  <div key={o.docId} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200">
                    <span className={`mt-0.5 shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full ${o.decision === 'APPROVED' ? 'bg-emerald-50 text-emerald-600' : o.decision === 'NEEDS_REVIEW' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                      {o.decision === 'APPROVED' ? <Check className="h-3.5 w-3.5" /> : o.decision === 'NEEDS_REVIEW' ? <AlertTriangle className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-800 truncate">{o.docName}</p>
                      <p className="text-caption text-slate-500">{o.error ? `Review failed: ${o.error}` : o.reason}</p>
                      {o.identityStatus === 'mismatch' && (
                        <p className="text-caption font-semibold text-red-600">Identity mismatch — excluded from bulk approval; review individually.</p>
                      )}
                      {o.note && (
                        <p className="text-caption text-slate-500 italic">{o.note}</p>
                      )}
                    </div>
                    {o.decision === 'REJECTED' && !o.error && o.appliedState !== 'skipped_decided' && o.appliedState !== 'auto_applied' && (
                      <button
                        disabled={applied || bulkApplying || bulkRejectingDocId === o.docId}
                        onClick={async () => {
                          // Per-document confirmation for every rejection.
                          // The busy flag engages synchronously on the
                          // click, so a double-click cannot fire the
                          // decision twice (the handler is guarded too).
                          if (bulkRejectingDocId === o.docId) return;
                          setBulkRejectingDocId(o.docId);
                          try {
                            const outcome = await appCtx.handleReviewDocument(o.docId, 'Rejected', o.reason, o.aiReview ? { ...o.aiReview, source: o.aiReview.source === 'bulk_review_job' ? 'bulk_review_job' : 'bulk_review' } : null);
                            if (outcome === 'applied' || outcome === 'deduplicated') {
                              setBulkAppliedIds(prev => new Set(prev).add(o.docId));
                            }
                          } finally {
                            setBulkRejectingDocId(null);
                          }
                        }}
                        className="shrink-0 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-caption font-bold rounded-lg cursor-pointer transition-all"
                      >
                        {applied ? 'Rejected ✓' : bulkRejectingDocId === o.docId ? 'Applying…' : 'Apply rejection'}
                      </button>
                    )}
                    {o.decision === 'APPROVED' && applied && (
                      <span className="shrink-0 text-caption font-bold text-emerald-600">{o.appliedState === 'auto_applied' ? 'Auto-approved ✓ · background review' : 'Approved ✓'}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-2">
              {bulkReviewSummary.some(o => o.decision === 'APPROVED' && o.identityStatus !== 'mismatch' && o.appliedState !== 'skipped_decided' && o.appliedState !== 'auto_applied' && !bulkAppliedIds.has(o.docId)) && (
                <button
                  disabled={bulkApplying}
                  onClick={async () => {
                    setBulkApplying(true);
                    try {
                      for (const o of bulkReviewSummary) {
                        if (o.decision === 'APPROVED' && o.identityStatus !== 'mismatch' && o.appliedState !== 'skipped_decided' && o.appliedState !== 'auto_applied' && !bulkAppliedIds.has(o.docId)) {
                          const outcome = await appCtx.handleReviewDocument(o.docId, 'Approved', '', o.aiReview ? { ...o.aiReview, source: o.aiReview.source === 'bulk_review_job' ? 'bulk_review_job' : 'bulk_review' } : null);
                          if (outcome === 'applied' || outcome === 'deduplicated') {
                            setBulkAppliedIds(prev => new Set(prev).add(o.docId));
                          }
                        }
                      }
                    } finally {
                      setBulkApplying(false);
                    }
                  }}
                  className="w-full px-4 py-2.5 btn-primary text-white text-sm font-semibold rounded-xl cursor-pointer transition-all disabled:opacity-50"
                >
                  {bulkApplying ? 'Applying…' : `Apply all approvals (${bulkReviewSummary.filter(o => o.decision === 'APPROVED' && o.identityStatus !== 'mismatch' && o.appliedState !== 'skipped_decided' && o.appliedState !== 'auto_applied' && !bulkAppliedIds.has(o.docId)).length})`}
                </button>
              )}
              <button
                disabled={bulkApplying}
                onClick={() => { setBulkReviewSummary(null); setBulkAppliedIds(new Set()); setBulkSummaryJobId(null); }}
                className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer transition-all disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* ── Generated Documents ── */}
      <GeneratedDocsSection appId={app.applicationId} applicantName={getSafeApplicantName(app)} appCtx={appCtx} isEnhancedReferrer={isEnhancedReferrer} currentUserProfile={currentUserProfile} />

      {previewModal.open && (
        <DocPreviewModal
          open={previewModal.open}
          onClose={() => setPreviewModal({ open: false, storagePath: '', fileName: '' })}
          storagePath={previewModal.storagePath}
          fileName={previewModal.fileName}
          mimeType={previewModal.mimeType}
        />
      )}

    </div>
  );
});

/* ── Generated Documents Section ── */
function GeneratedDocsSection({ appId, applicantName, appCtx, isEnhancedReferrer, currentUserProfile }: { appId: string; applicantName?: string; appCtx: any; isEnhancedReferrer?: boolean; currentUserProfile?: any }) {
  const [generatedDocs, setGeneratedDocs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showGenerateModal, setShowGenerateModal] = React.useState(false);
  const [templates, setTemplates] = React.useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [generating, setGenerating] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState<{ docId: string; storagePath: string; name: string } | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [archivingId, setArchivingId] = React.useState<string | null>(null);

  const showGenToast = (type: 'success' | 'error', message: string) => {
    notifyToast(type, message);
  };

  // Fetch generated documents via server-side API (for enhanced referrers)
  // or direct Firestore (for admins)
  const fetchGenerated = React.useCallback(async () => {
    if (!appId) return;
    try {
      setLoading(true);
      if (isEnhancedReferrer) {
        const { currentUser } = auth;
        const token = currentUser ? await currentUser.getIdToken() : '';
        const res = await fetch(`/api/referrer/applications/${appId}/generated-docs`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setGeneratedDocs(data.docs || []);
        }
      } else {
        const data = await safeFetch(`/api/admin/applications/${appId}/generated-docs`);
        setGeneratedDocs(data.docs || []);
      }
    } catch (e) {
      console.error('Failed to fetch generated docs:', e);
    } finally {
      setLoading(false);
    }
  }, [appId, isEnhancedReferrer]);

  const fetchTemplates = React.useCallback(async () => {
    try {
      const data = await safeFetch('/api/admin/document-templates');
      const items = (data.templates || [])
        .filter((t: any) => (t.status || 'draft') === 'published')
        .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
      setTemplates(items);
    } catch (e) {
      console.error('Failed to fetch templates:', e);
    }
  }, []);

  React.useEffect(() => { fetchGenerated(); }, [fetchGenerated]);

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;
    try {
      setGenerating(true);
      const { currentUser } = auth;
      const token = currentUser ? await currentUser.getIdToken() : '';
      const res = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ templateId: selectedTemplateId, applicantId: appId, applicationId: appId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      showGenToast('success', 'PDF generated successfully');
      setShowGenerateModal(false);
      setSelectedTemplateId('');
      fetchGenerated();
    } catch (e: any) {
      showGenToast('error', e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { currentUser } = auth;
      const token = currentUser ? await currentUser.getIdToken() : '';
      const res = await fetch('/api/delete-generated-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ docId: confirmDelete.docId, storagePath: confirmDelete.storagePath }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'Delete failed');
      }
      showGenToast('success', `"${confirmDelete.name}" deleted`);
      setConfirmDelete(null);
      fetchGenerated();
    } catch (e: any) {
      showGenToast('error', e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveToggle = async (genDoc: any) => {
    const newStatus = genDoc.status === 'archived' ? 'completed' : 'archived';
    const docLabel = genDoc.name || genDoc.templateName || 'this document';
    if (!(await confirmDialog(newStatus === 'archived'
      ? `Archive the generated document "${docLabel}"? It will be hidden from the active list and can be restored at any time.`
      : `Restore the generated document "${docLabel}" from the archive? It will return to the active list.`))) return;
    setArchivingId(genDoc.id);
    try {
      const docRef = doc(db, 'generatedDocuments', genDoc.id);
      await updateDoc(docRef, { status: newStatus, updatedAt: new Date().toISOString() });
      showGenToast('success', `Document ${newStatus === 'archived' ? 'archived' : 'restored'}`);
      fetchGenerated();
    } catch (e: any) {
      showGenToast('error', e.message || 'Failed to update status');
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Toasts render via the global root-level notification layer. */}

      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <div>
          <h3 className="text-xs font-extrabold text-slate-900 flex items-center gap-2">
            <FileText className="h-4 w-4 text-purple-600" />
            Generated Documents
          </h3>
          <p className="text-caption text-slate-400 mt-0.5">PDFs generated from document templates</p>
        </div>
        {!isEnhancedReferrer && hasModuleAccess('doc-templates', currentUserProfile) && hasApplicantAction(currentUserProfile, 'applicant.generateDocs', 'view') && (
        <button
          onClick={() => { fetchTemplates(); setShowGenerateModal(true); }}
          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1"
        >
          <Download className="h-3 w-3" />
          Generate
        </button>
        )}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-5 w-5 text-slate-300 animate-spin" />
          </div>
        ) : generatedDocs.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-500">No generated documents yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {generatedDocs.map((doc: any) => (
              <div key={doc.id} className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                doc.status === 'archived' ? 'bg-slate-100/50 opacity-60' : 'bg-slate-50 hover:bg-slate-100'
              }`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className={`h-4 w-4 shrink-0 ${doc.status === 'archived' ? 'text-slate-400' : 'text-purple-500'}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate flex items-center gap-1.5">
                      {doc.templateName || 'Document'}
                      {doc.status === 'archived' && (
                        <span className="text-[11px] font-bold text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded-full">Archived</span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      v{doc.templateVersion || 1} · {new Date(doc.generatedAt).toLocaleDateString()}
                      {doc.fileSize && ` · ${(doc.fileSize / 1024).toFixed(0)}KB`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!isEnhancedReferrer && (
                  <button
                    onClick={() => handleArchiveToggle(doc)}
                    disabled={archivingId === doc.id}
                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all cursor-pointer disabled:opacity-40"
                    title={doc.status === 'archived' ? 'Restore' : 'Archive'}
                  >
                    {archivingId === doc.id ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                  </button>
                  )}
                  {!isEnhancedReferrer && (
                  <button
                    onClick={() => setConfirmDelete({ docId: doc.id, storagePath: doc.storagePath || '', name: doc.templateName || 'Document' })}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  )}
                  <a
                    href={doc.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </a>
                  <a
                    href={doc.pdfUrl}
                    download={buildApplicantPdfFileName(doc.templateName || 'Document', appId, applicantName)}
                    className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1"
                  >
                    <Download className="h-3 w-3" />
                    Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <Modal onClose={() => { setShowGenerateModal(false); setSelectedTemplateId(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-extrabold text-slate-900">Generate Document</h3>
              <button aria-label="Close" title="Close" onClick={() => { setShowGenerateModal(false); setSelectedTemplateId(''); }} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-1">Template</label>
                {templates.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500">No published templates available</p>
                    <p className="text-[11px] text-slate-500 mt-1">Publish a template in the Doc Templates tab first</p>
                  </div>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {templates.map(t => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplateId(t.id)}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                          selectedTemplateId === t.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <p className="text-xs font-bold text-slate-800">{t.name}</p>
                        {t.description && <p className="text-[11px] text-slate-500 mt-0.5">{t.description}</p>}
                        <p className="text-[10px] text-slate-300 mt-0.5">v{t.version || 1} · {t.paperSize || 'A4'}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setShowGenerateModal(false); setSelectedTemplateId(''); }}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={!selectedTemplateId || generating}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {generating ? <Loader className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                  {generating ? 'Generating...' : 'Generate PDF'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <Modal onClose={() => !deleting && setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Delete Document</h3>
                <p className="text-caption text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-1">
              Are you sure you want to permanently delete
            </p>
            <p className="text-sm font-bold text-slate-800 mb-4">
              "{confirmDelete.name}"?
            </p>
            <p className="text-[11px] text-slate-500 mb-5">
              The file and its record will be permanently removed.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {deleting ? <Loader className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}

/* ─── PROGRAM PACKAGE SECTION ─── */
interface ProgramPackageDoc {
  docId: string;
  name: string;
  type: string;
  uploadDate: string;
  storagePath: string;
  downloadUrl?: string;
  fileSize?: number;
}

function ProgramPackageSection({ app, appCtx, adminEmail, isEnhancedReferrer }: { app: Application; appCtx: any; adminEmail: string; isEnhancedReferrer?: boolean }) {
  const [ppDocs, setPpDocs] = useState<ProgramPackageDoc[]>([]);
  const [ppLoading, setPpLoading] = useState(true);
  // Bulk upload state
  const [ppBulkFiles, setPpBulkFiles] = useState<{ file: File; customName: string; docType: string }[]>([]);
  const [ppUploading, setPpUploading] = useState(false);
  const [ppProgress, setPpProgress] = useState<{ current: number; total: number } | null>(null);
  const [ppResult, setPpResult] = useState<string | null>(null);
  const ppFileRef = useRef<HTMLInputElement>(null);
  const [renamingPpDocId, setRenamingPpDocId] = useState<string | null>(null);
  const [renamingPpDocName, setRenamingPpDocName] = useState<string>('');
  const [ppRenaming, setPpRenaming] = useState(false);
  const [ppPreviewModal, setPpPreviewModal] = useState<{ open: boolean; storagePath: string; fileName: string }>({ open: false, storagePath: '', fileName: '' });
  const [ppStatus, setPpStatus] = useState<string>((app as any).packageStatus || 'not_ready');
  const [ppTogglingStatus, setPpTogglingStatus] = useState(false);
  const [showExportChecklist, setShowExportChecklist] = useState(false);
  const [checkedExportDocs, setCheckedExportDocs] = useState<Set<string>>(new Set());
  const [exportingZip, setExportingZip] = useState(false);
  const appId = app.applicationId;

  const fetchPpDocs = async () => {
    setPpLoading(true);
    try {
      const snap = await getDocs(collection(db, 'applications', appId, 'programPackage'));
      const list: ProgramPackageDoc[] = [];
      snap.forEach(d => list.push({ docId: d.id, ...d.data() } as ProgramPackageDoc));
      list.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
      setPpDocs(list);
    } catch (err) {
      console.warn('Failed to fetch program package docs:', err);
    } finally {
      setPpLoading(false);
    }
  };

  React.useEffect(() => { fetchPpDocs(); }, [appId]);

  const handleToggleStatus = async () => {
    setPpTogglingStatus(true);
    const newStatus = ppStatus === 'ready' ? 'not_ready' : 'ready';
    try {
      // Routed through the server Admin SDK — bypasses Firestore rules.
      await appCtx.adminProfileUpdate?.(appId, { packageStatus: newStatus });
      setPpStatus(newStatus);

      // D-4: Auto-notify applicant when package is marked ready
      if (newStatus === 'ready') {
        const { emailProgramPackageReady } = await import('../../lib/emailTriggers');
        await emailProgramPackageReady(app as any, 'Program Package');
      }
    } catch (err) {
      console.error('Failed to update package status:', err);
      notifyToast('error', 'Failed to update package status.');
    } finally {
      setPpTogglingStatus(false);
    }
  };

  const handleExportChecklistOpen = () => {
    setCheckedExportDocs(new Set());
    setShowExportChecklist(true);
  };

  const handleExportZip = async () => {
    if (checkedExportDocs.size !== ppDocs.length) return;
    setExportingZip(true);
    try {
      const selectedDocs = ppDocs.filter(d => checkedExportDocs.has(d.docId));
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/program-package/export-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ applicationId: appId, docs: selectedDocs.map(d => ({ storagePath: d.storagePath, name: d.name })) }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `program-package-${appId.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setShowExportChecklist(false);
    } catch (err) {
      console.error('Export zip failed:', err);
      notifyToast('error', 'Export failed: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setExportingZip(false);
    }
  };

  React.useEffect(() => { setPpStatus((app as any).packageStatus || 'not_ready'); }, [(app as any).packageStatus]);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handlePpBulkFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files).map(f => ({ file: f, customName: f.name.replace(/\.[^/.]+$/, ''), docType: 'Program Document' }));
      setPpBulkFiles(prev => [...prev, ...newFiles]);
    }
    e.target.value = '';
  };

  const handlePpBulkFileRemove = (index: number) => {
    setPpBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePpBulkFileNameChange = (index: number, name: string) => {
    setPpBulkFiles(prev => prev.map((item, i) => i === index ? { ...item, customName: name } : item));
  };

  const handlePpBulkFileDocTypeChange = (index: number, docType: string) => {
    setPpBulkFiles(prev => prev.map((item, i) => i === index ? { ...item, docType } : item));
  };

  const handlePpBulkUpload = async () => {
    if (ppBulkFiles.length === 0) { notifyToast('warning', 'Please select at least one file.'); return; }
    setPpUploading(true);
    const token = await auth.currentUser?.getIdToken();
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < ppBulkFiles.length; i++) {
      setPpProgress({ current: i + 1, total: ppBulkFiles.length });
      const { file, customName, docType } = ppBulkFiles[i];
      const ext = file.name.split('.').pop() || '';
      const finalName = `${customName}.${ext}`;
      try {
        const base64Data = await readFileAsBase64(file);
        const res = await fetch('/api/program-package/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ applicationId: appId, filename: finalName, mimeType: file.type, base64Data, docType }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        const docMeta: ProgramPackageDoc = { docId: data.docId, name: data.name, type: docType, uploadDate: new Date().toISOString(), storagePath: data.storagePath, downloadUrl: data.downloadUrl };
        setPpDocs(prev => [docMeta, ...prev]);

        // Notify for the first uploaded doc only
        if (successCount === 0 && i === 0) {
          const appData = app as any;
          const applicantName = `${appData.personalDetails?.firstName || ''} ${appData.personalDetails?.lastName || ''}`.trim() || appData.userEmail || 'Applicant';
          const applicantEmail = appData.userEmail || appData.personalDetails?.email || '';
          if (applicantEmail && (await confirmNotification(`Notify "${applicantName}" that ${ppBulkFiles.length} document(s) have been added to their package?`))) {
            const isReady = ppStatus === 'ready';
            const bodyHtml = isReady
              ? `<p>${ppBulkFiles.length} new document(s) have been added to your program package:</p><ul>${ppBulkFiles.map(bf => `<li><strong>${bf.customName}.${bf.file.name.split('.').pop()}</strong> (${bf.docType})</li>`).join('')}</ul><p>You can view these documents now. Use the <strong>Export / Download Package</strong> option to download all documents together.</p>`
              : `<p>${ppBulkFiles.length} new document(s) have been added to your program package:</p><ul>${ppBulkFiles.map(bf => `<li><strong>${bf.customName}.${bf.file.name.split('.').pop()}</strong> (${bf.docType})</li>`).join('')}</ul><p>Please note that your package is <strong>not yet available for download</strong>. You will be notified once it is marked as "Package Ready." In the meantime, you can view individual documents from your portal.</p>`;
            await sendSystemNotification({
              to: applicantEmail,
              toName: applicantName,
              subject: `New Documents in Your Program Package (${ppBulkFiles.length} added)`,
              htmlBody: buildEmailTemplate({
                applicantName,
                title: 'Documents Added to Your Package',
                bodyHtml,
                portalUrl: (window.__FRONTEND_URL || 'https://portal.isats.org') + '/applicant',
                ctaText: 'View My Documents',
              }),
              notificationType: 'package_document_added',
              applicationId: appId,
            });
          }
        }
        successCount++;
      } catch (err) {
        console.error(`Upload failed for ${finalName}:`, err);
        failCount++;
      }
    }
    setPpUploading(false);
    setPpProgress(null);
    setPpBulkFiles([]);
    if (ppFileRef.current) ppFileRef.current.value = '';
    if (failCount === 0) {
      setPpResult(`Successfully uploaded ${successCount} file(s)!`);
    } else {
      setPpResult(`${successCount} uploaded, ${failCount} failed`);
    }
    setTimeout(() => setPpResult(null), 5000);
  };

  const handlePpDelete = async (docId: string, docName: string) => {
    if (!(await confirmDialog(`Delete "${docName}" from Program Package?`))) return;
    try {
      const docData = ppDocs.find(d => d.docId === docId);
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/program-package/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ docId, applicationId: appId, storagePath: docData?.storagePath }),
      });
      if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Delete failed'); }
      setPpDocs(prev => prev.filter(d => d.docId !== docId));
    } catch (err) {
      console.error('Delete program package doc failed:', err);
    }
  };

  const handlePpRename = async (docId: string, newName: string) => {
    if (!newName.trim()) return;
    setPpRenaming(true);
    try {
      // DESIGN NOTE: While program package creations are routed via secure server endpoints
      // (e.g., /api/program-package/upload) to ensure atomic integration with Cloud Storage,
      // rename operations are handled direct client-side via updateDoc() under firestore.rules.
      await appCtx.ensureAdminClaimReady?.();
      await updateDoc(doc(db, 'applications', appId, 'programPackage', docId), { name: newName.trim() });
      setPpDocs(prev => prev.map(d => d.docId === docId ? { ...d, name: newName.trim() } : d));
      setRenamingPpDocId(null);
      setRenamingPpDocName('');
    } catch (err) {
      console.error('Rename program package doc failed:', err);
      notifyToast('error', 'Rename failed: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setPpRenaming(false);
    }
  };

  return (<>
    <SectionCard title="Program Package" action={
      <div className="flex items-center space-x-2">
        {/* Status badge */}
        <span className={`text-caption font-extrabold px-2.5 py-1 rounded-lg border inline-flex items-center space-x-1 ${
          ppStatus === 'ready'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-500 border-slate-200'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${ppStatus === 'ready' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
          <span>{ppStatus === 'ready' ? 'Package Ready' : 'Not Ready'}</span>
        </span>
        <span className="text-caption font-semibold text-purple-600 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-100">Admin-Managed</span>
      </div>
    }>
      <div className="space-y-4">
        <p className="text-caption text-slate-500 leading-relaxed">
          This section contains official program documents managed by the administration. These are read-only documents available for your reference.
        </p>

        {/* Status Toggle + Export */}
        {!isEnhancedReferrer && (
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3">
          <div className="flex items-center space-x-3">
            <button type="button" onClick={handleToggleStatus} disabled={ppTogglingStatus}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
                ppStatus === 'ready' ? 'bg-emerald-500' : 'bg-slate-300'
              }`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                ppStatus === 'ready' ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className="text-xs font-bold text-slate-700">
              {ppStatus === 'ready' ? 'Package is Ready' : 'Mark as Package Ready'}
            </span>
          </div>
          {ppDocs.length > 0 && (
            <button type="button" onClick={handleExportChecklistOpen}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 shadow-sm">
              <Download className="h-3.5 w-3.5" />
              <span>Export ZIP</span>
            </button>
          )}
        </div>
        )}

        {/* Admin Upload Panel */}
        {!isEnhancedReferrer && (
        <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-4 space-y-3">
          <p className="text-caption font-bold text-purple-700">Upload Program Documents</p>
          <div className="flex flex-col gap-3">
            <input
              ref={ppFileRef}
              type="file"
              multiple
              onChange={handlePpBulkFilesSelected}
              className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
            />

            {/* Selected files list */}
            {ppBulkFiles.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-caption font-bold text-slate-600">{ppBulkFiles.length} file(s) selected</p>
                <div className="max-h-60 overflow-y-auto space-y-1.5">
                  {ppBulkFiles.map((bf, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded-lg border border-purple-200 px-3 py-2">
                      <select
                        value={bf.docType}
                        onChange={(e) => handlePpBulkFileDocTypeChange(idx, e.target.value)}
                        className="w-28 px-1.5 py-1 text-[10px] font-semibold border border-slate-200 rounded-md bg-white outline-none focus:border-purple-500 shrink-0"
                      >
                        {['Program Document', 'Host Company Agreement', 'Insurance Form', 'DS-2019', 'Placement Letter', 'Program Guide', 'Other'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <input
                        value={bf.customName}
                        onChange={(e) => handlePpBulkFileNameChange(idx, e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1 text-xs font-medium border border-slate-200 rounded-md bg-white outline-none focus:border-purple-500"
                        placeholder="File name"
                      />
                      <span className="text-caption text-slate-400 shrink-0">.{bf.file.name.split('.').pop()}</span>
                      <button onClick={() => handlePpBulkFileRemove(idx)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all cursor-pointer" title="Remove">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress */}
            {ppProgress && (
              <div className="flex items-center gap-2 text-caption text-purple-700 font-semibold">
                <Loader className="h-3.5 w-3.5 animate-spin" />
                <span>Uploading {ppProgress.current} of {ppProgress.total}...</span>
              </div>
            )}

            {/* Result */}
            {ppResult && (
              <div className={`text-caption font-semibold ${ppResult.includes('failed') ? 'text-red-600' : 'text-emerald-600'}`}>
                {ppResult}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePpBulkUpload}
                disabled={ppBulkFiles.length === 0 || ppUploading}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-xl text-sm font-semibold transition-all flex items-center space-x-2 cursor-pointer disabled:cursor-not-allowed"
              >
                {ppUploading ? (
                  <><Loader className="h-4 w-4 animate-spin" /><span>Uploading...</span></>
                ) : (
                  <><Upload className="h-4 w-4" /><span>Upload All ({ppBulkFiles.length})</span></>
                )}
              </button>
              {ppBulkFiles.length > 0 && !ppUploading && (
                <button onClick={() => { setPpBulkFiles([]); setPpResult(null); }} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Document List */}
        {ppLoading ? (
          <div className="py-6 text-center"><Loader className="animate-spin h-5 w-5 text-purple-600 mx-auto" /></div>
        ) : ppDocs.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl">
            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-500">No program documents uploaded yet</p>
            <p className="text-caption text-slate-400">Administrators can upload documents here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {ppDocs.map((doc) => (
              <div key={doc.docId} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-purple-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate max-w-[250px]">{doc.name}</p>
                    <p className="text-caption text-slate-400">{doc.type} — {formatFullDateTime(doc.uploadDate)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 shrink-0">
                  <button
                    onClick={() => setPpPreviewModal({ open: true, storagePath: doc.storagePath, fileName: doc.name })}
                    className="p-1.5 text-slate-400 hover:text-brand rounded-lg hover:bg-white cursor-pointer"
                    title="Preview"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => downloadFile(doc.storagePath, doc.downloadUrl, doc.name)}
                    className="p-1.5 text-slate-400 hover:text-brand rounded-lg hover:bg-white cursor-pointer"
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {renamingPpDocId === doc.docId ? (
                    <div className="flex items-center gap-1">
                      <input
                        value={renamingPpDocName}
                        onChange={(e) => setRenamingPpDocName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handlePpRename(doc.docId, renamingPpDocName); if (e.key === 'Escape') { setRenamingPpDocId(null); setRenamingPpDocName(''); } }}
                        className="w-36 px-2 py-1 text-xs border border-purple-300 rounded-lg"
                        autoFocus
                        disabled={ppRenaming}
                      />
                      <button onClick={() => handlePpRename(doc.docId, renamingPpDocName)} disabled={ppRenaming} className="p-1 text-purple-600 hover:text-purple-800 cursor-pointer disabled:opacity-50" title="Save">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => { setRenamingPpDocId(null); setRenamingPpDocName(''); }} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer" title="Cancel">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    !isEnhancedReferrer && (
                    <button
                      onClick={() => { setRenamingPpDocId(doc.docId); setRenamingPpDocName(doc.name); }}
                      className="p-1.5 text-slate-400 hover:text-purple-600 rounded-lg hover:bg-white cursor-pointer"
                      title="Rename"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    )
                  )}
                  {!isEnhancedReferrer && (
                  <button
                    onClick={() => handlePpDelete(doc.docId, doc.name)}
                    className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-white cursor-pointer"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Export Checklist Modal */}
      {showExportChecklist && (
        <Modal onClose={() => !exportingZip && setShowExportChecklist(false)} ariaLabel="Confirm Package Export">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 shrink-0">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Confirm Package Export</h3>
                <p className="text-caption text-slate-500 mt-0.5">Please confirm you have reviewed each document before downloading.</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowExportChecklist(false)} disabled={exportingZip}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer disabled:opacity-40">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {ppDocs.map(doc => (
                <label key={doc.docId} className={`flex items-center space-x-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  checkedExportDocs.has(doc.docId) ? 'border-purple-300 bg-purple-50' : 'border-slate-100 bg-white hover:bg-slate-50'
                }`}>
                  <input type="checkbox" checked={checkedExportDocs.has(doc.docId)} onChange={() => {
                    setCheckedExportDocs(prev => {
                      const next = new Set(prev);
                      if (next.has(doc.docId)) next.delete(doc.docId); else next.add(doc.docId);
                      return next;
                    });
                  }} className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500 shrink-0 cursor-pointer" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800 truncate">{doc.name}</p>
                    <p className="text-caption text-slate-400">{doc.type}</p>
                  </div>
                  <FileText className="h-5 w-5 text-purple-400 shrink-0" />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 shrink-0 bg-slate-50 rounded-b-2xl">
              <span className="text-caption font-semibold text-slate-500">{checkedExportDocs.size} of {ppDocs.length} confirmed</span>
              <button type="button" onClick={handleExportZip} disabled={checkedExportDocs.size !== ppDocs.length || exportingZip}
                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-bold text-xs rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5 shadow-sm">
                {exportingZip ? (
                  <><RefreshCw className="h-3.5 w-3.5 animate-spin" /><span>Exporting...</span></>
                ) : (
                  <><Download className="h-3.5 w-3.5" /><span>Confirm &amp; Export ({ppDocs.length} docs)</span></>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SectionCard>

    {ppPreviewModal.open && (
      <DocPreviewModal
        open={ppPreviewModal.open}
        onClose={() => setPpPreviewModal({ open: false, storagePath: '', fileName: '' })}
        storagePath={ppPreviewModal.storagePath}
        fileName={ppPreviewModal.fileName}
      />
    )}
  </>);
}

/* ─── AI DOCUMENT ANALYST ─── */
function AiDocumentAnalyst({ app, appCtx }: { app: Application; appCtx: any }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'ai'; text: string }[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAnalyze = async () => {
    if (!prompt.trim()) return;
    const userPrompt = prompt.trim();
    setPrompt('');
    setMessages(prev => [...prev, { role: 'user', text: userPrompt }]);
    setAnalyzing(true);

    try {
      // Collect documents, extracting text on-demand for any that are missing it
      const documents: { name: string; text: string }[] = [];
      for (const d of (appCtx.docsList || [])) {
        if (d.extractedText) {
          documents.push({ name: d.name || 'document', text: d.extractedText });
        } else {
          // On-demand extraction for documents without pre-extracted text
          try {
            const token = await auth.currentUser?.getIdToken();
            const extractRes = await fetch('/api/documents/extract', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ docId: d.docId, applicationId: app.applicationId }),
            });
            if (extractRes.ok) {
              const data = await extractRes.json();
              const text = data.text || '';
              if (text) {
                documents.push({ name: d.name || 'document', text });
              }
            }
          } catch (_) {
            // Silently skip documents that fail on-demand extraction
          }
        }
      }

      if (documents.length === 0) {
        setMessages(prev => [...prev, {
          role: 'ai',
          text: 'No readable document text found. Make sure files are PDFs or DOCX format, or try re-uploading them so their text can be extracted.',
        }]);
        setAnalyzing(false);
        return;
      }

      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/documents/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: userPrompt, documents }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      // M-5: Sanitize AI output before displaying (strip all HTML tags)
      const safeAnswer = typeof data.answer === 'string' ? DOMPurify.sanitize(data.answer, { ALLOWED_TAGS: [] }) : '';
      setMessages(prev => [...prev, { role: 'ai', text: safeAnswer }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Error: ' + (err instanceof Error ? err.message : 'Analysis failed') }]);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAnalyze(); }
  };

  return (
    <SectionCard title={
      <div className="flex items-center space-x-2 cursor-pointer" onClick={() => setOpen(!open)}>
        <Sparkles className="h-4 w-4 text-amber-500" />
        <span>AI Document Analyst</span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
      </div>
    }>
      {open && (
        <div className="space-y-4">
          <p className="text-caption text-slate-500">Ask questions about the uploaded documents. The AI will analyze them and provide answers.</p>

          {/* Chat Messages */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 max-h-[300px] overflow-y-auto space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <Sparkles className="h-6 w-6 mx-auto mb-2 text-amber-400" />
                <p className="text-sm font-semibold">Ask anything about the documents</p>
                <p className="text-caption mt-1">e.g. "Summarize my documents" or "Is my passport expired?"</p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'btn-primary text-white rounded-br-md'
                      : 'bg-white border border-slate-200 text-slate-700 rounded-bl-md shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))
            )}
            {analyzing && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-xl rounded-bl-md px-4 py-2.5 flex items-center space-x-2">
                  <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full" />
                  <span className="text-sm text-slate-500 font-medium">Analyzing documents...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="flex items-start space-x-2">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about the documents..."
              rows={2}
              className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 resize-none"
            />
            <button aria-label="Send prompt" title="Send prompt"
              onClick={handleAnalyze}
              disabled={analyzing || !prompt.trim()}
              className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white rounded-xl text-sm font-semibold transition-all flex items-center space-x-1.5 cursor-pointer disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {!open && (
        <p className="text-caption text-slate-400 mt-1">Click to expand and ask AI about uploaded documents.</p>
      )}
    </SectionCard>
  );
}

/* ─── Shared helper to read & analyze documents ─── */

function DocxPreview({ url, html, onHtmlReady }: {
  url: string;
  html: string;
  onHtmlReady: (html: string) => void;
}) {
  const [loading, setLoading] = useState(!html);
  const [error, setError] = useState(false);

  React.useEffect(() => {
    if (html) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    (async () => {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error(`Failed to fetch document: ${res.status}`);
        const arrayBuffer = await res.arrayBuffer();
        const mammothImport: any = await import('mammoth');
        const mammoth = mammothImport.default || mammothImport;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) onHtmlReady(result.value);
      })()
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, html, onHtmlReady]);

  if (loading) return (
    <div className="flex items-center justify-center h-[70vh] bg-white rounded-xl">
      <Loader className="h-8 w-8 text-slate-400 animate-spin" />
      <p className="text-sm text-slate-500 ml-3">Converting document...</p>
    </div>
  );

  if (error) return (
    <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
      <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
      <p className="text-sm font-bold text-slate-700">Could not convert this document.</p>
      <a href={url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center space-x-1.5 btn-primary  text-white px-4 py-2 rounded-xl text-sm font-bold">
        <ExternalLink className="h-4 w-4" /><span>Open in tab</span>
      </a>
    </div>
  );

  return (
    <div
      className="bg-white rounded-xl p-6 prose prose-sm max-w-none overflow-y-auto max-h-[70vh]"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ['p','b','i','br','ul','ol','li','a','strong','em','u','h1','h2','h3','h4','h5','h6','blockquote','pre','code','span','div','table','thead','tbody','tr','th','td','img','hr'], ALLOWED_ATTR: ['href','src','alt','class','target','rel'] }) }}
    />
  );
}

/* ───────────────────── ACTIVITY TAB ───────────────────── */

const ActivityTab = React.memo(function ActivityTab({ app, appCtx, isEnhancedReferrer, canViewNotes, canEditNotes, canViewComments, canEditComments }: { app: Application; appCtx: any; isEnhancedReferrer?: boolean; canViewNotes: boolean; canEditNotes: boolean; canViewComments: boolean; canEditComments: boolean }) {
  const [activityFilter, setActivityFilter] = useState<string>('all');
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [employerLogs, setEmployerLogs] = useState<any[]>([]);

  // Comments are always loaded through the server so fine-grained custom-role
  // grants and assigned-applicant scope are enforced independently of UI state.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isEnhancedReferrer) {
          const token = await auth.currentUser?.getIdToken();
          const res = await fetch(`/api/referrer/applications/${app.applicationId}/comments`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const data = await res.json();
            const comms = (data.comments || []).sort((a: any, b: any) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
            if (!cancelled) {
              const existing = appCtx.comments || [];
              if (JSON.stringify(comms) !== JSON.stringify(existing)) {
                appCtx.setComments(comms);
              }
            }
          }
        } else if (canViewComments) {
          const token = await auth.currentUser?.getIdToken();
          const res = await fetch(`/api/admin/applications/${encodeURIComponent(app.applicationId)}/comments`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workspaceHeader() },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (!cancelled) appCtx.setComments(data.comments || []);
        }
      } catch (err) {
        console.warn("ActivityTab comment fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [app.applicationId, isEnhancedReferrer, canViewComments]);

  // Fetch activity log
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await appCtx.getToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`/api/admin/applications/${app.applicationId}/activity-log`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setActivityLogs(data);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [app.applicationId]);

  // Fetch employer timeline logs (from applicationTimeline collection)
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qRef = query(
          collection(db, 'applicationTimeline'),
          where('applicationId', '==', app.applicationId)
        );
        const snap = await getDocs(qRef);
        const entries = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setEmployerLogs(entries);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [app.applicationId]);

  // Build unified feed from comments + audit logs + activity logs
  const feedItems = useMemo(() => {
    const items: { id: string; type: string; timestamp: string; ts: number; data: any }[] = [];

    if (canViewComments) {
      (appCtx.comments || []).forEach((c: Comment) => {
        items.push({ id: `comment-${c.commentId}`, type: 'comment', timestamp: c.createdAt, ts: new Date(c.createdAt).getTime(), data: c });
        (c.replies || []).forEach((r: any) => {
          items.push({ id: `reply-${c.commentId}-${r.replyId}`, type: 'reply', timestamp: r.createdAt, ts: new Date(r.createdAt).getTime(), data: { ...r, parentCommentId: c.commentId } });
        });
      });
    }

    (appCtx.auditLogs || [])
      .filter((l: AuditLog) => l.targetId === app.applicationId)
      .forEach((l: AuditLog) => {
        items.push({ id: `log-${l.logId}`, type: 'audit', timestamp: l.timestamp, ts: new Date(l.timestamp).getTime(), data: l });
      });

    activityLogs.forEach((al: any) => {
      items.push({ id: `activity-${al.activityId}`, type: 'activity', timestamp: al.timestamp, ts: new Date(al.timestamp).getTime(), data: al });
    });

    employerLogs.forEach((el: any) => {
      // Only include genuine employer actions so the feed stays focused
      if (el.performedByRole === 'employer' || el.actorRole === 'employer') {
        items.push({ id: `employer-${el.id || el.activityId || Math.random()}`, type: 'employer', timestamp: el.timestamp, ts: new Date(el.timestamp).getTime(), data: el });
      }
    });

    return items.sort((a, b) => a.ts - b.ts);
  }, [appCtx.comments, appCtx.auditLogs, activityLogs, employerLogs, app.applicationId, canViewComments]);

  const filteredItems = feedItems.filter(item => {
    if (activityFilter === 'all') return true;
    return item.type === activityFilter;
  });

  // Group by date
  const grouped = useMemo(() => {
    const groups: { date: string; items: typeof feedItems }[] = [];
    filteredItems.forEach(item => {
      const label = new Date(item.timestamp).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      const existing = groups.find(g => g.date === label);
      if (existing) existing.items.push(item);
      else groups.push({ date: label, items: [item] });
    });
    return groups;
  }, [filteredItems]);

  const typeIcons: Record<string, any> = {
    comment: MessageSquare,
    reply: MessageSquare,
    audit: Activity,
    activity: Activity,
    employer: Briefcase,
  };

  const typeColors: Record<string, string> = {
    comment: 'bg-blue-100 text-brand',
    reply: 'bg-slate-100 text-slate-600',
    audit: 'bg-amber-100 text-amber-700',
    activity: 'bg-purple-100 text-purple-700',
    employer: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="space-y-6">
      {/* Admin Notes — hidden for enhanced referrers */}
      {!isEnhancedReferrer && canViewNotes && (
      <SectionCard title="Admin Notes">
        <div className="space-y-3">
          <textarea
            value={appCtx.adminNoteInput || ''}
            onChange={(e) => appCtx.setAdminNoteInput(e.target.value)}
            disabled={!canEditNotes}
            rows={8}
            placeholder="Internal notes about this application..."
            className="w-full px-5 py-4 text-sm border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-amber-50/70 font-mono leading-7 shadow-inner"
            style={{ backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 27px, rgba(251, 191, 36, 0.25) 28px)' }}
          />
          <div className="flex items-center justify-between">
            <div className="flex flex-col text-[11px] text-slate-500 leading-normal">
              {app.adminNotesModifiedBy ? (
                <>
                  <p>Last modified by: <span className="font-semibold text-slate-600">{app.adminNotesModifiedBy}</span></p>
                  <p>Last modified: <span className="font-semibold text-slate-600">{new Date(app.adminNotesModifiedAt || app.updatedAt).toLocaleDateString()}</span> at <span className="font-semibold text-slate-600">{new Date(app.adminNotesModifiedAt || app.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></p>
                </>
              ) : (
                app.updatedAt && <p>Last saved: {new Date(app.updatedAt).toLocaleString()}</p>
              )}
            </div>
            {canEditNotes && <button
              onClick={() => appCtx.handleUpdateAdminNotes(appCtx.adminNoteInput)}
              className="px-4 py-2 btn-primary  text-white text-sm font-semibold rounded-xl transition-all cursor-pointer"
            >
              Save Notes
            </button>}
          </div>
        </div>
      </SectionCard>
      )}

      {/* Unified Activity Feed */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Filter bar */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center space-x-2 overflow-x-auto">
          <ListFilter className="h-4 w-4 text-slate-400 shrink-0" />
          {[
            { id: 'all', label: 'All Activity' },
            { id: 'comment', label: 'Comments' },
            { id: 'partner', label: 'Partner/Supplier Comments' },
            { id: 'activity', label: 'Job Activity' },
            { id: 'audit', label: 'Changes' },
            { id: 'employer', label: 'Employer Actions' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setActivityFilter(f.id)}
              className={`px-3 py-1.5 rounded-xl text-caption font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activityFilter === f.id ? 'bg-blue-100 text-brand' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Feed */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-body font-semibold text-slate-500">No activity yet</p>
            <p className="text-caption text-slate-400 mt-1">Activity will appear here as comments and status changes are made.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {grouped.map(group => (
              <div key={group.date}>
                <div className="px-5 py-3 bg-slate-50/80">
                  <p className="text-caption font-bold text-slate-500">{group.date}</p>
                </div>
                {group.items.map(item => {
                  const Icon = typeIcons[item.type] || Activity;
                  const color = typeColors[item.type] || 'bg-slate-100 text-slate-600';

                  if (item.type === 'comment') {
                    const c = item.data as Comment;
                    return (
                      <div key={item.id} className="px-5 py-4 hover:bg-slate-50/50 transition-all">
                        <div className="flex items-start space-x-3">
                          <div className={`h-8 w-8 rounded-full ${color} flex items-center justify-center text-xs font-bold shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-bold text-slate-800">{c.adminName}</p>
                              <span className="text-caption text-slate-400">{formatFullDateTime(c.createdAt)}</span>
                              {c.edited && <span className="text-caption font-semibold text-amber-600">edited · {formatFullDateTime(c.updatedAt)}</span>}
                            </div>
                            {appCtx.editingCommentId === c.commentId ? (
                              <div className="mt-2 space-y-2">
                                <textarea value={appCtx.editingCommentText} onChange={(e) => appCtx.setEditingCommentText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); appCtx.handleEditComment(c.commentId, appCtx.editingCommentText); } }} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" autoFocus />
                                <div className="flex items-center gap-2">
                                  <button onClick={() => appCtx.handleEditComment(c.commentId, appCtx.editingCommentText)} className="px-3 py-1.5 btn-primary text-white text-caption font-bold rounded-lg">Save</button>
                                  <button onClick={() => { appCtx.setEditingCommentId(null); appCtx.setEditingCommentText(''); }} className="px-3 py-1.5 bg-white border border-slate-200 text-caption font-bold rounded-lg">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                            )}
                            {c.fileUrl && (
                              <a href={c.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 mt-1.5 text-caption font-semibold text-brand hover:text-brand">
                                <Paperclip className="h-3 w-3" /><span>{c.fileName || 'Attachment'}</span>
                              </a>
                            )}
                            {/* Replies */}
                            <div className="mt-2 space-y-1.5">
                              {(c.replies || []).map((r: any) => {
                                const isEditingReply = appCtx.editingReplyId === r.replyId;
                                return (
                                  <div key={r.replyId} className="flex items-start space-x-2 ml-2 pl-3 border-l-2 border-slate-200 py-1">
                                    <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-caption font-bold text-slate-600 shrink-0">{r.adminName?.charAt(0) || 'A'}</div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-1.5">
                                        <p className="text-caption font-bold text-slate-700">{r.adminName}</p>
                                        <span className="text-caption text-slate-400">{new Date(r.createdAt).toLocaleString()}</span>
                                        {r.edited && <span className="text-caption font-semibold text-amber-600">edited · {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : ''}</span>}
                                      </div>
                                      {isEditingReply ? (
                                        <div className="mt-1 space-y-1.5">
                                          <textarea value={appCtx.editingReplyText} onChange={(e) => appCtx.setEditingReplyText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); appCtx.handleEditReply(c.commentId, r.replyId, appCtx.editingReplyText); } }} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" autoFocus />
                                          <div className="flex items-center gap-2">
                                            <button onClick={() => appCtx.handleEditReply(c.commentId, r.replyId, appCtx.editingReplyText)} className="px-3 py-1.5 btn-primary text-white text-caption font-bold rounded-lg">Save</button>
                                            <button onClick={() => { appCtx.setEditingReplyId(null); appCtx.setEditingReplyText(''); }} className="px-3 py-1.5 bg-white border border-slate-200 text-caption font-bold rounded-lg">Cancel</button>
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-600">{r.text}</p>
                                      )}
                                      {canEditComments && !isEditingReply && (
                                        <button onClick={() => { appCtx.setEditingReplyId(r.replyId); appCtx.setEditingReplyText(r.text); }} className="text-caption font-semibold text-slate-500 hover:text-brand cursor-pointer">
                                          Edit
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Reply toggle */}
                            <button
                              onClick={() => appCtx.setOpenReplyId(appCtx.openReplyId === c.commentId ? null : c.commentId)}
                              className="mt-1.5 text-caption font-semibold text-slate-500 hover:text-brand cursor-pointer flex items-center space-x-1"
                            >
                              {appCtx.openReplyId === c.commentId ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              <span>Reply</span>
                            </button>
                            {canEditComments && appCtx.editingCommentId !== c.commentId && (
                              <button onClick={() => { appCtx.setEditingCommentId(c.commentId); appCtx.setEditingCommentText(c.text); }} className="ml-3 mt-1.5 text-caption font-semibold text-slate-500 hover:text-brand cursor-pointer">
                                Edit
                              </button>
                            )}
                            {canEditComments && (
                              <button onClick={async () => { if (await confirmDialog('Delete this internal comment?')) appCtx.handleDeleteComment(c.commentId); }} className="ml-3 mt-1.5 text-caption font-semibold text-rose-500 hover:text-rose-700 cursor-pointer">
                                Delete
                              </button>
                            )}
                            {appCtx.openReplyId === c.commentId && (
                              <div className="mt-1.5 flex items-center space-x-2">
                                <input value={appCtx.replyInputs?.[c.commentId] || ''} onChange={(e) => appCtx.setReplyInputs({ ...appCtx.replyInputs, [c.commentId]: e.target.value })} placeholder="Write a reply..." className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl" />
                                <button aria-label="Send reply" title="Send reply" onClick={() => appCtx.handleAddReply(c.commentId)} className="p-2 btn-primary  text-white rounded-xl transition-all cursor-pointer"><Send className="h-4 w-4" /></button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'audit') {
                    const log = item.data as AuditLog;
                    return (
                      <div key={item.id} className="px-5 py-4 hover:bg-slate-50/50 transition-all">
                        <div className="flex items-start space-x-3">
                          <div className={`h-8 w-8 rounded-full ${color} flex items-center justify-center text-xs font-bold shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-bold text-slate-800">{log.action}</p>
                              <span className="text-caption text-slate-400">{new Date(log.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-caption text-slate-500 mt-0.5">{log.adminName} — {log.details}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'activity') {
                    const al = item.data as any;
                    const badgeColor: Record<string, string> = {
                      job_applied: 'bg-blue-100 text-brand',
                      job_withdrawn: 'bg-slate-100 text-slate-600',
                      admin_assigned: 'bg-emerald-100 text-emerald-700',
                      match_status_changed: 'bg-amber-100 text-amber-700',
                      job_status_changed: 'bg-amber-100 text-amber-700',
                    };
                    const badgeLabel: Record<string, string> = {
                      job_applied: 'Applied',
                      job_withdrawn: 'Withdrawn',
                      admin_assigned: 'Admin Assigned',
                      match_status_changed: 'Status Changed',
                      job_status_changed: 'Status Changed',
                    };
                    return (
                      <div key={item.id} className="px-5 py-4 hover:bg-slate-50/50 transition-all">
                        <div className="flex items-start space-x-3">
                          <div className={`h-8 w-8 rounded-full ${color} flex items-center justify-center text-xs font-bold shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-bold text-slate-800">{al.jobTitle || 'Job Activity'}</p>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${badgeColor[al.type] || 'bg-slate-100 text-slate-600'}`}>{badgeLabel[al.type] || al.type}</span>
                              <span className="text-caption text-slate-400">{new Date(al.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-caption text-slate-500 mt-0.5">{al.details || ''}</p>
                            {al.assignedByName && <p className="text-[11px] text-slate-500 mt-0.5">By: {al.assignedByName}</p>}
                            {al.withdrewByName && <p className="text-[11px] text-slate-500 mt-0.5">By: {al.withdrewByName}</p>}
                            {al.updatedByName && <p className="text-[11px] text-slate-500 mt-0.5">By: {al.updatedByName}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'employer') {
                    const el = item.data as any;
                    return (
                      <div key={item.id} className="px-5 py-4 hover:bg-slate-50/50 transition-all">
                        <div className="flex items-start space-x-3">
                          <div className={`h-8 w-8 rounded-full ${color} flex items-center justify-center text-xs font-bold shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-bold text-slate-800">{el.title || el.eventType || 'Employer Action'}</p>
                              <span className="text-caption text-slate-400">{new Date(el.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-caption text-slate-500 mt-0.5">{el.details || el.description || ''}</p>
                            {(el.performedByName || el.actorName) && (
                              <p className="text-[11px] text-slate-500 mt-0.5">By: {el.performedByName || el.actorName}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            ))}
          </div>
        )}

        {/* Sticky Comment Composer */}
        {canEditComments && <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/80">
          <div className="flex items-start space-x-2">
            <div className="flex-1">
              <textarea
                value={appCtx.newComment || ''}
                onChange={(e) => appCtx.setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (appCtx.newComment?.trim()) appCtx.handleAddComment(); } }}
                rows={2}
                placeholder="Add a comment... Press Enter to send, Shift+Enter for a new line"
                className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
              />
            </div>
            <button
              onClick={appCtx.handleAddComment}
              disabled={!appCtx.newComment?.trim()}
              className="px-5 py-3 btn-primary  disabled:bg-blue-300 text-white text-sm font-semibold rounded-xl transition-all flex items-center space-x-2 cursor-pointer disabled:cursor-not-allowed shrink-0"
            >
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Post</span>
            </button>
          </div>
        </div>}
      </div>

    </div>
  );
});

/* ── Application-Level Tickets Tab ── */
function TicketsTabField({
  app, applicationId, applicantName, applicantEmail, programName, adminEmail, allAdmins, isEnhancedReferrer, canCreate,
}: {
  app?: any;
  applicationId: string;
  applicantName: string;
  applicantEmail: string;
  programName: string;
  adminEmail: string;
  allAdmins?: { uid: string; name: string; email: string }[];
  isEnhancedReferrer?: boolean;
  canCreate: boolean;
}) {
  const [tickets, setTickets] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedTicket, setSelectedTicket] = React.useState<any>(null);
  const [selectedTicketComments, setSelectedTicketComments] = React.useState<any[]>([]);
  const [showCreate, setShowCreate] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/tickets', {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      const data = await res.json();
      const targetAppId = applicationId;
      const targetUserId = app?.userId || app?.id;
      setTickets((data.tickets || []).filter((t: any) =>
        t.applicationId === targetAppId || (targetUserId && t.applicantId === targetUserId)
      ));
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setLoading(false);
  }, [applicationId, app]);

  React.useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (t: any) => {
    setSelectedTicket(t);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/tickets/${t.ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSelectedTicketComments(data.comments || []);
    } catch {
      setSelectedTicketComments([]);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || !newDescription.trim()) return;
    setCreating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          applicationId,
          title: newTitle.trim(),
          description: newDescription.trim(),
        }),
      });
      const data = await res.json();
      if (data.ticket) {
        setTickets(prev => [data.ticket, ...prev]);
        setShowCreate(false);
        setNewTitle('');
        setNewDescription('');
      }
    } catch (err: any) {
      notifyToast('error', 'Failed to create ticket: ' + err.message);
    }
    setCreating(false);
  };

  const handleUpdate = async (ticketId: string, updates: any) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.ticket) {
        setTickets(prev => prev.map(t => t.ticketId === ticketId ? { ...t, ...data.ticket } : t));
        setSelectedTicket(prev => prev && prev.ticketId === ticketId ? { ...prev, ...data.ticket } : prev);
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const STATUS_COLORS_MAP: Record<string, string> = {
    open: 'bg-brand-active border-blue-200',
    in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
    pending: 'bg-purple-50 text-purple-700 border-purple-200',
    resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    closed: 'bg-slate-100 text-slate-500 border-slate-200',
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10 text-slate-400"><Loader className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TicketCheck className="h-4 w-4 text-brand" />
          <span className="text-xs font-extrabold text-slate-800">Support Tickets</span>
          <span className="text-caption text-slate-400">({tickets.length})</span>
        </div>
        {canCreate && <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 btn-primary  text-white text-caption font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1"
        >
          <Plus className="h-3 w-3" /> Report an Issue
        </button>}
      </div>

      {/* Create Ticket Form */}
      {showCreate && (
        <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3 shadow-sm">
          <p className="text-xs font-bold text-slate-800">Report an Issue</p>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Issue title..."
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Describe the issue..."
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400 resize-none min-h-[80px]"
            rows={3}
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => { setShowCreate(false); setNewTitle(''); setNewDescription(''); }} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || !newDescription.trim() || creating}
              className="px-4 py-1.5 btn-primary  disabled:bg-slate-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1"
            >
              {creating ? <Loader className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Submit
            </button>
          </div>
        </div>
      )}

      {/* Ticket List */}
      <div className="space-y-2">
        {tickets.length === 0 && !showCreate && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
            <TicketCheck className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-500">No tickets yet</p>
            <p className="text-caption text-slate-400 mt-1">Click "Report an Issue" to create a support ticket.</p>
          </div>
        )}
        {tickets.map(t => {
          const sColor = STATUS_COLORS_MAP[t.status] || 'bg-slate-100 text-slate-600';
          return (
            <div
              key={t.ticketId}
              onClick={() => openTicket(t)}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-200 hover:shadow-sm cursor-pointer transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-800 truncate">{t.title}</p>
                  <p className="text-caption text-slate-400 mt-0.5">
                    #{t.ticketId.slice(0, 8)} · Created {new Date(t.createdAt).toLocaleDateString()}
                    {t.assignedToName && ` · Assigned to ${t.assignedToName}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  <span className={`px-2 py-0.5 text-caption font-bold rounded-lg border ${sColor}`}>
                    {t.status === 'in_progress' ? 'In Progress' : t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                  </span>
                  {t.priority === 'urgent' && <span className="text-caption font-bold text-rose-600">Urgent</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {selectedTicket && (
        <React.Suspense fallback={null}>
          <TicketDetailModal
            ticket={selectedTicket}
            comments={selectedTicketComments}
            onClose={() => setSelectedTicket(null)}
            onUpdate={handleUpdate}
            allAdmins={allAdmins}
          />
        </React.Suspense>
      )}
    </div>
  );
}
